import { lazy, Suspense } from 'preact/compat';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  checkPortalExpertRequestAvailability,
  checkPortalProparcelAskAvailability,
  createPortalExpertRequest,
  createPortalProparcelAskRequest,
  createPortalMapOperationRequest,
  createPortalSpkValuationRequest,
  fetchPublicListingDetailData,
  getCreditCosts,
  getPortalDetailSection,
  getPortalDetailShell,
  getPortalDetailEnrichment,
  getProQueryHandoff,
  postPortalAgentRating,
  deletePortalAgentRating,
  getPortalInvestmentScore,
  getPortalPopulationChip,
  getPortalSolarEnergyScore,
  getPortalWindEnergyScore,
  getPortalDetailReport,
  rerunPortalDetailWithRoadOverride,
  resolvePortalApiErrorMessage,
  unlockListingProSorgu,
  uploadRequestAttachment,
  prefetchPortalDetailShell,
} from '../../lib/portal-detail-api.js';
import { buildListingOnlyPortalSummary } from '../../lib/listing-only-portal-summary.js';
import { openBinaCizFromPortal, canOpenBinaCizFromPortalSummary } from '../../lib/bina-ciz-portal-launch.js';
import { postPortalMahalleOrtSignal } from '../../lib/portal-api.js';
import CreditCelebrationModal from '../shared/CreditCelebrationModal.jsx';
import ProQueryCreditNoticeModal from '../shared/ProQueryCreditNoticeModal.jsx';
import { markDetailShellReady } from '../../lib/portal-detail-shell.js';
import { EXPERT_MODAL_BUNDLE_QUERY_KEY } from '../../lib/portal-expert-modal-api.js';
import { prefetchExpertModalBundle, useExpertModalBundle } from './useExpertModalBundle.js';
import {
  buildPortalCompareItemFromSummary,
  getPortalComparePageHref,
  PORTAL_COMPARE_MAX_ITEMS,
  togglePortalCompareItem,
  usePortalCompareSelection,
} from '../../lib/portal-compare-store.js';
import {
  emsalEntriesToFeatureCollection,
  mergeExpertResponseEmsalEntries,
  parcelCentroidFromFeatureCollection,
} from '../../lib/portal-geo.js';
import {
  buildQuarterCenterMapOverlays,
  fetchQuarterCenterLonLat,
} from '../../lib/quarter-center-map.js';
import {
  fetchTerrainBandGeoLayers,
  fetchQuarterSalesGrid,
  buildQuarterSalesChipOverlays,
  formatQuarterSalesChipValue,
} from '../../lib/portal-quarter-sales-grid.js';
import {
  buildPopulationGridOverlays,
  fetchQuarterPopulationGrid,
  formatPopulationChipLines,
  formatPopulationChipValue,
} from '../../lib/portal-population-grid.js';
import {
  fetchWetlandQuarterGeoLayers,
  fetchElectricQuarterGeoLayers,
  fetchValuableRoadQuarterGeoLayers,
  portalQuarterGeoQueryKey,
  scheduleIdle,
} from '../../lib/portal-quarter-geo-layers.js';
import {
  resolveQuarterCenterDistanceM,
  resolveImarBoundaryDistanceM,
  resolveRoadV2Frontage,
  isStructurePortalQueryType,
  buildInsightParcelRiskChips,
} from '../../lib/portal-insight-helpers.js';
import {
  buildParcelCardRows,
  isBuildingType,
  isLandOnlyStructurePortalSummary,
  shouldRenderStructureInfoCard,
  structureCostLabelTitleCase,
  LISTING_ATTR_KEYS_BOTTOM_COMFORT,
  LISTING_ATTR_KEYS_BOTTOM_COMFORT_ORDER,
  LISTING_ATTR_KEYS_SUPPRESS_DETAIL,
  LISTING_ATTR_KEY_CIFTLIK_LAND_TAPU,
} from '../../lib/portal-detail-card-contract.js';
import {
  composeHeroMapView,
  shouldForceHeroMapMedia,
} from '../../lib/portal-hero-map-orchestrator.js';
import { buildElectricMapOverlays } from '../../lib/portal-electric-line.js';
import { logElectricTabClick } from '../../lib/portal-electric-debug.js';
import {
  formatCurrency,
  formatDateTime,
  formatArea,
  formatRoadFrontageMeters,
  formatTitleCaseTr,
  queryTypeLabel,
  normalizeParcelShapeLabel,
  formatFxUsd,
  formatFxEur,
  formatFxGoldGrams,
} from '../../lib/formatters.js';
import { formatListingAttrDisplayValue, formatListingAttrLabel } from '../../lib/listing-attribute-display.js';
import { sanitizeListingHtml } from '../../lib/sanitizeListingHtml.js';
import { ListingFavoriteHeart } from '../ListingFavoriteHeart.jsx';
import { QueryFavoriteHeart } from '../QueryFavoriteHeart.jsx';
import { portalSummaryToListStarRow, resolveListRowMetaStarsPct } from '../../lib/portalListStarMeta.js';
import { PortalMetaStars } from '../PortalMetaStars.jsx';
import { SectionFrame } from './SectionFrame.jsx';
import { DetailPanelIcon } from './DetailTopPanelIcons.jsx';
import DetailInsightSlopeMetricCard from './DetailInsightSlopeMetricCard.jsx';
import DetailInsightMorphologyMetricCard from './DetailInsightMorphologyMetricCard.jsx';
import TerrainBandMeshExpandOverlay from './TerrainBandMeshExpandOverlay.jsx';
import TerrainBandRoadPanelOverlay from './TerrainBandRoadPanelOverlay.jsx';
import TerrainBandImagesOverlay from './TerrainBandImagesOverlay.jsx';
import { TerrainBandStreetViewSplit } from './TerrainBandStreetViewSplit.jsx';
import DetailInsightRiskPanels from './DetailInsightRiskPanels.jsx';
import DetailInsightRatingActionsCard from './DetailInsightRatingActionsCard.jsx';
import { createEdgeMeasurementFeatures } from '../../lib/edge-measurements.js';
import {
  ParcelSummaryCurrencyStrip,
  ParcelSummaryHeader,
  ParcelSummaryInfoRows,
  ParcelSummaryPriceHeader,
} from './ParcelInfoSummaryParts.jsx';
import { DetailStructureValuationStory } from './DetailStructureValuationStory.jsx';
import { StructureInfoSummaryCard } from './StructureInfoSummaryCard.jsx';
import {
  buildDfaRowsFromSteps,
  formatDfaPercent,
  mapMahalleOrtSimulationToFooter,
  parseMahalleOrtInput,
} from '../../lib/dfaPortalSteps.js';
import { DetailReportViewerModal } from './DetailReportTab.jsx';
import { capturePortalPdfMeshes } from '../../lib/portal-pdf-mesh-capture.js';
import { HeroCardMedia } from './HeroCardMedia.jsx';
import { MapboxParcelMapLazy as MapboxParcelMap } from './maps/MapboxParcelMapLazy.jsx';
import { DetailPageShell, DetailEnrichmentSkeleton } from './DetailPageShell.jsx';
import { PpLoaderAnalysisDots, PpLoaderCardSkeleton, PpLoaderParcelGrid } from './loaders/PpLoaders.jsx';
import { DetailTabPanelShell } from './DetailTabPanelShell.jsx';
import { DetailTabSkeleton } from './DetailTabSkeleton.jsx';
import { usePortalInsightSupplemental } from './usePortalInsightSupplemental.js';
import { LazyWhenVisible } from './LazyWhenVisible.jsx';
import DetailInsightPriceCard from './DetailInsightPriceCard.jsx';
import { resolvePortalTotalPrice, resolveProparcelAskPriceTierLabel } from '../../lib/portal-price-status.js';
import {
  buildPortalProQueryLauncherContext,
  launchPortalDetailProQueryFromSummary,
} from '../../lib/portal-detail-pro-query-launcher.js';
import { useDeferIdle } from './hooks/useDeferIdle.js';
import { useInViewport } from './hooks/useInViewport.js';
import { DetailPageStoreProvider, useDetailPageStore } from './state/useDetailPageStore.jsx';
import {
  PortalDetailMapLayerProvider,
  usePortalDetailMapLayers,
} from './state/PortalDetailMapLayerProvider.jsx';
const DetailExpertTab = lazy(() => import('./DetailExpertTab.jsx'));
const DetailKmTab = lazy(() => import('./DetailKmTab.jsx'));
const DetailElectricTab = lazy(() => import('./DetailElectricTab.jsx'));
const DetailSeaViewTab = lazy(() => import('./DetailSeaViewTab.jsx'));
const DetailSplitTab = lazy(() => import('./DetailSplitTab.jsx'));
const DetailFruitInvestmentTab = lazy(() => import('./DetailFruitInvestmentTab.jsx'));
const DetailInvestmentScoreTab = lazy(() => import('./DetailInvestmentScoreTab.jsx'));
const DetailSolarEnergyTab = lazy(() => import('./DetailSolarEnergyTab.jsx'));
const DetailWindEnergyTab = lazy(() => import('./DetailWindEnergyTab.jsx'));
const DetailSalesTab = lazy(() => import('./DetailSalesTab.jsx'));
const DetailCommissionTab = lazy(() => import('./DetailCommissionTab.jsx'));
const DetailMahalleBilgileriTab = lazy(() => import('./DetailMahalleBilgileriTab.jsx'));
const DetailSosyalAlanlarTab = lazy(() => import('./DetailSosyalAlanlarTab.jsx'));
const DetailImarBilgileriTab = lazy(() => import('./DetailImarBilgileriTab.jsx'));

const MAP_STYLE_STREETS = 'mapbox://styles/mapbox/streets-v12';
const MAP_STYLE_SATELLITE = 'mapbox://styles/mapbox/satellite-streets-v12';

/** İlan detayında Tepe Coin ile açılmadan pasif tutulan Pro Sorgu sekmeleri */
function buildRamsarHeroOverlay(ramsarSection) {
  const fc = ramsarSection?.ramsar_polygon;
  const features = Array.isArray(fc?.features) ? fc.features : [];
  if (!features.length) return null;
  return {
    id: 'portal-ramsar-wetland',
    data: { type: 'FeatureCollection', features },
    fillColor: '#67e8f9',
    fillOpacity: 0.26,
    lineColor: '#06b6d4',
    lineWidth: 2,
    showLabels: true,
    labelField: 'label_text',
    labelTextSize: 12,
    labelColor: '#083344',
    labelHaloColor: 'rgba(240,253,250,0.96)',
    labelHaloWidth: 2,
    labelAllowOverlap: true,
    labelIgnorePlacement: true,
    excludeFromFit: true,
  };
}

/** Poligon köşe/halka noktalarından en yakın lon/lat (turf yok). */
function nearestLonLatOnFeatureCollection(featureCollection, fromLonLat) {
  if (!featureCollection?.features?.length || !Array.isArray(fromLonLat) || fromLonLat.length < 2) {
    return null;
  }
  const fromLon = Number(fromLonLat[0]);
  const fromLat = Number(fromLonLat[1]);
  if (!Number.isFinite(fromLon) || !Number.isFinite(fromLat)) return null;

  let best = null;
  let bestDist = Infinity;

  const visit = (pt) => {
    if (!Array.isArray(pt) || pt.length < 2) return;
    const lon = Number(pt[0]);
    const lat = Number(pt[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    const dLon = lon - fromLon;
    const dLat = lat - fromLat;
    const dist = (dLon * dLon) + (dLat * dLat);
    if (dist < bestDist) {
      bestDist = dist;
      best = [lon, lat];
    }
  };

  const walkCoords = (coords) => {
    if (!Array.isArray(coords) || !coords.length) return;
    if (typeof coords[0] === 'number') {
      visit(coords);
      return;
    }
    coords.forEach(walkCoords);
  };

  featureCollection.features.forEach((feature) => {
    walkCoords(feature?.geometry?.coordinates);
  });

  return best;
}

function formatTerrainDistanceLabel(meters) {
  if (meters == null || Number.isNaN(Number(meters))) return '';
  return `${Math.round(Number(meters)).toLocaleString('tr-TR')} m`;
}

function escapeTerrainMarkerHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function lonLatAlongSegment(from, to, t) {
  const a0 = Number(from[0]);
  const a1 = Number(from[1]);
  const b0 = Number(to[0]);
  const b1 = Number(to[1]);
  return [a0 + ((b0 - a0) * t), a1 + ((b1 - a1) * t)];
}

/** Basit ray-casting — dış halka / delikler için yeterli. */
function pointInLonLatRing(lon, lat, ring) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i]?.[0]);
    const yi = Number(ring[i]?.[1]);
    const xj = Number(ring[j]?.[0]);
    const yj = Number(ring[j]?.[1]);
    if (![xi, yi, xj, yj].every(Number.isFinite)) continue;
    const intersect = ((yi > lat) !== (yj > lat))
      && (lon < ((((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-15)) + xi));
    if (intersect) inside = !inside;
  }
  return inside;
}

function pointInParcelFeatureCollection(lonLat, featureCollection) {
  if (!Array.isArray(lonLat) || lonLat.length < 2 || !featureCollection?.features?.length) {
    return false;
  }
  const lon = Number(lonLat[0]);
  const lat = Number(lonLat[1]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return false;

  for (const feature of featureCollection.features) {
    const geom = feature?.geometry;
    if (!geom) continue;
    const type = geom.type;
    const coords = geom.coordinates;
    if (type === 'Polygon' && Array.isArray(coords?.[0])) {
      if (pointInLonLatRing(lon, lat, coords[0])) return true;
    } else if (type === 'MultiPolygon' && Array.isArray(coords)) {
      for (const poly of coords) {
        if (Array.isArray(poly?.[0]) && pointInLonLatRing(lon, lat, poly[0])) return true;
      }
    }
  }
  return false;
}

/**
 * Etiketi çizgi üzerinde parsele denk gelmeyecek noktaya koy.
 * Parsel merkezinden hedefe doğru ilerler; ilk “dışarıda” noktayı alır.
 */
function labelLonLatAwayFromParcel(from, to, parcelFeatureCollection) {
  if (!Array.isArray(from) || !Array.isArray(to)) return null;
  const steps = [0.58, 0.64, 0.7, 0.76, 0.82, 0.88, 0.93];
  for (const t of steps) {
    const pt = lonLatAlongSegment(from, to, t);
    if (!pointInParcelFeatureCollection(pt, parcelFeatureCollection)) {
      return pt;
    }
  }
  // Çizgi kısa / hedef parsele çok yakınsa hedefe daha yakın bırak
  return lonLatAlongSegment(from, to, 0.9);
}

const LISTING_PRO_SORGU_TAB_IDS = new Set([
  'overview',
  'expert',
  'km',
  'split',
  'electric',
  'sea_view',
  'fruit_investment',
  'investment_score',
  'solar_energy',
  'wind_energy',
  'quarter_sales',
  'mahalle_bilgileri',
  'sosyal_alanlar',
  'imar_bilgileri',
]);

/** Orta blok (Mahalle Bilgileri + Uzman + Puan + Rapor öncesi) */
const BASE_TAB_DEFS = [
  { id: 'overview', label: 'DFA' },
  { id: 'imar_bilgileri', label: 'İmar Bilgileri' },
  { id: 'km', label: 'KM Analizi' },
  { id: 'sea_view', label: 'Manzara' },
  { id: 'electric', label: 'Yüksek Gerilim' },
  { id: 'fruit_investment', label: 'Meyve Bahçesi Skoru' },
  { id: 'investment_score', label: 'Mülk Skoru' },
  { id: 'solar_energy', label: 'Güneş Enerjisi' },
  { id: 'wind_energy', label: 'Rüzgar Enerjisi' },
  { id: 'quarter_sales', label: 'Satışlar' },
  { id: 'commission', label: 'Komisyon & Harçlar' },
];

/** Şerit: DFA’dan hemen sonra İmar; sonda Mahalle / Sosyal / Uzman */
const IMAR_TAB_DEF = { id: 'imar_bilgileri', label: 'İmar Bilgileri' };
const MAHALLE_TAB_DEF = { id: 'mahalle_bilgileri', label: 'Mahalle Bilgileri' };
const SOSYAL_ALANLAR_TAB_DEF = { id: 'sosyal_alanlar', label: 'Sosyal Alanlar' };
const TAIL_TAB_DEFS = [
  { id: 'expert', label: 'Uzman Görüşleri' },
];

/** Sekme etiketi — tek stil, tek satır (veya eşit kutuda sarmalı). */
function formatMainTabLabel(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  return <span className="pp-detail-main-tab-line">{t}</span>;
}

/** İlan taxonomy: imarlı arsa `imarli_arsa_*`, imarsız `arazi_*` (category_taxonomy_seed). */
function isImarliArsaLandCategory(summary) {
  const leaf = String(summary?.category_leaf_id || summary?.listing_category_leaf_id || '').trim();
  if (leaf) {
    if (leaf.startsWith('arazi_')) return false;
    if (leaf.startsWith('imarli_arsa_')) return true;
    return false;
  }
  const main = String(summary?.listing_category_main || '').trim().toLowerCase();
  if (main === 'arazi') return false;
  if (main === 'imarli_arsa') return true;
  const qt = String(summary?.query_type || '').toLowerCase();
  return qt === 'arsa';
}

/** İmarsız tarım / tarla: bölünebilirlik sekmesi (çoklu kriter). */
function isImarsizTarimLandCategory(summary) {
  const leaf = String(summary?.category_leaf_id || summary?.listing_category_leaf_id || '').trim();
  if (leaf.startsWith('imarli_arsa_')) return false;
  if (leaf.startsWith('arazi_')) return true;
  const qt = String(summary?.query_type || '').toLowerCase();
  if (qt === 'tarla') return true;
  const main = String(summary?.listing_category_main || '').trim().toLowerCase();
  if (main === 'arazi') return true;
  return false;
}

function getTabDefs(summary) {
  const isStructure = isStructurePortalQueryType(summary?.query_type);
  const tabs = isStructure
    ? BASE_TAB_DEFS.filter((t) => t.id !== 'fruit_investment' && t.id !== 'wind_energy')
    : [...BASE_TAB_DEFS];
  const hasListingTab =
    summary?.listing_id &&
    (summary?.listing_description ||
      summary?.listing_title ||
      (Array.isArray(summary?.listing_media) && summary.listing_media.length > 0));
  if (hasListingTab) {
    tabs.unshift({ id: 'listing_description', label: 'Açıklama' });
  }
  if (isImarliArsaLandCategory(summary) || isImarsizTarimLandCategory(summary)) {
    const electricIdx = tabs.findIndex((t) => t.id === 'electric');
    if (electricIdx >= 0) {
      tabs.splice(electricIdx, 0, { id: 'split', label: 'Bölünebilirlik' });
    }
  }
  return tabs;
}

function buildDfaRows(summary) {
  return buildDfaRowsFromSteps(Array.isArray(summary?.dfa_json) ? summary.dfa_json : []);
}

function extractParcelRingFromSummary(summary) {
  const coords = summary?.parcel_coords_lonlat;
  if (!Array.isArray(coords) || coords.length < 3) return [];
  let ring = coords
    .map((p) => (Array.isArray(p) && p.length >= 2 ? [Number(p[0]), Number(p[1])] : null))
    .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (ring.length >= 2) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      ring = ring.slice(0, -1);
    }
  }
  return ring.length >= 3 ? ring : [];
}

function haversineMeters(lon1, lat1, lon2, lat2) {
  const earthRadiusM = 6371000;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const dPhi = ((lat2 - lat1) * Math.PI) / 180;
  const dLambda = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dPhi / 2) ** 2
    + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) ** 2;
  return earthRadiusM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function buildSelectableParcelEdgeFeatures(ring, selectionMap, focusedEdgeId = null) {
  if (!Array.isArray(ring) || ring.length < 3) {
    return { type: 'FeatureCollection', features: [] };
  }
  const features = [];
  for (let i = 0; i < ring.length; i += 1) {
    const p1 = ring[i];
    const p2 = ring[(i + 1) % ring.length];
    const inSelection = selectionMap?.[i] != null;
    const selectedType = selectionMap?.[i]?.road_type_id ?? null;
    const isCompleted = selectedType != null;
    const isFocused = Number(focusedEdgeId) === i;
    const color = isCompleted
      ? '#22c55e'
      : (inSelection || isFocused ? '#f59e0b' : '#60a5fa');
    const width = (isCompleted || inSelection || isFocused) ? 6 : 4;
    const distance = haversineMeters(p1[0], p1[1], p2[0], p2[1]);
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [p1, p2],
      },
      properties: {
        edgeId: i,
        color,
        width,
        length_m: Math.round(distance),
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

function _normalizeRoadOverrideSelections(input) {
  const rows = Array.isArray(input) ? input : [];
  const used = new Set();
  const out = [];
  rows.forEach((row) => {
    const edgeId = Number(row?.edge_id);
    const roadTypeId = row?.road_type_id == null ? null : Number(row.road_type_id);
    if (!Number.isInteger(edgeId) || edgeId < 0 || used.has(edgeId)) return;
    if (roadTypeId != null && roadTypeId !== 9 && roadTypeId !== 13) return;
    used.add(edgeId);
    out.push({ edge_id: edgeId, road_type_id: roadTypeId });
  });
  return out.sort((a, b) => a.edge_id - b.edge_id);
}

function _normalizeElectricOverrideLines(input) {
  const rows = Array.isArray(input) ? input : [];
  const out = [];
  rows.forEach((row, idx) => {
    const s = row?.start;
    const e = row?.end;
    const voltageKv = Number(row?.voltage_kv);
    if (!Array.isArray(s) || s.length < 2 || !Array.isArray(e) || e.length < 2) return;
    const sLon = Number(s[0]); const sLat = Number(s[1]);
    const eLon = Number(e[0]); const eLat = Number(e[1]);
    if (![sLon, sLat, eLon, eLat].every(Number.isFinite)) return;
    if (![64, 154, 380].includes(voltageKv)) return;
    out.push({
      id: `line-${idx}-${sLon}-${sLat}-${eLon}-${eLat}-${voltageKv}`,
      start: [sLon, sLat],
      end: [eLon, eLat],
      voltage_kv: voltageKv,
    });
  });
  return out;
}

function getDfaEndUnitFromSteps(steps) {
  if (!Array.isArray(steps) || !steps.length) return null;
  const last = steps[steps.length - 1] || {};
  const candidates = [last.new_avg, last.prev_avg, last.final_avg];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (c != null && c !== '' && !Number.isNaN(Number(c))) {
      return Number(c);
    }
  }
  return null;
}

function finiteNumberOrNull(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function applyMahalleOrtFooterToSummary(summary, footer) {
  if (!summary || !footer) return summary;

  const endUnit = finiteNumberOrNull(footer.endUnit);
  const landTotal = finiteNumberOrNull(footer.landTotal);
  const deliveryTotal = finiteNumberOrNull(footer.deliveryTotal);
  const displayTotal = finiteNumberOrNull(footer.total) ?? deliveryTotal ?? landTotal;
  if (endUnit == null && displayTotal == null && landTotal == null && deliveryTotal == null) {
    return summary;
  }

  const next = { ...summary };
  const vc = { ...(summary.valuation_canonical || {}) };
  if (endUnit != null) {
    vc.final_unit_price_m2 = endUnit;
    next.unit_price = endUnit;
    next.arazi_birim_fiyati = endUnit;
  }
  if (landTotal != null) {
    vc.final_total_tl = landTotal;
    next.arsa_fiyati = landTotal;
  }
  next.valuation_canonical = vc;

  if (summary.portal_summary_prices || endUnit != null || displayTotal != null) {
    next.portal_summary_prices = {
      ...(summary.portal_summary_prices || {}),
      ...(endUnit != null ? { unit_price: endUnit } : {}),
      ...(displayTotal != null ? { total_price: displayTotal } : {}),
    };
  }
  if (summary.land_price_summary || endUnit != null || landTotal != null) {
    next.land_price_summary = {
      ...(summary.land_price_summary || {}),
      ...(endUnit != null ? { unit_price_m2: endUnit } : {}),
      ...(landTotal != null ? { total_tl: landTotal } : {}),
    };
  }
  if (summary.structure_price_summary || deliveryTotal != null) {
    next.structure_price_summary = {
      ...(summary.structure_price_summary || {}),
      ...(deliveryTotal != null ? { delivery_total_tl: deliveryTotal } : {}),
    };
  }
  if (displayTotal != null) {
    next.total_price = displayTotal;
  }

  return next;
}

/** DFA özet: arazi katmanı. Yapı sorgularında toplam yalnızca arazi DFA; nihai toplam summary.total_price. */
function getPortalDfaPriceFooter(summary) {
  const structureQuery = isStructurePortalQueryType(summary?.query_type);
  const landOnlyStructure = isLandOnlyStructurePortalSummary(summary);
  const useStructureLandSplit = structureQuery && !landOnlyStructure;
  const vc = summary?.valuation_canonical;
  const steps = Array.isArray(summary?.dfa_json) ? summary.dfa_json : [];
  const first = steps[0] || {};
  const rawStart =
    vc?.initial_unit_price_m2 ?? first.prev_avg ?? first.new_avg ?? null;
  const startUnit =
    rawStart != null && rawStart !== '' && !Number.isNaN(Number(rawStart)) ? Number(rawStart) : null;
  const sp = summary?.portal_summary_prices;
  const lps = summary?.land_price_summary;
  let up = null;
  if (useStructureLandSplit && lps?.unit_price_m2 != null && !Number.isNaN(Number(lps.unit_price_m2))) {
    up = Number(lps.unit_price_m2);
  } else if (vc && vc.final_unit_price_m2 != null && !Number.isNaN(Number(vc.final_unit_price_m2))) {
    up = Number(vc.final_unit_price_m2);
  }
  if (up == null && !landOnlyStructure) {
    const fromSteps = getDfaEndUnitFromSteps(steps);
    if (fromSteps != null) up = fromSteps;
  }
  if (
    up == null &&
    summary?.unit_price != null &&
    summary.unit_price !== '' &&
    !Number.isNaN(Number(summary.unit_price))
  ) {
    up = Number(summary.unit_price);
  }
  let tp = null;
  if (useStructureLandSplit) {
    if (lps?.total_tl != null && !Number.isNaN(Number(lps.total_tl))) {
      tp = Number(lps.total_tl);
    } else if (summary?.arsa_fiyati != null && !Number.isNaN(Number(summary.arsa_fiyati))) {
      tp = Number(summary.arsa_fiyati);
    } else if (vc && vc.final_total_tl != null && !Number.isNaN(Number(vc.final_total_tl))) {
      tp = Number(vc.final_total_tl);
    }
    if (up == null && summary?.arazi_birim_fiyati != null && !Number.isNaN(Number(summary.arazi_birim_fiyati))) {
      up = Number(summary.arazi_birim_fiyati);
    }
  } else {
    tp = summary?.total_price;
    if (vc && vc.final_total_tl != null && !Number.isNaN(Number(vc.final_total_tl))) {
      tp = Number(vc.final_total_tl);
    }
    if (sp && typeof sp === 'object') {
      if (up == null && sp.unit_price != null && sp.unit_price !== '' && !Number.isNaN(Number(sp.unit_price))) {
        up = Number(sp.unit_price);
      }
      if (tp == null && sp.total_price != null && sp.total_price !== '' && !Number.isNaN(Number(sp.total_price))) {
        tp = Number(sp.total_price);
      }
    }
  }
  const endUnit = up != null && up !== '' && !Number.isNaN(Number(up)) ? Number(up) : null;
  const total = tp != null && tp !== '' && !Number.isNaN(Number(tp)) ? Number(tp) : null;
  return { startUnit, endUnit, total, isLandOnly: structureQuery };
}

/** Özet API fx_portal — yalnız toplam karşılıkları (USD / EUR / Altın) */
function buildFxPortalTotalsLine(fx) {
  if (!fx || typeof fx !== 'object') return null;
  const totalParts = [];
  if (fx.price_usd != null && fx.price_usd !== '' && !Number.isNaN(Number(fx.price_usd))) {
    totalParts.push(`USD ${formatFxUsd(fx.price_usd)}`);
  }
  if (fx.price_eur != null && fx.price_eur !== '' && !Number.isNaN(Number(fx.price_eur))) {
    totalParts.push(`EUR ${formatFxEur(fx.price_eur)}`);
  }
  if (fx.price_gold_grams != null && fx.price_gold_grams !== '' && !Number.isNaN(Number(fx.price_gold_grams))) {
    totalParts.push(`Altın ${formatFxGoldGrams(fx.price_gold_grams)}`);
  }
  if (!totalParts.length) return null;
  return totalParts.join(' · ');
}

function listingTypeLabelTr(raw) {
  const k = String(raw || '').toLowerCase();
  if (k === 'sale') return 'Satılık';
  if (k === 'rent') return 'Kiralık';
  if (k === 'daily') return 'Günlük';
  return raw != null && String(raw).trim() !== '' ? String(raw) : '—';
}

/** Ada/parsel satırı ve üst kolon: ilanda yaprak (alt) kategori; değilse sorgu tipi (Tarla, Fabrika…). */
function adaParselKindLabel(summary) {
  if (!summary) return queryTypeLabel(null);
  if (summary.listing_id) {
    const leaf = String(summary.listing_category_leaf_label || '').trim();
    if (leaf) return formatTitleCaseTr(leaf);
  }
  return queryTypeLabel(summary.query_type);
}

/** Üst kart sağ kolon — ada/parsel ile aynı metin (ana kategori değil, alt kategori). */
function detailKindHeadline(summary) {
  if (!summary) return null;
  return adaParselKindLabel(summary);
}

function formatTotalAppliedPercent(summary) {
  const vc = summary?.valuation_canonical;
  if (vc && vc.net_multiplier_vs_initial != null) {
    const n = Number(vc.net_multiplier_vs_initial);
    if (Number.isFinite(n) && n > 0) {
      return formatDfaPercent(n, false);
    }
  }
  const rows = buildDfaRows(summary);
  const totalFactor = rows.reduce((acc, row, index) => {
    if (index === 0 || row.factor === null) {
      return acc;
    }
    return acc * row.factor;
  }, 1);

  if (!rows.length || !Number.isFinite(totalFactor)) {
    return '—';
  }

  return formatDfaPercent(totalFactor, false);
}

/** Kazanılmış: aktif SVG; değilse kilitli SVG. */
function pickBadgeUrlByEarned(item) {
  if (!item) return null;
  if (item.is_earned) {
    return item.svg_active_url || item.svg_locked_url || null;
  }
  return item.svg_locked_url || item.svg_active_url || null;
}

/** API’deki profil adresi; yoksa il/ilçe (sorgu konumu kullanılmaz). */
function resolvePortalOwnerAddressText(data) {
  const api = (data?.address_display && String(data.address_display).trim()) || '';
  if (api) return api;
  const prof = [data?.city_name, data?.district_name].filter(Boolean).join(' / ');
  if (prof) return prof;
  return '—';
}

/** TR GSM: DB’de 536… gibi saklanan numaraya başta 0; +90 / 90 öneklerini 0’lı ulusal forma çevir. */
function formatTurkeyPhoneDisplayTel(raw) {
  if (raw == null || raw === '') return null;
  const d = String(raw).replace(/\D/g, '');
  if (!d) return null;
  let national;
  if (d.length === 12 && d.startsWith('90')) {
    national = `0${d.slice(2)}`;
  } else if (d.length === 11 && d.startsWith('0')) {
    national = d;
  } else if (d.length === 10 && d.startsWith('5')) {
    national = `0${d}`;
  } else {
    national = d;
  }
  return { display: national, tel: national };
}

/** Mobil arama için tel: href (+90…) */
function phoneToTelHref(raw) {
  if (raw == null || raw === '') return '#';
  const d = String(raw).replace(/\D/g, '');
  if (!d) return '#';
  let intl;
  if (d.length === 12 && d.startsWith('90')) {
    intl = `+${d}`;
  } else if (d.length === 11 && d.startsWith('0')) {
    intl = `+90${d.slice(1)}`;
  } else if (d.length === 10 && d.startsWith('5')) {
    intl = `+90${d}`;
  } else {
    intl = `+${d}`;
  }
  return `tel:${intl}`;
}

function PortalOwnerBadgeTile({
  label,
  block,
  size = 'md',
  candidateOverlay = null,
  expertMain = false,
}) {
  const active = block?.active_badge;
  const earned = Boolean(active?.is_earned);
  const url = pickBadgeUrlByEarned(active);
  const showAday = Boolean(expertMain && candidateOverlay && active && !earned);

  const iconClass = [
    'pp-owner-badge-tile-icon',
    earned ? 'pp-owner-badge-tile-icon--vivid' : 'pp-owner-badge-tile-icon--locked',
  ].join(' ');

  return (
    <div className={`pp-owner-badge-tile pp-owner-badge-tile--${size}${earned ? '' : ' pp-owner-badge-tile--locked'}`}>
      <div className="pp-owner-badge-tile-label">{label}</div>
      <div className={iconClass}>
        {url ? <img src={url} alt="" /> : <div className="pp-owner-badge-tile-placeholder" aria-hidden />}
        {showAday ? <span className="pp-owner-badge-aday">{candidateOverlay}</span> : null}
      </div>
      <div
        className={`pp-owner-badge-tile-title${!active?.title || !earned ? ' pp-owner-badge-tile-title--muted' : ''}`}
        title={active?.unlock_hint || ''}
      >
        {active?.title || '—'}
      </div>
      {block?.summary && block.summary.sales_score != null && size !== 'hero' ? (
        <div className="pp-owner-badge-tile-meta">{Number(block.summary.sales_score)} onaylı satış</div>
      ) : null}
    </div>
  );
}

function splitDisplayNameForForm(fullName) {
  const s = String(fullName || '').trim();
  if (!s) return { first: '', last: '' };
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts.slice(1).join(' ') };
}

function ExpertOwnerStarRow({ label, value, onChange, disabled }) {
  return (
    <div className="pp-agent-rating-criterion">
      <span className="pp-agent-rating-criterion-label">{label}</span>
      <div className="pp-agent-rating-stars" role="group" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={`pp-agent-rating-star-btn${value >= n ? ' is-active' : ''}`}
            onClick={() => onChange(n)}
            disabled={disabled}
            aria-pressed={value >= n}
            aria-label={`${n} yıldız`}
          >
            <i className="fas fa-star" aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
}

function ExpertOwnerStarsReadOnly({ value }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className="pp-agent-rating-stars-readonly" aria-hidden>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className="pp-agent-rating-star-shell">
          <i className="fas fa-star" />
          <i
            className="fas fa-star is-on"
            style={{ width: `${Math.max(0, Math.min(100, (v - (n - 1)) * 100))}%` }}
          />
        </span>
      ))}
    </span>
  );
}

function ExpertOwnerProfileModal({ userId, onClose, initialOwner = null }) {
  const queryClient = useQueryClient();
  const {
    profile: data,
    ratings: rates,
    isPending: bundlePending,
    isError: bundleError,
    error: bundleErr,
  } = useExpertModalBundle(userId);

  const [formFirst, setFormFirst] = useState('');
  const [formLast, setFormLast] = useState('');
  const [starSubject, setStarSubject] = useState(0);
  const [starCourtesy, setStarCourtesy] = useState(0);
  const [starComm, setStarComm] = useState(0);
  const [starTrust, setStarTrust] = useState(0);
  const [comment, setComment] = useState('');
  const lastSeededMyRatingKeyRef = useRef('');

  useEffect(() => {
    setFormFirst('');
    setFormLast('');
    setStarSubject(0);
    setStarCourtesy(0);
    setStarComm(0);
    setStarTrust(0);
    setComment('');
    lastSeededMyRatingKeyRef.current = '';
  }, [userId]);

  useEffect(() => {
    if (!rates) return;
    const mr = rates.my_rating;
    const key = mr ? `id:${mr.id}` : 'none';
    if (lastSeededMyRatingKeyRef.current === key) return;
    lastSeededMyRatingKeyRef.current = key;

    if (mr) {
      setFormFirst(mr.reviewer_first_name || '');
      setFormLast(mr.reviewer_last_name || '');
      setStarSubject(mr.star_subject_mastery || 0);
      setStarCourtesy(mr.star_courtesy || 0);
      setStarComm(mr.star_communication || 0);
      setStarTrust(mr.star_trust || 0);
      setComment(mr.comment || '');
    } else {
      setStarSubject(0);
      setStarCourtesy(0);
      setStarComm(0);
      setStarTrust(0);
      setComment('');
      if (data?.full_name) {
        const sp = splitDisplayNameForForm(data.full_name);
        setFormFirst(sp.first);
        setFormLast(sp.last);
      } else {
        setFormFirst('');
        setFormLast('');
      }
    }
  }, [rates, data?.full_name]);

  const ratingMutation = useMutation({
    mutationFn: (payload) => postPortalAgentRating(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EXPERT_MODAL_BUNDLE_QUERY_KEY, userId] });
    },
  });

  const deleteRatingMutation = useMutation({
    mutationFn: () => deletePortalAgentRating(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EXPERT_MODAL_BUNDLE_QUERY_KEY, userId] });
    },
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!userId) {
    return null;
  }

  const headerFromOwner = initialOwner && String(initialOwner.user_id) === String(userId) ? initialOwner : null;
  const headerCompanyLogo = data?.company_logo_url || headerFromOwner?.company_logo_url;
  const headerCompanyName = (data?.company_name && String(data.company_name).trim())
    || (headerFromOwner?.company_name && String(headerFromOwner.company_name).trim())
    || '';
  const profileReady = Boolean(data);
  const profilePending = bundlePending;
  const badges = data?.badges;
  const errMsg = bundleErr?.message;
  const badgesVisitUrl = `/accounts/profile/visit/${encodeURIComponent(String(userId))}/`;
  const agg = rates?.aggregate;
  const canRate = Boolean(rates?.can_rate);
  const agentRatingsBusy = ratingMutation.isPending || deleteRatingMutation.isPending;
  const persistedCommentTrimmed = String(rates?.my_rating?.comment || '').trim();

  const onSubmitRating = (e) => {
    e.preventDefault();
    if (!canRate) return;
    if (!(starSubject && starCourtesy && starComm && starTrust)) {
      return;
    }
    ratingMutation.mutate({
      reviewer_first_name: formFirst.trim(),
      reviewer_last_name: formLast.trim(),
      star_subject_mastery: starSubject,
      star_courtesy: starCourtesy,
      star_communication: starComm,
      star_trust: starTrust,
      comment: comment.trim(),
    });
  };

  return (
    <div
      className="pp-owner-profile-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="pp-owner-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pp-owner-profile-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pp-owner-profile-modal-head">
          {(headerCompanyLogo || headerCompanyName) ? (
            <div className="pp-owner-profile-modal-head-brand">
              {headerCompanyLogo ? (
                <img
                  className="pp-owner-profile-modal-head-logo"
                  src={headerCompanyLogo}
                  alt=""
                />
              ) : null}
              {headerCompanyName ? (
                <span id="pp-owner-profile-title" className="pp-owner-profile-modal-head-company">
                  {headerCompanyName}
                </span>
              ) : (
                <span id="pp-owner-profile-title" className="pp-owner-profile-modal-head-company pp-sr-only">
                  Profil
                </span>
              )}
            </div>
          ) : (
            <span id="pp-owner-profile-title" className="pp-owner-profile-modal-head-company pp-sr-only">
              Profil
            </span>
          )}
          <button type="button" className="pp-request-modal-close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>
        <div className="pp-owner-profile-modal-body">
          {profilePending ? (
            <div className="pp-owner-profile-loading" role="status">
              <div className="pp-owner-profile-skeleton" />
              <div className="pp-owner-profile-skeleton pp-owner-profile-skeleton--short" />
            </div>
          ) : null}
          {bundleError && !data ? (
            <div className="pp-detail-empty">{errMsg || 'Profil yüklenemedi.'}</div>
          ) : null}
          {profileReady ? (
            <>
            <div className="pp-owner-profile-layout">
              <aside className="pp-owner-profile-aside">
                <div className="pp-owner-profile-aside-avatar-wrap">
                  {data.avatar_url ? (
                    <img
                      className="pp-owner-profile-aside-avatar"
                      src={data.avatar_url}
                      alt={data.full_name || data.company_name || ''}
                    />
                  ) : (
                    <div className="pp-owner-profile-aside-avatar pp-owner-profile-aside-avatar-placeholder" aria-hidden>
                      {(data.full_name || data.email || '?').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="pp-owner-profile-aside-id">
                  <div className="pp-owner-profile-aside-names">
                    <div className="pp-owner-profile-display-name">
                      {(data.full_name && String(data.full_name).trim())
                        || data.email
                        || 'Kullanıcı'}
                    </div>
                  </div>
                </div>
                {data.membership_display ? (
                  <div className="pp-owner-profile-aside-chips">
                    <span className="pp-owner-profile-chip">{data.membership_display}</span>
                  </div>
                ) : null}
                {(data.graduation?.university || data.graduation?.department || data.graduation?.education_level_label) ? (
                  <div className="pp-owner-profile-graduation pp-owner-profile-graduation--aside">
                    <span className="pp-owner-profile-graduation-label">Mezuniyet / eğitim</span>
                    <div>
                      {data.graduation.education_level_label ? (
                        <div className="pp-owner-profile-grad-line">{data.graduation.education_level_label}</div>
                      ) : null}
                      <div>
                        {[data.graduation.university, data.graduation.department].filter(Boolean).join(' · ')}
                        {data.graduation.is_verified ? (
                          <span className="pp-owner-profile-verified"> · Doğrulanmış</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
                {(data.city_name || data.district_name)
                && resolvePortalOwnerAddressText(data) === '—' ? (
                  <div className="pp-owner-profile-aside-loc">
                    <span className="pp-owner-profile-graduation-label">Konum</span>
                    <div>{[data.city_name, data.district_name].filter(Boolean).join(' / ') || '—'}</div>
                  </div>
                ) : null}
                {data.date_joined ? (
                  <div className="pp-owner-profile-aside-meta-line">
                    <span className="pp-owner-profile-graduation-label">Üyelik</span>
                    <div>{data.date_joined}</div>
                  </div>
                ) : null}
                <div className="pp-owner-profile-contact pp-owner-profile-contact--aside">
                  {data.email ? (
                    <div className="pp-owner-profile-contact-row">
                      <span>E-posta</span>
                      <a href={`mailto:${data.email}`}>{data.email}</a>
                    </div>
                  ) : null}
                  {data.phone ? (() => {
                    const ph = formatTurkeyPhoneDisplayTel(data.phone);
                    return ph ? (
                      <div className="pp-owner-profile-contact-row">
                        <span>Telefon</span>
                        <a href={`tel:${ph.tel}`}>{ph.display}</a>
                      </div>
                    ) : null;
                  })() : null}
                  <div className="pp-owner-profile-contact-row pp-owner-profile-contact-row--block">
                    <span>Adres</span>
                    <p>{resolvePortalOwnerAddressText(data)}</p>
                  </div>
                </div>
              </aside>
              <div className="pp-owner-profile-main">
                <div className="pp-owner-profile-score-strip pp-owner-profile-score-strip--two-only">
                  <div>
                    <span className="pp-owner-profile-score-label">Güncel puan</span>
                    <strong>{data.expert_score_current != null ? data.expert_score_current : '—'}</strong>
                  </div>
                  <div>
                    <span className="pp-owner-profile-score-label">Zirve</span>
                    <strong>{data.expert_score_peak != null ? data.expert_score_peak : '—'}</strong>
                  </div>
                </div>
                <a
                  href={badgesVisitUrl}
                  className="pp-owner-profile-badge-panel-link"
                  aria-label="Rozet sayfasını aç"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="pp-owner-profile-badge-panel">
                    <PortalOwnerBadgeTile
                      label="Ana uzmanlık rozeti"
                      block={badges?.expert_level}
                      size="hero"
                      expertMain
                      candidateOverlay="ADAY"
                    />
                    <div className="pp-owner-profile-badge-grid">
                      <PortalOwnerBadgeTile label="Meslek yılı" block={badges?.tenure} />
                      <PortalOwnerBadgeTile label="Satış rozeti" block={badges?.sales} />
                      <PortalOwnerBadgeTile label="Kilometre taşı" block={badges?.milestone} />
                      <PortalOwnerBadgeTile label="Mezuniyet rozeti" block={badges?.education} />
                    </div>
                  </div>
                </a>
              </div>
            </div>
            <div className="pp-owner-agent-ratings">
              <h4 className="pp-owner-agent-ratings-title">Emlakçı değerlendirmesi</h4>
              {rates ? (
                <>
                  <div className="pp-owner-agent-ratings-summary">
                    <div className="pp-owner-agent-ratings-summary-main">
                      <span className="pp-owner-agent-ratings-summary-label">Genel ortalama</span>
                      <div className="pp-owner-agent-ratings-summary-row">
                        <ExpertOwnerStarsReadOnly value={agg?.avg_overall} />
                        <strong className="pp-owner-agent-ratings-score">
                          {agg?.avg_overall != null ? agg.avg_overall.toFixed(1) : '—'}
                          <span className="pp-owner-agent-ratings-max">/ 5</span>
                        </strong>
                        <span className="pp-owner-agent-ratings-count">
                          ({agg?.count ?? 0} değerlendirme)
                        </span>
                      </div>
                    </div>
                    {(agg?.count ?? 0) > 0 ? (
                      <ul className="pp-owner-agent-ratings-avg-dims">
                        <li>
                          <span>Konuya hakimiyet</span>
                          <strong>{agg?.avg_subject_mastery != null ? agg.avg_subject_mastery.toFixed(1) : '—'}</strong>
                        </li>
                        <li>
                          <span>İlgi ve kurumsal nezaket</span>
                          <strong>{agg?.avg_courtesy != null ? agg.avg_courtesy.toFixed(1) : '—'}</strong>
                        </li>
                        <li>
                          <span>İletişim ve geri dönüş</span>
                          <strong>{agg?.avg_communication != null ? agg.avg_communication.toFixed(1) : '—'}</strong>
                        </li>
                        <li>
                          <span>Güvenilirlik ve şeffaflık</span>
                          <strong>{agg?.avg_trust != null ? agg.avg_trust.toFixed(1) : '—'}</strong>
                        </li>
                      </ul>
                    ) : null}
                  </div>
                  {canRate ? (
                    <form className="pp-owner-agent-ratings-form" onSubmit={onSubmitRating}>
                      <div className="pp-owner-agent-ratings-form-names">
                        <label className="pp-owner-agent-ratings-field">
                          <span>Ad</span>
                          <input
                            type="text"
                            name="reviewer_first_name"
                            autoComplete="given-name"
                            value={formFirst}
                            onChange={(e) => setFormFirst(e.target.value)}
                            maxLength={80}
                            required
                            disabled={agentRatingsBusy}
                          />
                        </label>
                        <label className="pp-owner-agent-ratings-field">
                          <span>Soyad</span>
                          <input
                            type="text"
                            name="reviewer_last_name"
                            autoComplete="family-name"
                            value={formLast}
                            onChange={(e) => setFormLast(e.target.value)}
                            maxLength={80}
                            required
                            disabled={agentRatingsBusy}
                          />
                        </label>
                      </div>
                      <ExpertOwnerStarRow
                        label="Konuya hakimiyet"
                        value={starSubject}
                        onChange={setStarSubject}
                        disabled={agentRatingsBusy}
                      />
                      <ExpertOwnerStarRow
                        label="İlgi ve kurumsal nezaket"
                        value={starCourtesy}
                        onChange={setStarCourtesy}
                        disabled={agentRatingsBusy}
                      />
                      <ExpertOwnerStarRow
                        label="İletişim ve geri dönüş"
                        value={starComm}
                        onChange={setStarComm}
                        disabled={agentRatingsBusy}
                      />
                      <ExpertOwnerStarRow
                        label="Güvenilirlik ve şeffaflık"
                        value={starTrust}
                        onChange={setStarTrust}
                        disabled={agentRatingsBusy}
                      />
                      <label className="pp-owner-agent-ratings-comment">
                        <span>Yorum (isteğe bağlı)</span>
                        <textarea
                          name="comment"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          rows={3}
                          maxLength={2000}
                          disabled={agentRatingsBusy}
                          placeholder="Deneyiminizi kısaca paylaşın…"
                        />
                      </label>
                      {rates.my_rating ? (
                        <div className="pp-owner-agent-ratings-form-actions">
                          {persistedCommentTrimmed ? (
                            <button
                              type="button"
                              className="pp-owner-agent-ratings-remove-comment"
                              disabled={agentRatingsBusy}
                              onClick={() => {
                                if (!rates.my_rating) return;
                                if (!window.confirm('Yorumunuz kaldırılsın mı? Puanlarınız aynı kalır; dilediğiniz zaman yeni yorum yazıp güncelleyebilirsiniz.')) return;
                                const mr = rates.my_rating;
                                ratingMutation.mutate(
                                  {
                                    reviewer_first_name: String(mr.reviewer_first_name || '').trim(),
                                    reviewer_last_name: String(mr.reviewer_last_name || '').trim(),
                                    star_subject_mastery: mr.star_subject_mastery,
                                    star_courtesy: mr.star_courtesy,
                                    star_communication: mr.star_communication,
                                    star_trust: mr.star_trust,
                                    comment: '',
                                  },
                                  { onSuccess: () => setComment('') },
                                );
                              }}
                            >
                              Yorumu kaldır
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="pp-owner-agent-ratings-delete-all"
                            disabled={agentRatingsBusy}
                            onClick={() => {
                              if (!window.confirm('Tüm değerlendirmeniz (yıldızlar ve yorum) silinsin mi? İsterseniz sonra yeniden gönderebilirsiniz.')) return;
                              deleteRatingMutation.mutate();
                            }}
                          >
                            Değerlendirmeyi tamamen sil
                          </button>
                        </div>
                      ) : null}
                      {ratingMutation.isError ? (
                        <p className="pp-detail-empty">{ratingMutation.error?.message || 'Gönderilemedi.'}</p>
                      ) : null}
                      {deleteRatingMutation.isError ? (
                        <p className="pp-detail-empty">{deleteRatingMutation.error?.message || 'Silinemedi.'}</p>
                      ) : null}
                      <button
                        type="submit"
                        className="pp-owner-agent-ratings-submit"
                        disabled={
                          agentRatingsBusy
                          || !formFirst.trim()
                          || !formLast.trim()
                          || !(starSubject && starCourtesy && starComm && starTrust)
                        }
                      >
                        {ratingMutation.isPending
                          ? 'Gönderiliyor…'
                          : deleteRatingMutation.isPending
                            ? 'Siliniyor…'
                          : rates.my_rating
                            ? 'Değerlendirmeyi güncelle'
                            : 'Değerlendirmeyi gönder'}
                      </button>
                    </form>
                  ) : (
                    <p className="pp-owner-agent-ratings-muted">Kendi profiliniz için değerlendirme gönderemezsiniz.</p>
                  )}
                  {rates.items?.length ? (
                    <div className="pp-owner-agent-ratings-list">
                      <h5 className="pp-owner-agent-ratings-list-title">Yorumlar</h5>
                      <ul className="pp-owner-agent-ratings-ul">
                        {rates.items.map((item) => (
                          <li key={item.id} className="pp-owner-agent-ratings-item">
                            <div className="pp-owner-agent-ratings-item-head">
                              <strong>{item.reviewer_display_name}</strong>
                              <ExpertOwnerStarsReadOnly value={item.avg_overall} />
                              <span className="pp-owner-agent-ratings-item-date">
                                {formatDateTime(item.created_at)}
                              </span>
                            </div>
                            {item.comment ? (
                              <p className="pp-owner-agent-ratings-item-text">{item.comment}</p>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SideNavOwnerCard({
  owner,
  listingId,
  viewerIsOwner,
  onOpenProfile,
  cardRef = null,
  className = '',
  variant = 'sidebar',
}) {
  const isBottomHorizontal = variant === 'bottom-horizontal';
  const ownerUserId = owner?.user_id ?? null;
  const { ratings: ownerBundleRatings } = useExpertModalBundle(ownerUserId);

  if (!owner) {
    return null;
  }

  const expertBlock = owner.badges?.expert_level;
  const interactive = Boolean(owner.user_id && typeof onOpenProfile === 'function');
  const firmTitle =
    (owner.company_name && String(owner.company_name).trim()) || '—';
  const displayName =
    (owner.full_name && String(owner.full_name).trim())
    || (owner.company_name && String(owner.company_name).trim())
    || 'Kullanıcı';

  const openProfile = () => {
    if (interactive) {
      onOpenProfile(owner.user_id);
    }
  };

  const phoneFmt = owner.phone ? formatTurkeyPhoneDisplayTel(owner.phone) : null;
  const showMessageRow = Boolean(expertBlock || listingId);
  const ownerRatingValueRaw = Number(ownerBundleRatings?.aggregate?.avg_overall);
  const ownerRatingValue = Number.isFinite(ownerRatingValueRaw) ? ownerRatingValueRaw : null;
  const ownerRatingDisplay = ownerRatingValue != null
    ? `${ownerRatingValue.toFixed(1).replace('.', ',')}/5`
    : null;
  const userKeyDown = interactive
    ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openProfile();
      }
    }
    : undefined;

  const firmRowKeyDown = interactive
    ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openProfile();
      }
    }
    : undefined;

  const footClick = interactive
    ? (e) => {
      if (e.target.closest('a')) {
        return;
      }
      openProfile();
    }
    : undefined;

  return (
    <div
      ref={cardRef}
      className={`pp-detail-side-contact-card${isBottomHorizontal ? ' pp-detail-side-contact-card--bottom-horizontal' : ''}${className ? ` ${className}` : ''}`}
    >
      <div
        className={`pp-detail-side-contact-user-hero${interactive ? ' pp-detail-side-contact-user-hero--interactive' : ''}`}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? openProfile : undefined}
        onKeyDown={userKeyDown}
      >
        <div className="pp-detail-side-contact-avatar-stage">
          {owner.avatar_url ? (
            <img className="pp-detail-side-contact-avatar" src={owner.avatar_url} alt={displayName} />
          ) : (
            <div className="pp-detail-side-contact-avatar pp-detail-side-contact-avatar-placeholder" aria-hidden>
              {(owner.full_name || owner.email || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="pp-detail-side-contact-user-meta">
          <strong className="pp-detail-side-contact-user-name">{displayName}</strong>
          {ownerRatingDisplay ? (
            <span className="pp-detail-side-contact-user-rating">
              <ExpertOwnerStarsReadOnly value={ownerRatingValue} />
              <span className="pp-detail-side-contact-user-rating-text">{ownerRatingDisplay}</span>
            </span>
          ) : null}
        </div>
      </div>
      <div className="pp-detail-side-contact-divider" aria-hidden="true" />
      <div
        className={`pp-detail-side-contact-firm-row${interactive ? ' pp-detail-side-contact-firm-row--interactive' : ''}`}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={interactive ? openProfile : undefined}
        onKeyDown={firmRowKeyDown}
      >
        <div
          className="pp-detail-side-contact-firm-logo-wrap"
          title={owner.company_logo_url ? firmTitle : undefined}
        >
          {owner.company_logo_url ? (
            <img
              className="pp-detail-side-contact-firm-logo"
              src={owner.company_logo_url}
              alt=""
            />
          ) : (
            <div className="pp-detail-side-contact-firm-logo-placeholder" aria-hidden="true" />
          )}
        </div>
        <div className="pp-detail-side-contact-firm-copy">
          <span className="pp-detail-side-contact-caption">Firma</span>
          <div className="pp-detail-side-contact-firm" title={firmTitle}>
            {firmTitle}
          </div>
        </div>
      </div>
      <div className="pp-detail-side-contact-divider" aria-hidden="true" />
      <div className="pp-detail-side-contact-info">
        <span className="pp-detail-side-contact-caption">İletişim</span>
        <div className="pp-detail-side-contact-info-list">
          {owner.email ? (
            <a
              href={`mailto:${owner.email}`}
              className="pp-detail-side-contact-link"
              onClick={(e) => e.stopPropagation()}
            >
              {owner.email}
            </a>
          ) : null}
          {phoneFmt ? (
            <a
              href={phoneToTelHref(owner.phone)}
              className="pp-detail-side-contact-link"
              onClick={(e) => e.stopPropagation()}
            >
              {phoneFmt.display}
            </a>
          ) : null}
        </div>
        {interactive ? (
          <button type="button" className="pp-detail-side-contact-profile-link" onClick={openProfile}>
            Profili Aç
          </button>
        ) : null}
      </div>
      {showMessageRow ? (
        <div
          className={`pp-detail-side-contact-foot${interactive ? ' pp-detail-side-contact-foot--interactive' : ''}`}
          onClick={footClick}
        >
          {expertBlock ? (
            <div className="pp-detail-owner-main-badge">
              <PortalOwnerBadgeTile
                label=""
                block={expertBlock}
                size="md"
                expertMain
                candidateOverlay="ADAY"
              />
            </div>
          ) : null}
          {listingId ? (
            <div
              className={`pp-detail-side-contact-msg-stack${expertBlock ? '' : ' pp-detail-side-contact-msg-stack--wide'}`}
            >
              <a
                href={
                  viewerIsOwner
                    ? '/portal/ilan/mesajlar/'
                    : `/portal/ilan/mesajlar/?listing=${encodeURIComponent(String(listingId))}`
                }
                className="pp-detail-owner-message-btn"
              >
                Mesaj
              </a>
              {owner.phone ? (
                <a
                  href={phoneToTelHref(owner.phone)}
                  className="pp-detail-owner-message-btn pp-detail-owner-message-btn--phone"
                >
                  Telefon
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Sayfa altı detay kartları: çiftlik tapu payı (yalnızca ciftlik_ev) + konfor/donanım alanları.
 */
function buildListingBottomDetailCardBlocks(summary) {
  const listingAttrs =
    summary?.listing_attributes && typeof summary.listing_attributes === 'object'
      ? summary.listing_attributes
      : {};
  const qt = String(summary?.query_type || '').trim().toLowerCase();
  const blocks = [];

  if (qt === 'ciftlik_ev') {
    const v = listingAttrs[LISTING_ATTR_KEY_CIFTLIK_LAND_TAPU];
    if (v != null && String(v).trim() !== '') {
      blocks.push({
        key: 'ciftlik-tapu',
        title: 'Çiftlik',
        rows: [
          {
            label: formatListingAttrLabel(LISTING_ATTR_KEY_CIFTLIK_LAND_TAPU),
            value: formatListingAttrDisplayValue(LISTING_ATTR_KEY_CIFTLIK_LAND_TAPU, v),
          },
        ],
      });
    }
  }

  const comfortRows = [];
  for (const k of LISTING_ATTR_KEYS_BOTTOM_COMFORT_ORDER) {
    if (LISTING_ATTR_KEYS_SUPPRESS_DETAIL.has(k)) continue;
    const v = listingAttrs[k];
    if (v == null || String(v).trim() === '') continue;
    comfortRows.push({
      label: formatListingAttrLabel(k),
      value: formatListingAttrDisplayValue(k, v),
    });
  }
  if (comfortRows.length) {
    blocks.push({
      key: 'comfort',
      title: 'Konfor ve donanım',
      rows: comfortRows,
    });
  }

  return blocks;
}

function ListingBottomDetailCardsSection({ summary }) {
  const blocks = useMemo(() => buildListingBottomDetailCardBlocks(summary), [summary]);
  if (!blocks.length) return null;

  return (
    <section
      className="pp-detail-listing-extra-cards pp-detail-listing-extra-cards--under-desc"
      aria-label="İlan detay özellikleri"
    >
      <div className="pp-detail-listing-extra-cards-stack">
        {blocks.map((block) => (
          <div
            key={block.key}
            className={`pp-detail-listing-extra-card${block.key === 'comfort' ? ' pp-detail-listing-extra-card--comfort' : ''}`}
          >
            <h3 className="pp-detail-listing-extra-card-title">{block.title}</h3>
            {block.key === 'comfort' ? (
              <div className="pp-detail-comfort-chips" role="list">
                {block.rows.map((row) => (
                  <div key={row.label} className="pp-detail-comfort-chip" role="listitem">
                    <span className="pp-detail-comfort-chip-label">{row.label}</span>
                    <span className="pp-detail-comfort-chip-value">{row.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pp-detail-listing-extra-card-body">
                {block.rows.map((row) => (
                  <div className="pp-detail-info-row" key={row.label}>
                    <span>{row.label}</span>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** Valuation passthrough — motor/API alanları olduğu gibi okunur; çapraz türetme yok. */
function getPortalValuationDisplay(summary) {
  const vc = summary?.valuation_canonical;
  const sp = summary?.portal_summary_prices;

  const areaRaw =
    summary?.area_m2 ??
    sp?.area_m2 ??
    vc?.area_m2_used ??
    summary?.arazi_m2 ??
    sp?.arazi_m2;
  const area =
    areaRaw != null && areaRaw !== '' && !Number.isNaN(Number(areaRaw)) && Number(areaRaw) > 0
      ? Number(areaRaw)
      : null;

  let unit = null;
  if (vc?.final_unit_price_m2 != null && !Number.isNaN(Number(vc.final_unit_price_m2))) {
    unit = Number(vc.final_unit_price_m2);
  } else if (sp?.unit_price != null && !Number.isNaN(Number(sp.unit_price))) {
    unit = Number(sp.unit_price);
  } else if (summary?.unit_price != null && !Number.isNaN(Number(summary.unit_price))) {
    unit = Number(summary.unit_price);
  }

  let total = null;
  if (vc?.final_total_tl != null && !Number.isNaN(Number(vc.final_total_tl))) {
    total = Number(vc.final_total_tl);
  } else if (sp?.total_price != null && !Number.isNaN(Number(sp.total_price))) {
    total = Number(sp.total_price);
  } else if (summary?.total_price != null && !Number.isNaN(Number(summary.total_price))) {
    total = Number(summary.total_price);
  }

  return { unit, total, area };
}

/** { label, value } — sağ kolonda daha küçük gösterilen arazi birim fiyatı */
function getAraziLandUnitSublineParts(summary) {
  const { unit } = getPortalValuationDisplay(summary);
  if (unit == null || !Number.isFinite(unit)) return null;
  return {
    label: 'Birim fiyat (TL/m²)',
    value: formatCurrency(unit),
  };
}

/** Üst sabit şerit — info kartındaki konum, ada/parsel ve arazi m² ile aynı metin. */
function buildDetailModeNoticeTitle(summary) {
  if (!summary) return '';

  const location = [summary.city_name, summary.town_name, summary.quarter_name]
    .filter(Boolean)
    .join(' / ');

  const adaParsel = `${summary.ada || '0'}/${summary.parsel || '0'}`;

  const { area } = getPortalValuationDisplay(summary);
  const areaRaw = area ?? summary.area_m2 ?? summary.arazi_m2;
  let areaPart = null;
  if (areaRaw != null && areaRaw !== '' && !Number.isNaN(Number(areaRaw)) && Number(areaRaw) > 0) {
    areaPart = `${formatArea(areaRaw)} m2`;
  }

  return [location, adaParsel, areaPart].filter(Boolean).join(' · ');
}

/** Parsel özet kartında gösterilmeyecek satır etiketleri. */
const PARCEL_INFO_CARD_HIDDEN_ROW_LABELS = new Set([
  'oluşturma tarihi',
  'parsel şekli',
  'önemli cephe',
  'uzun cephe',
  'toplam yol cephesi',
  'cephe sayısı',
]);

const PARCEL_INFO_CARD_LISTING_LEAD_LABELS = new Set(['ilan tipi', 'ilan m²']);
const PARCEL_INFO_CARD_PRIORITY_LABELS = ['ada / parsel', 'arazi m²'];

function normalizeParcelInfoRowLabel(label) {
  return String(label || '').trim().toLocaleLowerCase('tr-TR');
}

function organizeParcelInfoCardRows(rows) {
  const filtered = (rows || []).filter(
    (row) => !PARCEL_INFO_CARD_HIDDEN_ROW_LABELS.has(normalizeParcelInfoRowLabel(row?.label)),
  );
  const listingLead = [];
  const priorityByLabel = new Map();
  const rest = [];

  for (const row of filtered) {
    const key = normalizeParcelInfoRowLabel(row.label);
    if (PARCEL_INFO_CARD_LISTING_LEAD_LABELS.has(key)) {
      listingLead.push(row);
    } else if (PARCEL_INFO_CARD_PRIORITY_LABELS.includes(key)) {
      priorityByLabel.set(key, row);
    } else {
      rest.push(row);
    }
  }

  const priority = PARCEL_INFO_CARD_PRIORITY_LABELS
    .map((key) => priorityByLabel.get(key))
    .filter(Boolean);

  return [...listingLead, ...priority, ...rest];
}

function DfaSummaryCard({
  summary,
  valuationLocked,
  onProSorguInteraction,
  listingOnly = false,
  isEnriching = false,
  supplementalCoreRows = [],
  structureOverviewGrid = false,
  stackExtra = null,
}) {
  const isStructure = isBuildingType(summary.query_type);
  const buildingCostLabel = structureCostLabelTitleCase(summary.query_type);
  const isFromListing = Boolean(summary.listing_id);

  const coreRows = organizeParcelInfoCardRows(
    buildParcelCardRows(summary, {
      supplementalRows: supplementalCoreRows,
      formatDateTime,
      getLandDisplay: getPortalValuationDisplay,
      listingTypeLabelTr,
    }),
  );
  const createdAtLabel = summary.created_at ? formatDateTime(summary.created_at) : null;

  const araziLandUnitParts = !isStructure ? getAraziLandUnitSublineParts(summary) : null;
  const arsaFiyatNum =
    summary.arsa_fiyati != null && summary.arsa_fiyati !== ''
      ? Number(summary.arsa_fiyati)
      : null;
  const showAraziMaliyeti =
    isStructure &&
    arsaFiyatNum != null &&
    Number.isFinite(arsaFiyatNum);

  /** API: admin / SPK kurumsal / SPK danışman — diğer rollerde yapı+arazi maliyeti satırları yok. */
  const canSeeStructureCost =
    !isStructure || summary.viewer_can_see_structure_cost_breakdown === true;

  const valuationRows = isStructure
    ? [
        { label: 'Uygulanan Toplam % Oran', value: formatTotalAppliedPercent(summary) },
        ...(canSeeStructureCost && summary.bina_maliyeti
          ? [{ label: buildingCostLabel, value: formatCurrency(summary.bina_maliyeti) }]
          : []),
        ...(canSeeStructureCost && showAraziMaliyeti
          ? [{ label: 'Arazi maliyeti', value: formatCurrency(arsaFiyatNum) }]
          : []),
        {
          label: 'Toplam fiyat (TL)',
          value: formatCurrency(summary.total_price),
          prominent: true,
        },
      ]
    : [
        { label: 'Uygulanan Toplam % Oran', value: formatTotalAppliedPercent(summary) },
        {
          label: 'Toplam fiyat (TL)',
          value: formatCurrency(summary.total_price),
          prominent: true,
          landUnitParts: araziLandUnitParts,
        },
      ];

  const fxPortalLine = buildFxPortalTotalsLine(summary.fx_portal);

  const listingCurrencyRaw = summary.listing_currency != null && summary.listing_currency !== ''
    ? String(summary.listing_currency).trim()
    : '';
  const listingCurrencyNorm = listingCurrencyRaw.toUpperCase();
  const listingCurrencySuffix =
    listingCurrencyNorm && !['TRY', 'TL', '₺'].includes(listingCurrencyNorm)
      ? listingCurrencyNorm
      : '';
  const listingPrice =
    summary.listing_price_amount != null && summary.listing_price_amount !== ''
      ? `${formatCurrency(summary.listing_price_amount)}${listingCurrencySuffix ? ` ${listingCurrencySuffix}` : ''}`.trim()
      : null;

  const listingPriceNum = Number(summary.listing_price_amount);
  const listingAreaNum = Number(summary.listing_area_m2);
  const listingM2UnitPrice =
    listingPrice &&
    Number.isFinite(listingPriceNum) &&
    Number.isFinite(listingAreaNum) &&
    listingAreaNum > 0
      ? `${formatCurrency(listingPriceNum / listingAreaNum)} / m²`
      : null;

  const locationLine =
    [summary.city_name, summary.town_name, summary.quarter_name].filter(Boolean).join(' / ')
    || 'Konum bilgisi yok';
  const listingOnlyKindLabel = listingOnly
    ? (detailKindHeadline(summary)?.toLocaleUpperCase('tr-TR') || null)
    : null;

  const primaryCard = (
    <div className={`pp-detail-top-card pp-detail-top-card--parcel${!isFromListing ? ' pp-detail-top-card--listing-style' : ''}`}>
      <div className="pp-detail-info-card pp-detail-parcel-info-card">
        <ParcelSummaryHeader
          title={summary.title || 'Sorgu Detayı'}
          location={locationLine}
          kindLabel={listingOnlyKindLabel}
          createdAt={summary.created_at ?? null}
          createdAtLabel={createdAtLabel}
        />
        <ParcelSummaryInfoRows rows={coreRows} />
        {listingPrice ? (
          <div className="pp-detail-summary-listing-block">
            <ParcelSummaryInfoRows
              rows={[
                { label: 'İlan fiyatı', value: listingPrice, prominent: true },
                ...(listingM2UnitPrice && !isStructure
                  ? [{ label: 'İlan m² fiyatı', value: listingM2UnitPrice }]
                  : []),
              ]}
            />
          </div>
        ) : null}
      </div>
    </div>
  );

  const structureInfoCard = isStructure && shouldRenderStructureInfoCard(summary) ? (
    <StructureInfoSummaryCard summary={summary} />
  ) : null;

  const renderStructureOverviewSplit = (priceCard) => (
    <>
      <div className="pp-detail-top-stack pp-detail-top-stack--structure-right">
        {primaryCard}
        {structureInfoCard}
        {stackExtra}
      </div>
      <div className="pp-detail-top-structure-price-slot">
        {priceCard}
      </div>
    </>
  );

  if (!isFromListing) {
    const proparcelSystemCard = (
      <div className="pp-detail-top-card pp-detail-top-card--proparcel-system pp-detail-top-card--price">
        <div className="pp-detail-info-card pp-detail-price-info-card">
          <ParcelSummaryPriceHeader title="ProParcel fiyatı" />
          <ParcelSummaryInfoRows rows={valuationRows} variant="price" />
          <ParcelSummaryCurrencyStrip line={fxPortalLine} />
        </div>
      </div>
    );
    if (structureOverviewGrid && structureInfoCard) {
      return renderStructureOverviewSplit(proparcelSystemCard);
    }
    return (
      <>
        {primaryCard}
        {structureInfoCard}
        {proparcelSystemCard}
      </>
    );
  }

  if (listingOnly) {
    return (
      <>
        {primaryCard}
        {structureInfoCard}
      </>
    );
  }

  const prosorguPriceCard = (
    <div
      className={`pp-detail-top-card pp-detail-top-card--prosorgu-wrap pp-detail-top-card--price${valuationLocked ? ' is-prosorgu-locked' : ''}`}
    >
      <div
        className="pp-detail-info-card pp-detail-info-card--proparcel-valuation pp-detail-price-info-card"
        aria-hidden={valuationLocked}
      >
        <ParcelSummaryPriceHeader title="Proparcel Değerlemesi" />
        {isEnriching && !valuationRows.length && !valuationLocked ? (
          <DetailEnrichmentSkeleton label="Değerleme detayları yükleniyor…" />
        ) : (
          <>
            <ParcelSummaryInfoRows rows={valuationRows} variant="price" />
            <ParcelSummaryCurrencyStrip line={fxPortalLine} />
          </>
        )}
      </div>
      {valuationLocked ? (
        <button
          type="button"
          className="pp-detail-prosorgu-overlay"
          onClick={onProSorguInteraction}
          aria-label="ProSorgu: Tepe Coin ile aç"
        >
          <div className="pp-detail-prosorgu-overlay-card">
            <span className="pp-detail-prosorgu-overlay-brand">ProSorgu</span>
            <p className="pp-detail-prosorgu-overlay-hint">Detayları görmek için dokunun — 1 Tepe Coin</p>
          </div>
        </button>
      ) : null}
    </div>
  );

  if (structureOverviewGrid && structureInfoCard) {
    return renderStructureOverviewSplit(prosorguPriceCard);
  }

  return (
    <>
      {primaryCard}
      {structureInfoCard}
      {prosorguPriceCard}
    </>
  );
}

function ListingDescriptionTab({ summary }) {
  const desc = summary.listing_description != null ? String(summary.listing_description) : '';
  const listingVideos = Array.isArray(summary.listing_videos) ? summary.listing_videos : [];
  const [descCollapsed, setDescCollapsed] = useState(false);
  const activeVideoUrl = listingVideos[0]?.processed_url ? String(listingVideos[0].processed_url) : null;

  return (
    <>
      <SectionFrame title="İlan açıklaması ve detayları" subtitle="İlan metni ve seçilen özellikler">
        <div className="pp-detail-listing-desc-sahibinden">
          <div className="pp-detail-sb-tabs pp-detail-sb-tabs--single" aria-label="İlan detayı">
            <div className="pp-detail-sb-tab is-active">İlan Detayları</div>
          </div>
          {listingVideos.length ? (
            <div className="pp-detail-listing-video-section">
              <div className="pp-detail-sb-desc-panel-head">
                <span className="pp-detail-sb-desc-panel-title">Video</span>
              </div>
              <div className="pp-detail-listing-video-player-wrap">
                <video
                  key={activeVideoUrl || 'listing-video'}
                  className="pp-detail-listing-video-player"
                  src={activeVideoUrl || listingVideos[0]?.processed_url || ''}
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
            </div>
          ) : null}
          <div className="pp-detail-sb-desc-panel">
            <div className="pp-detail-sb-desc-panel-head">
              <span className="pp-detail-sb-desc-panel-title" id="pp-detail-sb-desc-heading">
                Açıklama
              </span>
              <button
                type="button"
                className="pp-detail-sb-desc-toggle"
                onClick={() => setDescCollapsed((c) => !c)}
                aria-expanded={!descCollapsed}
                aria-controls="pp-detail-sb-desc-body"
                aria-label={descCollapsed ? 'Açıklamayı göster' : 'Açıklamayı gizle'}
              >
                <span className="pp-detail-sb-desc-toggle-icon" aria-hidden="true">
                  {descCollapsed ? '▼' : '▲'}
                </span>
              </button>
            </div>
            {!descCollapsed ? (
              desc ? (
                <div
                  id="pp-detail-sb-desc-body"
                  role="region"
                  aria-labelledby="pp-detail-sb-desc-heading"
                  className="pp-detail-sb-desc-panel-body pp-detail-section-note pp-detail-listing-desc pp-html-rich"
                  dangerouslySetInnerHTML={{ __html: sanitizeListingHtml(desc) }}
                />
              ) : (
                <div
                  id="pp-detail-sb-desc-body"
                  role="region"
                  aria-labelledby="pp-detail-sb-desc-heading"
                  className="pp-detail-sb-desc-panel-body pp-detail-empty"
                >
                  Açıklama yok.
                </div>
              )
            ) : null}
          </div>
        </div>
      </SectionFrame>
      <ListingBottomDetailCardsSection summary={summary} />
    </>
  );
}

/** Arazi DFA adım tablosu — yapı sorgularında «Arazi Metrikleri Açıklaması». */
function LandDfaMetricsExplanation({
  mergedRows,
  expandedDfaRowKey,
  setExpandedDfaRowKey,
  hasRoadOverride,
  hasElectricOverride,
  canSeeRoadActions,
  onOpenRoadModal,
  onOpenElectricModal,
  canOpenRoadModal,
  canOpenElectricModal,
  showSectionTitle = false,
}) {
  if (!mergedRows.length) return null;

  return (
    <div className="pp-detail-land-metrics-block">
      {showSectionTitle ? (
        <div className="pp-detail-dfa-summary-title">Arazi Metrikleri Açıklaması</div>
      ) : null}
      <div className="pp-detail-dfa-table-wrap">
        <table className="pp-detail-dfa-table pp-detail-dfa-table--steps">
          <thead>
            <tr>
              <th>Açıklama</th>
              <th>Yüzde</th>
              <th className="pp-detail-dfa-action-col">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {mergedRows.map((row) => {
              const descLower = String(row?.description || '').toLowerCase();
              const canReportRoad = row.portalReportRoad === true;
              const canReportElectric = row.portalReportElectric === true;
              const isElectricRelatedRow = descLower.includes('yüksek gerilim') || descLower.includes('elektrik hattı');
              const isRoadReportedRow = canReportRoad && Boolean(hasRoadOverride);
              const isElectricReportedRow = canReportElectric && Boolean(hasElectricOverride);
              const isExpanded = expandedDfaRowKey === row.key;
              const canExpand = Boolean(row.hasDetails);
              const toggleExpand = () => {
                if (!canExpand) return;
                setExpandedDfaRowKey((prev) => (prev === row.key ? null : row.key));
              };
              return (
                <>
                  <tr
                    className={`tone-${row.tone}${(isRoadReportedRow || isElectricReportedRow) ? ' tone-road-reported' : ''}${canExpand ? ' pp-detail-dfa-row--expandable' : ''}${isExpanded ? ' pp-detail-dfa-row--expanded' : ''}`}
                  >
                    <td
                      className={canExpand ? 'pp-detail-dfa-desc-cell--clickable' : undefined}
                      onClick={canExpand ? toggleExpand : undefined}
                      onKeyDown={
                        canExpand
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                toggleExpand();
                              }
                            }
                          : undefined
                      }
                      role={canExpand ? 'button' : undefined}
                      tabIndex={canExpand ? 0 : undefined}
                      aria-expanded={canExpand ? isExpanded : undefined}
                    >
                      <div>{row.description}</div>
                      {isElectricRelatedRow && hasElectricOverride ? (
                        <div className="pp-detail-dfa-user-note">Hat bildirimi kullanıcı tarafından yapılmıştır.</div>
                      ) : null}
                      {canExpand ? (
                        <div className="pp-detail-dfa-expand-hint">{isExpanded ? 'Detayı gizle' : 'Detayı göster'}</div>
                      ) : null}
                    </td>
                    <td>{row.percent}</td>
                    <td className="pp-detail-dfa-action-col">
                      {canReportRoad && canSeeRoadActions ? (
                        <button
                          type="button"
                          className="pp-detail-dfa-row-action-btn"
                          onClick={onOpenRoadModal}
                          disabled={!canOpenRoadModal}
                        >
                          Yol Bildir
                        </button>
                      ) : null}
                      {canReportElectric && canSeeRoadActions ? (
                        <button
                          type="button"
                          className="pp-detail-dfa-row-action-btn"
                          onClick={onOpenElectricModal}
                          disabled={!canOpenElectricModal}
                        >
                          Hat Bildir
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {isExpanded && canExpand ? (
                    <tr className="pp-detail-dfa-details-row">
                      <td colSpan={3}>
                        <div className="pp-detail-dfa-details-panel">
                          {(row.details || []).map((line, detailIdx) => (
                            <p key={`${row.key}-detail-${detailIdx}`} className="pp-detail-dfa-detail-line">
                              {line}
                            </p>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Arazi DFA başlangıç/bitiş özeti — arsa/tarla ve ticari arazi-only fallback. */
function LandDfaPriceSummaryGrid({
  title,
  totalLabel,
  displayFooter,
  appliedPercentLabel,
}) {
  return (
    <div className="pp-detail-dfa-summary-wrap" aria-label="Değerleme özeti">
      <div className="pp-detail-dfa-summary-title">{title}</div>
      <div className="pp-detail-dfa-summary-grid">
        <div className="pp-detail-dfa-summary-item">
          <span>Başlangıç (TL/m²)</span>
          <strong>
            {displayFooter.startUnit != null ? formatCurrency(displayFooter.startUnit) : '—'}
          </strong>
        </div>
        <div className="pp-detail-dfa-summary-item">
          <span>Bitiş (TL/m²)</span>
          <strong>
            {displayFooter.endUnit != null ? formatCurrency(displayFooter.endUnit) : '—'}
          </strong>
        </div>
        <div className="pp-detail-dfa-summary-item">
          <span>Uygulanan %</span>
          <strong>{appliedPercentLabel}</strong>
        </div>
        <div className="pp-detail-dfa-summary-item">
          <span>{totalLabel}</span>
          <strong>
            {displayFooter.total != null ? formatCurrency(displayFooter.total) : '—'}
          </strong>
        </div>
      </div>
    </div>
  );
}

function OverviewTab({
  summary,
  simulatedFooter = null,
  onSimulatedFooterChange,
  onOpenRoadModal,
  onOpenElectricModal,
  canOpenRoadModal,
  canOpenElectricModal,
  canSeeRoadActions,
  hasRoadOverride,
  hasElectricOverride,
  isEnriching = false,
  enrichmentLoadFailed = false,
  onRetryEnrichment,
}) {
  const queryClient = useQueryClient();
  const [mahalleOrtInput, setMahalleOrtInput] = useState('');
  const [mahalleOrtNotice, setMahalleOrtNotice] = useState('');
  const [mahalleOrtBildirSubmitting, setMahalleOrtBildirSubmitting] = useState(false);
  const [mahalleOrtHesaplaSubmitting, setMahalleOrtHesaplaSubmitting] = useState(false);
  const [creditCelebration, setCreditCelebration] = useState(null);
  const [expandedDfaRowKey, setExpandedDfaRowKey] = useState(null);
  const dfaRows = useMemo(() => buildDfaRows(summary), [summary]);
  const dfaFooter = useMemo(() => getPortalDfaPriceFooter(summary), [summary]);

  const displayFooter = simulatedFooter ?? dfaFooter;
  const appliedPercentLabel = simulatedFooter?.appliedPercent ?? formatTotalAppliedPercent(summary);
  const structureQuery = isStructurePortalQueryType(summary?.query_type);
  const landOnlyStructure = isLandOnlyStructurePortalSummary(summary);
  const hasStructureEnrichmentCards =
    Boolean(summary?.valuation_layers_summary)
    || (Array.isArray(summary?.building_dfa_json) && summary.building_dfa_json.length > 0)
    || Boolean(summary?.land_price_summary);
  const landDfaSummaryTitle = landOnlyStructure || !structureQuery
    ? 'Değerleme özeti (özet API)'
    : 'Arazi değerlemesi özeti';
  const landTotalLabel = landOnlyStructure || !structureQuery ? 'Toplam (TL)' : 'Arazi toplam (TL)';

  const refreshKmAfterMahalleOrtSave = (snapshotId) => {
    queryClient.invalidateQueries({ queryKey: ['portal-detail-section', snapshotId, 'km'] });
    const pv = summary?.proparcel_value;
    const qt = summary?.query_type;
    if (pv != null) {
      queryClient.invalidateQueries({ queryKey: ['portal-km-price-map', pv, qt] });
    }
  };

  useEffect(() => {
    setMahalleOrtInput('');
    onSimulatedFooterChange?.(null);
    setMahalleOrtNotice('');
  }, [onSimulatedFooterChange, summary?.snapshot_id]);

  const resolveMahalleOrtUnit = () => {
    const parsed = parseMahalleOrtInput(mahalleOrtInput);
    if (parsed != null) return parsed;
    const start = Number(dfaFooter.startUnit);
    return Number.isFinite(start) && start > 0 ? start : null;
  };

  const applyMahalleOrtSimulation = (simulation) => {
    const footer = mapMahalleOrtSimulationToFooter(simulation);
    if (footer) {
      onSimulatedFooterChange?.(footer);
    }
  };

  const applyMahalleOrtReward = (res) => {
    const reward = res?.reward;
    if (!reward?.show_celebration_modal) return;
    setCreditCelebration({
      credits: reward.credit_awarded ?? 1,
      message: reward.celebration_message || 'Tebrikler! 1 Tepe Coin kazandınız.',
    });
    const attempts = reward.attempts_remaining;
    if (Number.isFinite(attempts) && attempts >= 0) {
      setMahalleOrtNotice((prev) => {
        const base = prev || 'İşlem tamamlandı.';
        return `${base} Kalan deneme hakkı: ${attempts}.`;
      });
    }
  };

  const viewerIsExpert = Boolean(summary?.viewer_is_expert);

  const handleMahalleOrtHesapla = async () => {
    const unit = resolveMahalleOrtUnit();
    const snapshotId = summary?.snapshot_id;
    if (unit == null || !snapshotId || mahalleOrtHesaplaSubmitting) {
      onSimulatedFooterChange?.(null);
      return;
    }

    setMahalleOrtHesaplaSubmitting(true);
    setMahalleOrtNotice('');
    try {
      const res = await postPortalMahalleOrtSignal(snapshotId, unit, { persist: false });
      const displaySimulation = res?.db_simulation || res?.simulation;
      if (displaySimulation) {
        applyMahalleOrtSimulation(displaySimulation);
        setMahalleOrtInput(String(Math.round(unit)));
      }
      if (res?.db_saved) {
        setMahalleOrtNotice(res?.message || 'Mahalle ortalaması güncellendi.');
        refreshKmAfterMahalleOrtSave(snapshotId);
      } else if (res?.message) {
        setMahalleOrtNotice(res.message);
      }
      applyMahalleOrtReward(res);
      if (!res?.reward?.show_celebration_modal && res?.reward?.badge_counted) {
        setMahalleOrtNotice('Katılımınız kaydedildi. Bu parsel için kredi penceresi henüz dolmadı.');
      }
    } catch (err) {
      if (err?.apiData?.simulation) {
        applyMahalleOrtSimulation(err.apiData.simulation);
        setMahalleOrtInput(String(Math.round(unit)));
      } else {
        onSimulatedFooterChange?.(null);
      }
      setMahalleOrtNotice(err?.message || 'Hesaplama yapılamadı.');
    } finally {
      setMahalleOrtHesaplaSubmitting(false);
    }
  };

  const handleMahalleOrtBildir = async () => {
    const unit = parseMahalleOrtInput(mahalleOrtInput);
    const snapshotId = summary?.snapshot_id;
    if (unit == null || !snapshotId || mahalleOrtBildirSubmitting) {
      return;
    }

    const submitMahalleOrt = async (confirmExtremePrice = false) => {
      const res = await postPortalMahalleOrtSignal(snapshotId, unit, {
        persist: true,
        confirmExtremePrice,
      });
      const displaySimulation = res?.db_simulation || res?.simulation;
      if (displaySimulation) {
        applyMahalleOrtSimulation(displaySimulation);
        setMahalleOrtInput(String(Math.round(unit)));
      }
      setMahalleOrtNotice('Mahalle ortalaması bildirildi.');
      refreshKmAfterMahalleOrtSave(snapshotId);
      applyMahalleOrtReward(res);
      if (!res?.reward?.show_celebration_modal && res?.reward?.badge_counted) {
        setMahalleOrtNotice('Mahalle ortalaması bildirildi. Bu parsel için kredi penceresi henüz dolmadı.');
      }
    };

    setMahalleOrtBildirSubmitting(true);
    try {
      await submitMahalleOrt(false);
    } catch (err) {
      if (err?.code === 'confirm_extreme_price') {
        if (err?.apiData?.simulation) {
          applyMahalleOrtSimulation(err.apiData.simulation);
        }
        const confirmed = typeof window !== 'undefined' && window.confirm(err.message);
        if (confirmed) {
          try {
            await submitMahalleOrt(true);
          } catch (confirmErr) {
            if (confirmErr?.apiData?.simulation) {
              applyMahalleOrtSimulation(confirmErr.apiData.simulation);
            }
            setMahalleOrtNotice(confirmErr?.message || 'Fiyat kaydedilemedi.');
          }
        } else {
          setMahalleOrtNotice('Onay verilmediği için fiyat veritabanına kaydedilmedi.');
        }
      } else {
        setMahalleOrtNotice(err?.message || 'Fiyat kaydedilemedi.');
      }
    } finally {
      setMahalleOrtBildirSubmitting(false);
    }
  };
  const roadExtra = useMemo(() => {
    const roadV2Frontage = resolveRoadV2Frontage(summary);
    const raw =
      roadV2Frontage?.totalLengthM
      ?? summary?.road_frontage_values?.total_road_frontage_edge_length_m;
    if (raw == null || raw === '' || Number.isNaN(Number(raw))) {
      return null;
    }
    return {
      key: 'road-frontage-dfa-row',
      description: 'Toplam yola cephe',
      percent: '—',
      unit: '—',
      total: formatRoadFrontageMeters(raw),
      tone: 'neutral',
    };
  }, [
    summary?.road_v2_frontage,
    summary?.road_frontage_values?.total_road_frontage_edge_length_m,
  ]);

  const mergedRows = useMemo(() => {
    const base = dfaRows || [];
    return roadExtra ? [roadExtra, ...base] : base;
  }, [dfaRows, roadExtra]);

  const mahalleOrtValid = resolveMahalleOrtUnit() != null;
  const hasLandDfaSteps = dfaRows.length > 0;
  const hasLandDfaTable = hasLandDfaSteps || Boolean(roadExtra);
  const hasStructureDfaLayout = structureQuery && (
    Boolean(summary?.valuation_layers_summary)
    || (Array.isArray(summary?.building_dfa_json) && summary.building_dfa_json.length > 0)
  );
  const showStructureLandPriceFallback =
    landOnlyStructure
    && (
      hasLandDfaTable
      || displayFooter.startUnit != null
      || displayFooter.endUnit != null
      || displayFooter.total != null
    );
  const showUserMahalleOrtEntry = Boolean(summary?.pro_query_credit_notice?.show);
  const showMahalleOrtControls = structureQuery
    ? hasLandDfaSteps || Boolean(summary?.valuation_layers_summary) || showUserMahalleOrtEntry
    : hasLandDfaSteps || showUserMahalleOrtEntry;

  const dfaSectionSubtitleNode = (
    <p>
      Piyasa verileri, ilanlar, danışman girdileri ve SPK lisanslı uzmanların katkılarıyla oluşturulan piyasa ortalaması esas alınmıştır.{' '}
      <strong>21 ana kriter üzerinde 120 kombinasyonla değerleme yapılmıştır.</strong>{' '}
      Değerleme sırasında sunduğumuz bu hesap makinesiyle yeniden hesaplama yapabilirsiniz.
      {viewerIsExpert
        ? ' «Mahalle Ortalaması Bildir» ile girdiğiniz fiyat sistemimizde kayıt altına alınır ve bölgesel analizlerde değerlendirilir.'
        : ' Fiyat bildirimi yalnızca uzman üyeler içindir; yeniden hesaplama ile katkıda bulunabilirsiniz.'}
    </p>
  );

  const mahalleOrtBelowHead =
    showMahalleOrtControls ? (
      <div className="pp-detail-dfa-mahalle-ort-wrap pp-detail-dfa-mahalle-ort-wrap--below-head">
        <label className="pp-detail-dfa-quick-label" htmlFor="pp-dfa-mahalle-ort-input">
          Mahalle Ortalaması
        </label>
        <input
          id="pp-dfa-mahalle-ort-input"
          type="text"
          inputMode="decimal"
          className="pp-detail-dfa-quick-input pp-detail-dfa-mahalle-ort-input"
          placeholder={dfaFooter.startUnit != null ? String(Math.round(dfaFooter.startUnit)) : 'TL/m²'}
          value={mahalleOrtInput}
          onInput={(e) => {
            setMahalleOrtInput(e.currentTarget.value);
            onSimulatedFooterChange?.(null);
            setMahalleOrtNotice('');
          }}
          onChange={(e) => {
            setMahalleOrtInput(e.currentTarget.value);
            onSimulatedFooterChange?.(null);
            setMahalleOrtNotice('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleMahalleOrtHesapla();
            }
          }}
          autoComplete="off"
        />
        <button
          type="button"
          className="pp-detail-dfa-mahalle-ort-btn"
          disabled={!mahalleOrtValid || mahalleOrtHesaplaSubmitting || mahalleOrtBildirSubmitting}
          onClick={handleMahalleOrtHesapla}
        >
          {mahalleOrtHesaplaSubmitting ? 'Hesaplanıyor…' : 'Yeniden Hesapla'}
        </button>
        {viewerIsExpert ? (
          <button
            type="button"
            className="pp-detail-dfa-mahalle-ort-btn pp-detail-dfa-mahalle-ort-btn--bildir"
            disabled={!mahalleOrtValid || mahalleOrtBildirSubmitting || mahalleOrtHesaplaSubmitting}
            onClick={handleMahalleOrtBildir}
          >
            {mahalleOrtBildirSubmitting ? 'Bildiriliyor…' : 'Mahalle Ortalaması Bildir'}
          </button>
        ) : null}
        {mahalleOrtNotice ? (
          <p className="pp-detail-dfa-mahalle-ort-notice" role="status">
            {mahalleOrtNotice}
          </p>
        ) : null}
        <CreditCelebrationModal
          open={Boolean(creditCelebration)}
          credits={creditCelebration?.credits ?? 1}
          message={creditCelebration?.message ?? ''}
          onClose={() => setCreditCelebration(null)}
        />
      </div>
    ) : null;

  const landDfaTableProps = {
    mergedRows,
    expandedDfaRowKey,
    setExpandedDfaRowKey,
    hasRoadOverride,
    hasElectricOverride,
    canSeeRoadActions,
    onOpenRoadModal,
    onOpenElectricModal,
    canOpenRoadModal,
    canOpenElectricModal,
  };

  return (
    <SectionFrame
      title="Gayrimenkul Değerleme Hesap Makinesi"
      subtitleNode={dfaSectionSubtitleNode}
      afterHead={mahalleOrtBelowHead}
    >
      {enrichmentLoadFailed ? (
        <div className="pp-detail-enrichment-notice pp-detail-enrichment-notice--error" role="alert">
          <p>Arazi ve yapı değerleme kartları yüklenemedi.</p>
          {onRetryEnrichment ? (
            <button type="button" className="pp-detail-enrichment-retry-btn" onClick={onRetryEnrichment}>
              Tekrar dene
            </button>
          ) : null}
        </div>
      ) : null}
      {(structureQuery ? isEnriching && !hasStructureEnrichmentCards : isEnriching && !hasLandDfaTable) ? (
        <DetailEnrichmentSkeleton label="Değerleme kartları yükleniyor…" />
      ) : null}
      {structureQuery ? (
        hasStructureDfaLayout || hasLandDfaTable || showStructureLandPriceFallback ? (
          <>
            {showStructureLandPriceFallback ? (
              <LandDfaPriceSummaryGrid
                title={landDfaSummaryTitle}
                totalLabel={landTotalLabel}
                displayFooter={displayFooter}
                appliedPercentLabel={appliedPercentLabel}
              />
            ) : null}
            <DetailStructureValuationStory summary={summary} simulatedLand={simulatedFooter} />
            <LandDfaMetricsExplanation {...landDfaTableProps} showSectionTitle />
          </>
        ) : !isEnriching ? (
          <div className="pp-detail-empty">DFA verisi bulunamadı.</div>
        ) : null
      ) : hasLandDfaTable ? (
        <>
          <LandDfaPriceSummaryGrid
            title={landDfaSummaryTitle}
            totalLabel={landTotalLabel}
            displayFooter={displayFooter}
            appliedPercentLabel={appliedPercentLabel}
          />
          <LandDfaMetricsExplanation {...landDfaTableProps} />
        </>
      ) : !isEnriching ? (
        <div className="pp-detail-empty">DFA verisi bulunamadı.</div>
      ) : null}
    </SectionFrame>
  );
}

function RoadOverrideModal({
  open,
  summary,
  selections,
  focusedEdgeId,
  onEdgeClick,
  onFocusEdge,
  onRoadTypeChange,
  onRemoveSelection,
  onSubmit,
  canSubmit,
  submitting,
  onClose,
}) {
  const { setModalOpen } = usePortalDetailMapLayers();
  const ring = useMemo(() => extractParcelRingFromSummary(summary), [summary]);
  const selectionMap = useMemo(() => {
    const map = {};
    (selections || []).forEach((item) => {
      if (item && Number.isFinite(Number(item.edge_id))) {
        map[Number(item.edge_id)] = item;
      }
    });
    return map;
  }, [selections]);
  const parcelData = useMemo(() => {
    if (!ring.length) return null;
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [[...ring, ring[0]]],
          },
          properties: {},
        },
      ],
    };
  }, [ring]);
  const edgeSelectionGeoJson = useMemo(
    () => buildSelectableParcelEdgeFeatures(ring, selectionMap, focusedEdgeId),
    [ring, selectionMap, focusedEdgeId],
  );

  useEffect(() => {
    setModalOpen('roadModal', open);
    return () => setModalOpen('roadModal', false);
  }, [open, setModalOpen]);

  const completedCount = useMemo(
    () => (selections || []).filter((item) => item?.road_type_id != null).length,
    [selections],
  );
  const titleText = completedCount > 0
    ? 'Başka kenarlarda da yol varsa haritadan seçip her kenar için yol tipini belirleyin'
    : 'Yol geçen kenarları haritadan seçin; birden fazla kenar seçebilirsiniz';

  if (!open) return null;
  return (
    <div className="pp-road-report-modal-overlay" onClick={onClose}>
      <div className="pp-road-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-road-report-modal-header">
          <h3>Yol Bildir</h3>
          <p>{titleText}</p>
        </div>
        <div className="pp-road-report-modal-body">
          <div className="pp-road-report-modal-map">
            <MapboxParcelMap
              hideTitle
              hideMapLabels
              parcelData={parcelData}
              edgeSelectionGeoJson={edgeSelectionGeoJson}
              onEdgeClick={onEdgeClick}
            />
          </div>
          <div className="pp-road-report-modal-panel">
            {(selections || []).length ? (
              (selections || []).map((item) => {
                const edgeId = Number(item.edge_id);
                const selectedType = item.road_type_id == null ? null : Number(item.road_type_id);
                return (
                  <div
                    key={`road-sel-${edgeId}`}
                    className={`pp-road-report-selection ${focusedEdgeId === edgeId ? 'is-focused' : ''}`}
                  >
                    <div className="pp-road-report-selection-head">
                      <button
                        type="button"
                        className="pp-road-report-edge-pill"
                        onClick={() => onFocusEdge(edgeId)}
                      >
                        Kenar {edgeId + 1}
                      </button>
                      <button
                        type="button"
                        className="pp-road-report-remove-btn"
                        onClick={() => onRemoveSelection(edgeId)}
                      >
                        Kaldır
                      </button>
                    </div>
                    <label className="pp-road-report-radio">
                      <input
                        type="radio"
                        name={`road-type-${edgeId}`}
                        checked={selectedType === 13}
                        onChange={() => onRoadTypeChange(edgeId, 13)}
                      />
                      <span>Kadastral Yol</span>
                    </label>
                    <label className="pp-road-report-radio">
                      <input
                        type="radio"
                        name={`road-type-${edgeId}`}
                        checked={selectedType === 9}
                        onChange={() => onRoadTypeChange(edgeId, 9)}
                      />
                      <span>Asfalt Yol</span>
                    </label>
                  </div>
                );
              })
            ) : (
              <div className="pp-road-report-empty">Haritada bir kenar seçin.</div>
            )}
          </div>
        </div>
        <div className="pp-road-report-modal-actions">
          <button type="button" className="pp-road-report-update-btn" onClick={onSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Güncelleniyor...' : 'Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ElectricOverrideModal({
  open,
  summary,
  lines,
  draftStart,
  draftCursor,
  selectedVoltageKv,
  onMapClick,
  onMapMove,
  onVoltageChange,
  onRemoveLine,
  onSubmit,
  canSubmit,
  submitting,
  onClose,
}) {
  const { store, revision, ensureModalLayers, setModalOpen } = usePortalDetailMapLayers();
  const ring = useMemo(() => extractParcelRingFromSummary(summary), [summary]);
  const parcelData = useMemo(() => {
    if (!ring.length) return null;
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] },
          properties: {},
        },
      ],
    };
  }, [ring]);
  const lineOverlays = useMemo(() => {
    const lineFeatures = (lines || []).map((line) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [line.start, line.end] },
      properties: {
        label_text: `${line.voltage_kv} KV`,
        color: '#eab308',
      },
    }));
    const overlays = [];
    if (lineFeatures.length) {
      overlays.push({
        id: 'user-electric-lines',
        data: { type: 'FeatureCollection', features: lineFeatures },
        color: '#eab308',
        lineWidth: 4,
        showLabels: true,
        labelField: 'label_text',
        labelTextSize: 12,
        labelColor: '#ffffff',
        labelHaloColor: 'rgba(0,0,0,0.9)',
        labelHaloWidth: 2,
        labelAllowOverlap: true,
        labelIgnorePlacement: true,
      });
    }
    if (draftStart && draftCursor) {
      overlays.push({
      id: 'user-electric-draft',
      data: { type: 'FeatureCollection', features: [{
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [draftStart, draftCursor] },
        properties: {},
      }] },
      color: '#eab308',
      lineWidth: 3,
      lineDasharray: [2, 2],
      });
    }
    return overlays;
  }, [lines, draftStart, draftCursor]);

  const modalOverlays = useMemo(
    () => store.composeForMap('electricModal'),
    [store, revision],
  );

  useEffect(() => {
    setModalOpen('electricModal', open);
    return () => setModalOpen('electricModal', false);
  }, [open, setModalOpen]);

  useEffect(() => {
    if (!open) return;
    ensureModalLayers('electricModal', lineOverlays);
  }, [open, lineOverlays, ensureModalLayers]);

  if (!open) return null;
  return (
    <div className="pp-road-report-modal-overlay" onClick={onClose}>
      <div className="pp-road-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pp-road-report-modal-header">
          <h3>Hat Bildir</h3>
          <p>
            Parsel üzerinde ilk noktayı seçin, sonra ikinci noktayı seçerek hattı tamamlayın.
            Yeni hat eklemek için aynı işlemi tekrarlayın.
          </p>
        </div>
        <div className="pp-road-report-modal-body">
          <div className="pp-road-report-modal-map">
            <MapboxParcelMap
              hideTitle
              hideMapLabels
              parcelData={parcelData}
              overlays={modalOverlays}
              onMapClick={({ lon, lat }) => onMapClick([lon, lat])}
              onMapMouseMove={({ lon, lat }) => onMapMove([lon, lat])}
            />
          </div>
          <div className="pp-road-report-modal-panel">
            <div className="pp-road-report-selection">
              <div className="pp-road-report-selection-head">
                <span className="pp-road-report-edge-pill">Enerji Tipi</span>
              </div>
              <label className="pp-road-report-radio">
                <input
                  type="radio"
                  name="electric-voltage"
                  checked={selectedVoltageKv === 64}
                  onChange={() => onVoltageChange(64)}
                />
                <span>Küçük Hat (64 KV) — Varsayılan</span>
              </label>
              <label className="pp-road-report-radio">
                <input
                  type="radio"
                  name="electric-voltage"
                  checked={selectedVoltageKv === 154}
                  onChange={() => onVoltageChange(154)}
                />
                <span>Orta Hat (154 KV)</span>
              </label>
              <label className="pp-road-report-radio">
                <input
                  type="radio"
                  name="electric-voltage"
                  checked={selectedVoltageKv === 380}
                  onChange={() => onVoltageChange(380)}
                />
                <span>Büyük Hat (380 KV)</span>
              </label>
            </div>
            {(lines || []).length ? (
              (lines || []).map((line, index) => (
                <div key={line.id || `el-${index}`} className="pp-road-report-selection">
                  <div className="pp-road-report-selection-head">
                    <span className="pp-road-report-edge-pill">Hat {index + 1} ({line.voltage_kv} KV)</span>
                    <button
                      type="button"
                      className="pp-road-report-remove-btn"
                      onClick={() => onRemoveLine(line.id)}
                    >
                      Kaldır
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="pp-road-report-empty">Henüz çizilen hat yok.</div>
            )}
          </div>
        </div>
        <div className="pp-road-report-modal-actions">
          <button type="button" className="pp-road-report-update-btn" onClick={onSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Güncelleniyor...' : 'Güncelle'}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderLazyTab(
  activeTab,
  snapshotId,
  summary,
  listingProLocked,
  structureQuery,
  overviewProps = {},
  tabCallbacks = {},
) {
  switch (activeTab) {
    case 'listing_description':
      return (
        <ListingDescriptionTab summary={summary} />
      );
    case 'expert':
      return <DetailExpertTab snapshotId={snapshotId} />;
    case 'fruit_investment':
      return <DetailFruitInvestmentTab snapshotId={snapshotId} />;
    case 'investment_score':
      return (
        <DetailInvestmentScoreTab
          snapshotId={snapshotId}
          summary={summary}
          structureQuery={structureQuery}
        />
      );
    case 'solar_energy':
      return <DetailSolarEnergyTab snapshotId={snapshotId} />;
    case 'wind_energy':
      return <DetailWindEnergyTab snapshotId={snapshotId} />;
    case 'km':
      return (
        <DetailKmTab
          snapshotId={snapshotId}
          onExpertRequest={tabCallbacks.onExpertRequest}
        />
      );
    case 'split':
      return <DetailSplitTab snapshotId={snapshotId} />;
    case 'electric':
      return <DetailElectricTab snapshotId={snapshotId} />;
    case 'sea_view':
      return <DetailSeaViewTab snapshotId={snapshotId} />;
    case 'quarter_sales':
      return (
        <DetailSalesTab
          summary={summary}
        />
      );
        case 'commission':
      return <DetailCommissionTab summary={summary} />;
    case 'imar_bilgileri':
      return <DetailImarBilgileriTab snapshotId={snapshotId} />;
    case 'mahalle_bilgileri':
      return <DetailMahalleBilgileriTab snapshotId={snapshotId} />;
    case 'sosyal_alanlar':
      return <DetailSosyalAlanlarTab snapshotId={snapshotId} />;
    case 'overview':
    default:
      return (
        <OverviewTab summary={summary} {...overviewProps} />
      );
  }
}

function getBackLinkHref() {
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('returnTo');

  if (returnTo && returnTo.startsWith('/portal/recent-queries/')) {
    return returnTo;
  }

  return '/portal/recent-queries/';
}

/** Üst toolbar sağ sayaçları — tutarlı rakam gösterimi (0, — veya % karışmaz). */
function formatTopbarStatNumber(value) {
  if (value == null || value === '') return '0';
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(Math.max(0, Math.round(n)));
}

function withDetailTimeout(promise, ms = 30_000) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => {
      reject(new Error('Detay verisi zamanında yüklenemedi. Lütfen tekrar deneyin.'));
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer) window.clearTimeout(timer);
  });
}

function DetailInsightSnapshotCard({
  snapshotId,
  summary,
  listingProLocked,
  riskChips = [],
  parcelRiskChips = [],
  roadMetrics = null,
  onOpenInvestmentTab,
  onOpenMulkScoreTab,
  onOpenKmTab,
  onOpenRoadTab,
  onOpenRatings = null,
  onQuarterCenterChipClick,
  quarterCenterMapActive = false,
  quarterCenterMapLoading = false,
  onImarChipClick,
  imarMapActive = false,
  imarMapLoading = false,
  onPopulationChipClick = null,
  populationMapActive = false,
  populationMapLoading = false,
  populationChipLines = null,
  onRamsarChipClick = null,
  ramsarMapActive = false,
  ramsarMapLoading = false,
  onHvChipClick = null,
  hvMapActive = false,
  hvMapLoading = false,
  onQuarterSalesChipClick = null,
  quarterSalesMapActive = false,
  quarterSalesMapLoading = false,
  onValuableRoadsChipClick = null,
  valuableRoadsMapActive = false,
  valuableRoadsMapLoading = false,
  variant = 'horizontal',
}) {
  const isSidebarVertical = variant === 'sidebar-vertical';
  const kindHeadline = detailKindHeadline(summary);
  const kindHeadlineUpper = kindHeadline ? kindHeadline.toLocaleUpperCase('tr-TR') : null;
  const cardRef = useRef(null);
  const insightVisible = useInViewport(cardRef, { rootMargin: '200px 0px', once: true });
  const invQuery = useQuery({
    queryKey: ['portal-investment-score', snapshotId],
    queryFn: () => getPortalInvestmentScore(snapshotId),
    enabled: insightVisible && Boolean(snapshotId) && !listingProLocked,
    staleTime: 60_000,
  });
  const analysis = invQuery.data?.analysis;
  const emptyReason = invQuery.data?.empty_reason;

  /** Genel Puan = mülk skoru (liste ile aynı formül; mahalle potansiyeli yok). */
  const insightStarScorePct = useMemo(() => {
    const md = invQuery.data?.meta_stars_denorm;
    const ss = invQuery.data?.structure_score;
    const structPct =
      md?.structure_score_pct ??
      (ss && ss.structure_score != null && ss.structure_score !== ''
        ? Number(ss.structure_score)
        : null);
    const rowLike = portalSummaryToListStarRow({
      ...summary,
      combined_meta_stars_pct:
        summary?.combined_meta_stars_pct ?? md?.combined_meta_stars_pct,
      investment_normalized_score:
        summary?.investment_normalized_score ??
        invQuery.data?.analysis?.normalized_score ??
        md?.investment_normalized_score,
      structure_score_pct: summary?.structure_score_pct ?? structPct,
      portal_star_blend: summary?.portal_star_blend ?? md?.portal_star_blend,
    });
    return rowLike ? resolveListRowMetaStarsPct(rowLike) : null;
  }, [summary, invQuery.data]);

  const hasInsightStarScore = insightStarScorePct != null;
  const showInsightEmptyOnly =
    invQuery.isFetched && Boolean(emptyReason) && !analysis && !hasInsightStarScore;

  const resolvedParcelRiskChips = useMemo(() => {
    if (analysis) {
      return buildInsightParcelRiskChips({ summary, analysis });
    }
    return parcelRiskChips || [];
  }, [summary, analysis, parcelRiskChips]);

  const riskHandlers = {
    onQuarterCenterChipClick,
    quarterCenterMapActive,
    quarterCenterMapLoading,
    onImarChipClick,
    imarMapActive,
    imarMapLoading,
    onPopulationChipClick,
    populationMapActive,
    populationMapLoading,
    onRamsarChipClick,
    ramsarMapActive,
    ramsarMapLoading,
    onHvChipClick,
    hvMapActive,
    hvMapLoading,
    onQuarterSalesChipClick,
    quarterSalesMapActive,
    quarterSalesMapLoading,
    onValuableRoadsChipClick,
    valuableRoadsMapActive,
    valuableRoadsMapLoading,
    onOpenRoadTab,
  };

  const priceCluster = (
    <div className="pp-detail-insight-price-cluster">
      <DetailInsightPriceCard
        summary={summary}
        onOpenKmTab={onOpenKmTab}
        kindLabel={kindHeadlineUpper}
      />
      <DetailInsightRatingActionsCard
        snapshotId={snapshotId}
        onOpenRatings={onOpenRatings}
      />
    </div>
  );

  return (
    <div
      ref={cardRef}
      className={`pp-detail-insight-card${isSidebarVertical ? ' pp-detail-insight-card--sidebar-vertical' : ''}`}
      aria-label="Özet karar kartı"
    >
      {listingProLocked ? (
        <div className="pp-detail-insight-locked">
          <p>
            <strong>Pro Sorgu</strong> ile açıldığında bu özet — fiyat, puan ve risk — tek bakışta
            görünür.
          </p>
          <button type="button" className="pp-detail-insight-unlock-hint" onClick={onOpenInvestmentTab}>
            Pro Sorgu’yu aç
          </button>
        </div>
      ) : invQuery.isLoading && !hasInsightStarScore ? (
        <div className="pp-detail-insight-loading">
          <PpLoaderAnalysisDots label="Yatırım analizi hesaplanıyor…" />
          <PpLoaderCardSkeleton compact />
        </div>
      ) : showInsightEmptyOnly ? (
        <div className="pp-detail-insight-empty">
          {emptyReason === 'not_analyzed'
            ? 'Mülk özeti henüz yok; hesap tamamlanınca bu kart dolacak.'
            : 'Özet için veri yok.'}
        </div>
      ) : (
        <>
          {invQuery.isError ? (
            <p className="pp-detail-insight-notice" role="status">
              Yatırım özeti şu an alınamadı; aşağıdaki alanlar özet verisinden gösteriliyor.
            </p>
          ) : null}
          {!listingProLocked ? (
            <div className="pp-detail-insight-analysis-panel">
              {hasInsightStarScore || analysis ? (
                <div
                  className="pp-detail-insight-rating-row"
                  role="group"
                  aria-label="Genel puan"
                >
                  <button
                    type="button"
                    className="pp-detail-insight-rating-stars"
                    aria-label="Genel puan. Mülk Skoru sekmesini aç."
                    onClick={onOpenMulkScoreTab}
                  >
                    <PortalMetaStars
                      scorePct={insightStarScorePct}
                      showOutOfFive
                      listingCategoryMain={summary?.listing_category_main}
                      categoryLeafId={summary?.listing_category_leaf_id ?? summary?.category_leaf_id}
                      listingCategoryLeafId={summary?.listing_category_leaf_id ?? summary?.category_leaf_id}
                      queryType={summary?.query_type}
                      imarsizLandStarBlend={summary?.imarsiz_land_star_blend}
                    />
                  </button>
                </div>
              ) : null}
              <div className={`pp-detail-insight-metrics-row${isSidebarVertical ? ' pp-detail-insight-metrics-row--sidebar-vertical' : ''}`}>
                {riskChips.length || resolvedParcelRiskChips.length || roadMetrics || onOpenRoadTab ? (
                  <DetailInsightRiskPanels
                    riskChips={riskChips}
                    parcelRiskChips={resolvedParcelRiskChips}
                    roadMetrics={roadMetrics}
                    populationChipLines={populationChipLines}
                    handlers={riskHandlers}
                  />
                ) : null}
                {priceCluster}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function handleBackLinkClick(event, href) {
  if (typeof window === 'undefined') {
    return;
  }

  const referrer = document.referrer || '';
  const sameOriginReferrer = referrer.startsWith(window.location.origin)
    ? referrer.slice(window.location.origin.length)
    : '';

  if (sameOriginReferrer.startsWith('/portal/recent-queries/')) {
    event.preventDefault();
    window.history.back();
    return;
  }

  if (window.history.length > 1 && href === window.location.pathname + window.location.search) {
    event.preventDefault();
    window.history.back();
  }
}

/** Sabit kopya / yüzen kart için üst boşluk: #main-topbar veya .pp-header. */
function getPortalTopbarBottomPx() {
  if (typeof document === 'undefined') return 60;
  const main = document.getElementById('main-topbar');
  if (main) {
    return Math.max(0, Math.round(main.getBoundingClientRect().bottom));
  }
  const header = document.querySelector('header.pp-header');
  if (header) {
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }
  return 60;
}

function PortalRecentQueryDetailAppInner({
  snapshotId,
  summary,
  requestedListingId = null,
  isEnriching = false,
  enrichmentLoadFailed = false,
  onRetryEnrichment,
}) {
  const queryClient = useQueryClient();
  const heroOverlaysEnabled = useDeferIdle({ timeoutMs: 1200 });
  const deferNonCriticalReady = useDeferIdle({ timeoutMs: 1500 });
  const ownerCardUserId = summary?.owner_card?.user_id ?? null;

  const {
    activeTab,
    setActiveTab,
    parcelFeatureCollection,
    heroCardMedia,
    setHeroCardMedia,
  } = useDetailPageStore();
  const {
    store,
    revision,
    ensureDefaultLayers,
    ensureQuarterCenterLayers,
    ensureTabLayers,
    setQuarterCenterVisible,
  } = usePortalDetailMapLayers();
  const compareSelection = usePortalCompareSelection();
  const listingOnly = Boolean(summary?.listing_only);
  const showSideInsight = !listingOnly && Boolean(summary?.owner_card);
  const showTopInsight = !listingOnly && !summary?.owner_card;
  const listingProLocked = !listingOnly && Boolean(summary.listing_pro_sorgu_locked);
  const isListingDetailView = Boolean(summary?.listing_id)
    || Boolean(String(requestedListingId || '').trim())
    || listingOnly;
  const [mahalleOrtSimulatedFooter, setMahalleOrtSimulatedFooter] = useState(null);
  const [belowFoldEager, setBelowFoldEager] = useState(false);
  const handleMahalleOrtSimulatedFooterChange = useCallback((footer) => {
    setMahalleOrtSimulatedFooter(footer);
  }, []);

  useEffect(() => {
    if (!deferNonCriticalReady || !ownerCardUserId) return undefined;
    prefetchExpertModalBundle(queryClient, ownerCardUserId);
    return undefined;
  }, [deferNonCriticalReady, ownerCardUserId, queryClient]);

  useEffect(() => {
    setBelowFoldEager(false);
  }, [summary?.snapshot_id]);

  useEffect(() => {
    if (!summary) return undefined;
    void import('./DetailRatingsTab.jsx');
    void import('./SimilarPublishedListingsSection.jsx');
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(() => setBelowFoldEager(true), { timeout: 1800 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timerId = window.setTimeout(() => setBelowFoldEager(true), 500);
    return () => window.clearTimeout(timerId);
  }, [summary?.snapshot_id]);

  const displaySummary = useMemo(
    () => applyMahalleOrtFooterToSummary(summary, mahalleOrtSimulatedFooter),
    [summary, mahalleOrtSimulatedFooter],
  );
  useEffect(() => {
    setMahalleOrtSimulatedFooter(null);
  }, [summary?.snapshot_id]);
  const detailModeNoticeHostRef = useRef(null);
  const [detailModeNoticeFloatingStyle, setDetailModeNoticeFloatingStyle] = useState(null);
  const [proQueryCreditNoticeModal, setProQueryCreditNoticeModal] = useState(null);
  const [favoriteCountTotal, setFavoriteCountTotal] = useState(() => {
    const initialCount = Number(summary?.favorite_count_total);
    return Number.isFinite(initialCount) && initialCount > 0 ? initialCount : 0;
  });
  const [queryFavoriteCountTotal, setQueryFavoriteCountTotal] = useState(() => {
    const initialCount = Number(summary?.favorite_count_total);
    return Number.isFinite(initialCount) && initialCount > 0 ? initialCount : 0;
  });
  const expertSectionSnapshotKey = useMemo(() => {
    const n = snapshotId != null && snapshotId !== '' ? Number(snapshotId) : NaN;
    return Number.isFinite(n) && n > 0 ? n : snapshotId;
  }, [snapshotId]);
  const expertMapQuery = useQuery({
    queryKey: ['portal-detail-section', expertSectionSnapshotKey, 'expert'],
    queryFn: () => getPortalDetailSection(expertSectionSnapshotKey, 'expert'),
    staleTime: 60_000,
    enabled: heroOverlaysEnabled && Boolean(snapshotId) && !listingOnly,
  });
  const ramsarMapQuery = useQuery({
    queryKey: ['portal-detail-section', snapshotId, 'ramsar'],
    queryFn: () => getPortalDetailSection(snapshotId, 'ramsar'),
    staleTime: 120_000,
    enabled: heroOverlaysEnabled && Boolean(snapshotId) && !listingOnly && !listingProLocked,
  });
  const defaultHeroMapOverlays = useMemo(() => {
    const overlays = [];
    const ramsarOverlay = buildRamsarHeroOverlay(ramsarMapQuery.data);
    if (ramsarOverlay) {
      overlays.push(ramsarOverlay);
    }
    const d = expertMapQuery.data;
    const responses = d?.responses ?? d?.Responses;
    const entries = mergeExpertResponseEmsalEntries(Array.isArray(responses) ? responses : undefined);
    const fc = emsalEntriesToFeatureCollection(entries);
    if (!fc) {
      return overlays;
    }
    overlays.push({
      id: 'portal-merged-emsal',
      data: fc,
      fillColor: '#2563eb',
      fillOpacity: 0.14,
      lineColor: '#1d4ed8',
      lineWidth: 2,
      showLabels: true,
      labelField: 'label_text',
      labelTextSize: 11,
      labelColor: '#ffffff',
      labelHaloColor: 'rgba(0,0,0,0.88)',
      labelHaloWidth: 2,
      labelMaxWidth: 14,
      labelAllowOverlap: true,
      labelIgnorePlacement: true,
    });
    return overlays;
  }, [expertMapQuery.data, ramsarMapQuery.data]);
  const [showReportViewer, setShowReportViewer] = useState(false);
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const [requestModal, setRequestModal] = useState(null);
  const [requestNote, setRequestNote] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestFiles, setRequestFiles] = useState([]);
  const [roadReportModalOpen, setRoadReportModalOpen] = useState(false);
  const [roadOverrideSelections, setRoadOverrideSelections] = useState([]);
  const [roadFocusedEdgeId, setRoadFocusedEdgeId] = useState(null);
  const [electricReportModalOpen, setElectricReportModalOpen] = useState(false);
  const [terrainMeshExpand, setTerrainMeshExpand] = useState(null);
  const [terrainRoadPanelOpen, setTerrainRoadPanelOpen] = useState(false);
  const [terrainImagesOpen, setTerrainImagesOpen] = useState(false);
  const [edgeMeasureVisible, setEdgeMeasureVisible] = useState(false);
  const [terrainStreetViewOpen, setTerrainStreetViewOpen] = useState(false);
  const terrainStreetsReadyRef = useRef(false);
  const [terrainMapsReady, setTerrainMapsReady] = useState(false);
  const [terrainRightMode, setTerrainRightMode] = useState('satellite');

  const markTerrainStreetsMapReady = useCallback(() => {
    if (terrainStreetsReadyRef.current) return;
    terrainStreetsReadyRef.current = true;
    setTerrainMapsReady(true);
  }, []);

  useEffect(() => {
    terrainStreetsReadyRef.current = false;
    setTerrainMapsReady(false);
    setTerrainRightMode('satellite');
    setPopulationMapVisible(false);
    setPopulationMapLoading(false);
    setPopulationChipValue('—');
    setPopulationChipLines(null);
    setPopulationGridOverlays([]);
    populationGridCacheRef.current = null;
    setWetlandMapVisible(false);
    setWetlandMapLoading(false);
    setWetlandOverlays([]);
    setHvMapVisible(false);
    setHvMapLoading(false);
    setHvOverlays([]);
    quarterGeoLayersCacheRef.current = null;
    setQuarterSalesMapVisible(false);
    setQuarterSalesMapLoading(false);
    setQuarterSalesOverlays([]);
    setQuarterSalesChipValue('—');
    quarterSalesCacheRef.current = null;
    setValuableRoadsMapVisible(false);
    setValuableRoadsMapLoading(false);
    setValuableRoadsOverlays([]);
    setValuableRoadsChipValue('—');
    setTerrainMeshExpand(null);
    setTerrainRoadPanelOpen(false);
    setTerrainImagesOpen(false);
    setEdgeMeasureVisible(false);
  }, [snapshotId]);

  const showTerrainBand = !listingOnly && !listingProLocked;

  /* Overlay çizimi için: map ready callback kaçarsa sonsuza kadar beklemesin */
  useEffect(() => {
    if (!showTerrainBand || terrainMapsReady) return undefined;
    const timer = window.setTimeout(() => setTerrainMapsReady(true), 4500);
    return () => window.clearTimeout(timer);
  }, [showTerrainBand, terrainMapsReady, snapshotId]);
  const [electricOverrideLines, setElectricOverrideLines] = useState([]);
  const [electricDraftStart, setElectricDraftStart] = useState(null);
  const [electricDraftCursor, setElectricDraftCursor] = useState(null);
  const [electricSelectedVoltageKv, setElectricSelectedVoltageKv] = useState(64);
  const [electricOverrideApplied, setElectricOverrideApplied] = useState(false);
  const [roadRerunSubmitting, setRoadRerunSubmitting] = useState(false);
  const [proQueryLaunchSubmitting, setProQueryLaunchSubmitting] = useState(false);
  const [ownerProfileUserId, setOwnerProfileUserId] = useState(null);
  const [creditCosts, setCreditCosts] = useState({});
  const [creditCostsLoadFailed, setCreditCostsLoadFailed] = useState(false);
  const [proSorguPurchaseModal, setProSorguPurchaseModal] = useState(false);
  const backLinkHref = useMemo(() => getBackLinkHref(), []);
  const currentDetailHref = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname}${window.location.search}`;
  }, []);
  const reportViewerUrl = useMemo(
    () => {
      const url = `/reports/analysis-report-v5.html?from_portal=1&snapshot_id=${snapshotId}`;
      return reportRefreshKey ? `${url}&refresh=${reportRefreshKey}` : url;
    },
    [snapshotId, reportRefreshKey],
  );
  /** Mahalle linkinden önceki sekmeler (Uzman + Puan ayrı sırada, en sonda) */
  const tabDefs = useMemo(() => {
    if (listingOnly) {
      return [{ id: 'listing_description', label: 'Açıklama' }];
    }
    return getTabDefs(summary);
  }, [listingOnly, summary]);
  const tailTabDefs = useMemo(() => (listingOnly ? [] : TAIL_TAB_DEFS), [listingOnly]);
  const canOpenMahalleBilgileri = Boolean(
    summary?.proparcel_value
    || (
      summary?.city_id != null
      && summary?.town_id != null
      && summary?.quarter_id != null
    ),
  );
  const allTabDefs = useMemo(() => {
    const defs = [...tabDefs];
    if (canOpenMahalleBilgileri) {
      defs.push(MAHALLE_TAB_DEF);
      defs.push(SOSYAL_ALANLAR_TAB_DEF);
    }
    defs.push(...tailTabDefs);
    return defs;
  }, [tabDefs, tailTabDefs, canOpenMahalleBilgileri]);
  const insightSupplement = usePortalInsightSupplemental(snapshotId, summary, listingProLocked, {
    isEnriching,
  });

  const terrainGeoQuery = useQuery({
    queryKey: [
      'portal-terrain-band-geo',
      summary?.quarter_id,
      summary?.proparcel_value,
      summary?.city_name,
    ],
    queryFn: () => fetchTerrainBandGeoLayers({
      quarter_id: summary?.quarter_id,
      proparcel_value: summary?.proparcel_value,
      city_name: summary?.city_name,
    }),
    enabled: Boolean(
      showTerrainBand
      && String(summary?.city_name || '').trim()
      && (summary?.quarter_id != null || summary?.proparcel_value != null),
    ),
    staleTime: 300_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const terrainCenterQuery = useQuery({
    queryKey: [
      'portal-terrain-quarter-center',
      summary?.quarter_id,
      summary?.proparcel_value,
      summary?.city_id,
    ],
    queryFn: () => fetchQuarterCenterLonLat(summary),
    enabled: Boolean(showTerrainBand && summary),
    staleTime: 300_000,
    placeholderData: keepPreviousData,
    retry: false,
  });

  const quarterCenterCacheRef = useRef(null);
  const [quarterCenterLonLat, setQuarterCenterLonLat] = useState(null);
  const [quarterCenterMapVisible, setQuarterCenterMapVisible] = useState(true);
  const [quarterCenterMapLoading, setQuarterCenterMapLoading] = useState(false);
  const [quarterCenterMapError, setQuarterCenterMapError] = useState('');
  const [imarMapVisible, setImarMapVisible] = useState(true);
  const [populationMapVisible, setPopulationMapVisible] = useState(false);
  const [populationMapLoading, setPopulationMapLoading] = useState(false);
  const [populationChipValue, setPopulationChipValue] = useState('—');
  const [populationChipLines, setPopulationChipLines] = useState(null);
  const [populationGridOverlays, setPopulationGridOverlays] = useState([]);
  const populationGridCacheRef = useRef(null);
  const [wetlandMapVisible, setWetlandMapVisible] = useState(false);
  const [wetlandMapLoading, setWetlandMapLoading] = useState(false);
  const [wetlandOverlays, setWetlandOverlays] = useState([]);
  const [hvMapVisible, setHvMapVisible] = useState(false);
  const [hvMapLoading, setHvMapLoading] = useState(false);
  const [hvOverlays, setHvOverlays] = useState([]);
  const quarterGeoLayersCacheRef = useRef(null);
  const [quarterSalesMapVisible, setQuarterSalesMapVisible] = useState(false);
  const [quarterSalesMapLoading, setQuarterSalesMapLoading] = useState(false);
  const [quarterSalesOverlays, setQuarterSalesOverlays] = useState([]);
  const [quarterSalesChipValue, setQuarterSalesChipValue] = useState('—');
  const quarterSalesCacheRef = useRef(null);
  const [valuableRoadsMapVisible, setValuableRoadsMapVisible] = useState(false);
  const [valuableRoadsMapLoading, setValuableRoadsMapLoading] = useState(false);
  const [valuableRoadsOverlays, setValuableRoadsOverlays] = useState([]);
  const [valuableRoadsChipValue, setValuableRoadsChipValue] = useState('—');

  /* Progressive paint: mahalle/imar geo gelince boyanır; merkez ayrı gelir */
  const terrainCenterLonLatResolved = (
    (Array.isArray(quarterCenterLonLat) && quarterCenterLonLat.length >= 2)
      ? quarterCenterLonLat
      : terrainCenterQuery.data?.lonLat
  );
  const hasTerrainCenterLonLat = Boolean(
    Array.isArray(terrainCenterLonLatResolved)
    && terrainCenterLonLatResolved.length >= 2
    && Number.isFinite(Number(terrainCenterLonLatResolved[0]))
    && Number.isFinite(Number(terrainCenterLonLatResolved[1])),
  );
  const terrainDrawingsLoading = Boolean(
    showTerrainBand && (
      terrainGeoQuery.isFetching
      || (quarterCenterMapVisible && terrainCenterQuery.isFetching && !hasTerrainCenterLonLat)
    ),
  );
  const terrainMapStatusLabel = terrainDrawingsLoading
    ? (!terrainMapsReady ? 'Harita hazırlanıyor…' : 'Çizimler yükleniyor…')
    : null;

  const terrainBandDistanceLinks = useMemo(() => {
    const links = [];
    const imarFc = terrainGeoQuery.data?.imarBoundary;
    const parcelCenter = parcelCentroidFromFeatureCollection(parcelFeatureCollection);
    const from = parcelCenter
      ? [Number(parcelCenter.lng), Number(parcelCenter.lat)]
      : null;
    const hasFrom = from && Number.isFinite(from[0]) && Number.isFinite(from[1]);
    const centerLonLat = (
      (Array.isArray(quarterCenterLonLat) && quarterCenterLonLat.length >= 2)
        ? quarterCenterLonLat
        : terrainCenterQuery.data?.lonLat
    );

    if (
      quarterCenterMapVisible
      && hasFrom
      && Array.isArray(centerLonLat)
      && centerLonLat.length >= 2
    ) {
      const to = [Number(centerLonLat[0]), Number(centerLonLat[1])];
      if (Number.isFinite(to[0]) && Number.isFinite(to[1])) {
        const distanceText = formatTerrainDistanceLabel(
          resolveQuarterCenterDistanceM(summary, insightSupplement.analysis),
        );
        const labelAt = labelLonLatAwayFromParcel(from, to, parcelFeatureCollection);
        links.push({
          id: 'quarter-center',
          kind: 'quarter-center',
          from,
          to,
          labelAt,
          distanceText,
          label_text: distanceText,
          variant: 'quarter',
        });
      }
    }

    if (imarMapVisible && hasFrom && imarFc?.features?.length) {
      const imarTarget = nearestLonLatOnFeatureCollection(imarFc, from);
      if (imarTarget) {
        const distanceText = formatTerrainDistanceLabel(
          resolveImarBoundaryDistanceM(summary, insightSupplement.analysis),
        );
        const labelAt = labelLonLatAwayFromParcel(from, imarTarget, parcelFeatureCollection);
        links.push({
          id: 'imar-boundary',
          kind: 'imar-boundary',
          from,
          to: imarTarget,
          labelAt,
          distanceText,
          label_text: distanceText,
          variant: 'imar',
        });
      }
    }

    return links;
  }, [
    terrainGeoQuery.data,
    terrainCenterQuery.data,
    parcelFeatureCollection,
    quarterCenterLonLat,
    quarterCenterMapVisible,
    imarMapVisible,
    summary,
    insightSupplement.analysis,
  ]);

  const terrainBandOverlays = useMemo(() => {
    const overlays = [];
    const quarterFc = terrainGeoQuery.data?.quarterPolygon;
    const imarFc = terrainGeoQuery.data?.imarBoundary;
    const centerLonLat = (
      (Array.isArray(quarterCenterLonLat) && quarterCenterLonLat.length >= 2)
        ? quarterCenterLonLat
        : terrainCenterQuery.data?.lonLat
    );

    // Mahalle sınırı — her zaman
    if (quarterFc?.features?.length) {
      overlays.push({
        id: 'terrain-band-quarter-boundary',
        data: quarterFc,
        fillColor: '#22c55e',
        fillOpacity: 0.07,
        lineColor: '#15803d',
        lineWidth: 2.5,
        showLabels: false,
        excludeFromFit: true,
      });
    }

    // İmar sınırı
    if (imarMapVisible && imarFc?.features?.length) {
      overlays.push({
        id: 'terrain-band-imar-boundary',
        data: imarFc,
        fillColor: '#6366f1',
        fillOpacity: 0.12,
        lineColor: '#4338ca',
        lineWidth: 2.5,
        showLabels: false,
        excludeFromFit: true,
      });
    }

    // Parsel → mahalle merkezi + imar kesik çizgileri
    if (terrainBandDistanceLinks.length) {
      overlays.push({
        id: 'terrain-band-dashed-links',
        data: {
          type: 'FeatureCollection',
          features: terrainBandDistanceLinks.map((link) => ({
            type: 'Feature',
            properties: { kind: link.kind },
            geometry: {
              type: 'LineString',
              coordinates: [link.from, link.to],
            },
          })),
        },
        lineColor: '#0f172a',
        lineWidth: 2.25,
        lineOpacity: 0.85,
        lineDasharray: [2.2, 1.6],
        showLabels: false,
        excludeFromFit: true,
      });

      // Mesafe etiketleri — Mapbox symbol (HTML marker 3D'de çizgiden kayıyordu)
      const labelFeatures = terrainBandDistanceLinks
        .map((link) => {
          const coords = link.labelAt;
          const text = String(link.label_text || '').trim();
          if (!Array.isArray(coords) || coords.length < 2 || !text) return null;
          const lng = Number(coords[0]);
          const lat = Number(coords[1]);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
          return {
            type: 'Feature',
            properties: { kind: link.kind, label_text: text },
            geometry: { type: 'Point', coordinates: [lng, lat] },
          };
        })
        .filter(Boolean);

      if (labelFeatures.length) {
        overlays.push({
          id: 'terrain-band-distance-labels',
          data: { type: 'FeatureCollection', features: labelFeatures },
          showLabels: true,
          labelField: 'label_text',
          labelTextSize: 11,
          labelLineHeight: 1.1,
          labelColor: '#f8fafc',
          labelHaloColor: 'rgba(15,23,42,0.92)',
          labelHaloWidth: 2,
          labelAllowOverlap: true,
          labelIgnorePlacement: true,
          labelPlacement: 'point',
          labelAnchor: 'center',
          labelMaxWidth: 8,
          labelRotationAlignment: 'viewport',
          labelPitchAlignment: 'viewport',
          excludeFromFit: true,
        });
      }
    }

    // Mahalle merkezi noktası — yazı yok (iğne HTML marker)
    if (
      quarterCenterMapVisible
      && Array.isArray(centerLonLat)
      && centerLonLat.length >= 2
    ) {
      overlays.push({
        id: 'terrain-band-quarter-center-point',
        data: {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [Number(centerLonLat[0]), Number(centerLonLat[1])],
            },
            properties: {},
          }],
        },
        circleRadius: 9,
        circleColor: '#f97316',
        circleStrokeColor: '#ffffff',
        circleStrokeWidth: 2,
        showLabels: false,
        excludeFromFit: true,
      });
    }

    if (populationMapVisible && populationGridOverlays.length) {
      overlays.push(...populationGridOverlays);
    }

    if (wetlandMapVisible && wetlandOverlays.length) {
      overlays.push(...wetlandOverlays);
    }

    if (hvMapVisible && hvOverlays.length) {
      overlays.push(...hvOverlays);
    }

    if (quarterSalesMapVisible && quarterSalesOverlays.length) {
      overlays.push(...quarterSalesOverlays);
    }

    if (valuableRoadsMapVisible && valuableRoadsOverlays.length) {
      overlays.push(...valuableRoadsOverlays);
    }

    return overlays;
  }, [
    terrainGeoQuery.data,
    terrainCenterQuery.data,
    quarterCenterLonLat,
    quarterCenterMapVisible,
    imarMapVisible,
    populationMapVisible,
    populationGridOverlays,
    wetlandMapVisible,
    wetlandOverlays,
    hvMapVisible,
    hvOverlays,
    quarterSalesMapVisible,
    quarterSalesOverlays,
    valuableRoadsMapVisible,
    valuableRoadsOverlays,
    terrainBandDistanceLinks,
  ]);

  const terrainBandHtmlMarkers = useMemo(() => {
    if (!quarterCenterMapVisible) return null;
    const lonLat = (
      (Array.isArray(quarterCenterLonLat) && quarterCenterLonLat.length >= 2)
        ? quarterCenterLonLat
        : terrainCenterQuery.data?.lonLat
    );
    if (!Array.isArray(lonLat) || lonLat.length < 2) return null;
    const lng = Number(lonLat[0]);
    const lat = Number(lonLat[1]);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
    const label = String(terrainCenterQuery.data?.label || 'Mahalle merkezi').trim() || 'Mahalle merkezi';
    const labelEsc = escapeTerrainMarkerHtml(label);
    return [{
      id: 'terrain-quarter-center-pin',
      lng,
      lat,
      className: 'pp-terrain-map-pin',
      html: `
        <div class="pp-terrain-map-pin__stack" title="${labelEsc}">
          <span class="pp-terrain-map-pin__head" aria-hidden="true"></span>
          <span class="pp-terrain-map-pin__needle" aria-hidden="true"></span>
          <span class="pp-terrain-map-pin__label">${labelEsc}</span>
        </div>
      `,
      anchor: 'bottom',
    }];
  }, [terrainCenterQuery.data, quarterCenterLonLat, quarterCenterMapVisible]);

  const handleQuarterCenterChipClick = useCallback(async () => {
    if (listingOnly || listingProLocked) return;
    setQuarterCenterMapError('');
    const cached = quarterCenterCacheRef.current?.lonLat
      || quarterCenterLonLat
      || terrainCenterQuery.data?.lonLat;
    if (Array.isArray(cached) && cached.length >= 2) {
      if (!quarterCenterCacheRef.current?.lonLat) {
        quarterCenterCacheRef.current = terrainCenterQuery.data || { lonLat: cached, label: 'Mahalle merkezi' };
        setQuarterCenterLonLat(cached);
      }
      setQuarterCenterMapVisible((v) => {
        const next = !v;
        if (next) setHeroCardMedia('map');
        return next;
      });
      return;
    }
    setQuarterCenterMapLoading(true);
    try {
      const result = await fetchQuarterCenterLonLat(summary);
      if (!result?.lonLat) {
        setQuarterCenterMapError('Mahalle merkezi koordinatı bulunamadı.');
        return;
      }
      quarterCenterCacheRef.current = result;
      setQuarterCenterLonLat(result.lonLat);
      setQuarterCenterMapVisible(true);
      setHeroCardMedia('map');
    } catch (err) {
      setQuarterCenterMapError(err?.message || 'Mahalle merkezi alınamadı.');
    } finally {
      setQuarterCenterMapLoading(false);
    }
  }, [
    listingOnly,
    listingProLocked,
    summary,
    setHeroCardMedia,
    quarterCenterLonLat,
    terrainCenterQuery.data,
  ]);

  const handleImarChipClick = useCallback(() => {
    if (listingOnly || listingProLocked) return;
    setImarMapVisible((v) => {
      const next = !v;
      if (next) setHeroCardMedia('map');
      return next;
    });
  }, [listingOnly, listingProLocked, setHeroCardMedia]);

  const handlePopulationChipClick = useCallback(async () => {
    if (listingOnly || listingProLocked) return;
    if (populationMapVisible) {
      setPopulationMapVisible(false);
      return;
    }
    if (populationGridCacheRef.current?.overlays?.length) {
      setPopulationGridOverlays(populationGridCacheRef.current.overlays);
      setPopulationMapVisible(true);
      setHeroCardMedia('map');
      return;
    }

    const pv = summary?.proparcel_value;
    if (pv == null || String(pv).trim() === '') {
      return;
    }

    setPopulationMapLoading(true);
    try {
      const gridResult = await fetchQuarterPopulationGrid({
        proparcel_value: pv,
        quarter_id: summary?.quarter_id,
      });
      const overlays = buildPopulationGridOverlays(gridResult.cells);
      if (!overlays.length) {
        setPopulationGridOverlays([]);
        return;
      }
      populationGridCacheRef.current = { overlays };
      setPopulationGridOverlays(overlays);
      setPopulationMapVisible(true);
      setHeroCardMedia('map');
    } catch {
      setPopulationGridOverlays([]);
    } finally {
      setPopulationMapLoading(false);
    }
  }, [
    listingOnly,
    listingProLocked,
    populationMapVisible,
    summary?.proparcel_value,
    summary?.quarter_id,
    setHeroCardMedia,
  ]);

  const canFetchQuarterGeo = Boolean(
    showTerrainBand
    && String(summary?.city_name || '').trim()
    && (summary?.quarter_id != null || (summary?.proparcel_value != null && String(summary.proparcel_value).trim() !== '')),
  );

  const fetchQuarterGeoSet = useCallback(async (layerSet) => {
    const queryKey = portalQuarterGeoQueryKey(layerSet, summary);
    if (layerSet === 'wetland') {
      return queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchWetlandQuarterGeoLayers(summary),
        staleTime: 300_000,
      });
    }
    if (layerSet === 'electric') {
      return queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchElectricQuarterGeoLayers(summary),
        staleTime: 300_000,
      });
    }
    return queryClient.fetchQuery({
      queryKey,
      queryFn: () => fetchValuableRoadQuarterGeoLayers(summary),
      staleTime: 300_000,
    });
  }, [queryClient, summary]);

  useEffect(() => {
    if (!canFetchQuarterGeo || !deferNonCriticalReady || listingOnly || listingProLocked) {
      return undefined;
    }
    void queryClient.prefetchQuery({
      queryKey: portalQuarterGeoQueryKey('wetland', summary),
      queryFn: () => fetchWetlandQuarterGeoLayers(summary),
      staleTime: 300_000,
    });
    void queryClient.prefetchQuery({
      queryKey: portalQuarterGeoQueryKey('roads', summary),
      queryFn: () => fetchValuableRoadQuarterGeoLayers(summary),
      staleTime: 300_000,
    });
    void queryClient.prefetchQuery({
      queryKey: portalQuarterGeoQueryKey('electric', summary),
      queryFn: () => fetchElectricQuarterGeoLayers(summary),
      staleTime: 300_000,
    });
    return undefined;
  }, [
    canFetchQuarterGeo,
    deferNonCriticalReady,
    listingOnly,
    listingProLocked,
    queryClient,
    summary?.quarter_id,
    summary?.proparcel_value,
    summary?.city_name,
  ]);

  const handleRamsarChipClick = useCallback(async () => {
    if (listingOnly || listingProLocked) return;
    if (wetlandMapVisible) {
      setWetlandMapVisible(false);
      return;
    }

    setWetlandMapLoading(true);
    try {
      // Tam Ramsar: portal ramsar section (şehir dissolve); göl/nehir: quarter wetland
      let ramsarSection = queryClient.getQueryData(['portal-detail-section', snapshotId, 'ramsar']);
      if (!ramsarSection && snapshotId) {
        ramsarSection = await queryClient.fetchQuery({
          queryKey: ['portal-detail-section', snapshotId, 'ramsar'],
          queryFn: () => getPortalDetailSection(snapshotId, 'ramsar'),
          staleTime: 120_000,
        });
      }
      const ramsarOverlay = buildRamsarHeroOverlay(ramsarSection);

      let wetlandResult = queryClient.getQueryData(portalQuarterGeoQueryKey('wetland', summary));
      if (!wetlandResult?.wetlandOverlays && canFetchQuarterGeo) {
        wetlandResult = await fetchQuarterGeoSet('wetland');
      }
      const waterOverlays = (wetlandResult?.wetlandOverlays || []).filter(
        (o) => o && o.id !== 'portal-quarter-sulak',
      );
      const overlays = [
        ...(ramsarOverlay ? [ramsarOverlay] : []),
        ...waterOverlays,
      ];
      // Section boşsa şehir dissolve sulak katmanını kullan
      if (!ramsarOverlay) {
        const sulak = (wetlandResult?.wetlandOverlays || []).find(
          (o) => o && o.id === 'portal-quarter-sulak',
        );
        if (sulak) overlays.unshift(sulak);
      }
      setWetlandOverlays(overlays);
      if (overlays.length) {
        setWetlandMapVisible(true);
        setHeroCardMedia('map');
      }
    } catch {
      setWetlandOverlays([]);
    } finally {
      setWetlandMapLoading(false);
    }
  }, [
    listingOnly,
    listingProLocked,
    wetlandMapVisible,
    canFetchQuarterGeo,
    fetchQuarterGeoSet,
    queryClient,
    summary,
    snapshotId,
    setHeroCardMedia,
  ]);

  const handleHvChipClick = useCallback(async () => {
    if (listingOnly || listingProLocked) return;
    if (hvMapVisible) {
      setHvMapVisible(false);
      return;
    }
    const cached = queryClient.getQueryData(portalQuarterGeoQueryKey('electric', summary));
    if (cached?.electricOverlays?.length) {
      setHvOverlays(cached.electricOverlays);
      setHvMapVisible(true);
      setHeroCardMedia('map');
      return;
    }
    if (!canFetchQuarterGeo) return;

    setHvMapLoading(true);
    try {
      const result = await fetchQuarterGeoSet('electric');
      const overlays = result?.electricOverlays || [];
      setHvOverlays(overlays);
      if (overlays.length) {
        setHvMapVisible(true);
        setHeroCardMedia('map');
      }
    } catch {
      setHvOverlays([]);
    } finally {
      setHvMapLoading(false);
    }
  }, [
    listingOnly,
    listingProLocked,
    hvMapVisible,
    canFetchQuarterGeo,
    fetchQuarterGeoSet,
    queryClient,
    summary,
    setHeroCardMedia,
  ]);

  const handleValuableRoadsChipClick = useCallback(async () => {
    if (listingOnly || listingProLocked) return;
    if (valuableRoadsMapVisible) {
      setValuableRoadsMapVisible(false);
      return;
    }

    const applyProgressive = (result) => {
      const line = result?.valuableRoadLineOverlay || null;
      const buffers = Array.isArray(result?.valuableRoadBufferOverlays)
        ? result.valuableRoadBufferOverlays
        : [];
      const roadCount = line?.data?.features?.length || 0;
      setValuableRoadsChipValue(roadCount > 0 ? `${roadCount} yol` : 'Yok');
      if (!line) {
        setValuableRoadsOverlays([]);
        return;
      }
      setValuableRoadsOverlays([line]);
      setValuableRoadsMapVisible(true);
      setHeroCardMedia('map');
      if (buffers.length) {
        scheduleIdle(() => {
          setValuableRoadsOverlays([...buffers, line]);
        });
      }
    };

    const cached = queryClient.getQueryData(portalQuarterGeoQueryKey('roads', summary));
    if (cached?.valuableRoadLineOverlay || cached?.valuableRoadOverlays?.length) {
      applyProgressive(cached);
      return;
    }
    if (!canFetchQuarterGeo) return;

    setValuableRoadsMapLoading(true);
    try {
      const result = await fetchQuarterGeoSet('roads');
      applyProgressive(result);
    } catch {
      setValuableRoadsOverlays([]);
    } finally {
      setValuableRoadsMapLoading(false);
    }
  }, [
    listingOnly,
    listingProLocked,
    valuableRoadsMapVisible,
    canFetchQuarterGeo,
    fetchQuarterGeoSet,
    queryClient,
    summary,
    setHeroCardMedia,
  ]);

  const handleQuarterSalesChipClick = useCallback(async () => {
    if (listingOnly || listingProLocked) return;
    if (quarterSalesMapVisible) {
      setQuarterSalesMapVisible(false);
      return;
    }
    if (quarterSalesCacheRef.current?.overlays?.length) {
      setQuarterSalesOverlays(quarterSalesCacheRef.current.overlays);
      if (quarterSalesCacheRef.current.chipValue) {
        setQuarterSalesChipValue(quarterSalesCacheRef.current.chipValue);
      }
      setQuarterSalesMapVisible(true);
      setHeroCardMedia('map');
      return;
    }

    const cityName = String(summary?.city_name || '').trim();
    if (!cityName) return;
    if (summary?.quarter_id == null && (summary?.proparcel_value == null || String(summary.proparcel_value).trim() === '')) {
      return;
    }

    setQuarterSalesMapLoading(true);
    try {
      const result = await fetchQuarterSalesGrid({
        quarter_id: summary?.quarter_id,
        proparcel_value: summary?.proparcel_value,
        city_name: cityName,
      });
      const overlays = buildQuarterSalesChipOverlays(result?.heat);
      const chipValue = formatQuarterSalesChipValue(result?.salesCountByYear);
      setQuarterSalesChipValue(chipValue);
      if (!overlays.length) {
        setQuarterSalesOverlays([]);
        return;
      }
      quarterSalesCacheRef.current = { overlays, chipValue };
      setQuarterSalesOverlays(overlays);
      setQuarterSalesMapVisible(true);
      setHeroCardMedia('map');
    } catch {
      setQuarterSalesOverlays([]);
    } finally {
      setQuarterSalesMapLoading(false);
    }
  }, [
    listingOnly,
    listingProLocked,
    quarterSalesMapVisible,
    summary?.city_name,
    summary?.quarter_id,
    summary?.proparcel_value,
    setHeroCardMedia,
  ]);

  /* Nüfus özeti sayfa açılışında (hafif DB); grid yalnızca tıklamada */
  useEffect(() => {
    if (!showTerrainBand || !snapshotId) return undefined;
    let cancelled = false;
    getPortalPopulationChip(snapshotId)
      .then((chip) => {
        if (cancelled || !chip) return;
        const population = chip?.nufus_ozeti?.son_nufus;
        const year = chip?.nufus_ozeti?.son_yil;
        const fiveYearGrowthPct = chip?.nufus_ozeti?.son_5_yil_toplam_artis_pct;
        const sqmPerPerson = chip?.yapi_alani?.kisi_basi_imarli_m2;
        const lines = formatPopulationChipLines({
          population, sqmPerPerson, year, fiveYearGrowthPct,
        });
        const chipValue = formatPopulationChipValue({
          population, sqmPerPerson, year, fiveYearGrowthPct,
        });
        setPopulationChipLines(lines);
        setPopulationChipValue(chipValue);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showTerrainBand, snapshotId]);

  useEffect(() => {
    setQuarterCenterVisible(quarterCenterMapVisible);
  }, [quarterCenterMapVisible, setQuarterCenterVisible]);

  useEffect(() => {
    if (!quarterCenterMapVisible) return;
    const lonLat = (
      (Array.isArray(quarterCenterLonLat) && quarterCenterLonLat.length >= 2)
        ? quarterCenterLonLat
        : terrainCenterQuery.data?.lonLat
    );
    if (!Array.isArray(lonLat) || lonLat.length < 2) return;
    if (!quarterCenterLonLat) {
      setQuarterCenterLonLat(lonLat);
      if (!quarterCenterCacheRef.current?.lonLat) {
        quarterCenterCacheRef.current = terrainCenterQuery.data || { lonLat, label: 'Mahalle merkezi' };
      }
    }
    const parcelCenter = parcelCentroidFromFeatureCollection(parcelFeatureCollection);
    if (!parcelCenter) return;
    const distanceM = resolveQuarterCenterDistanceM(
      summary,
      insightSupplement.analysis,
    );
    ensureQuarterCenterLayers(
      buildQuarterCenterMapOverlays(parcelCenter, lonLat, distanceM),
    );
  }, [
    quarterCenterMapVisible,
    quarterCenterLonLat,
    terrainCenterQuery.data,
    parcelFeatureCollection,
    summary,
    insightSupplement.analysis,
    ensureQuarterCenterLayers,
  ]);

  const structureQuery = useMemo(
    () => isStructurePortalQueryType(summary?.query_type),
    [summary?.query_type],
  );
  const structureOverviewGrid = useMemo(
    () => structureQuery && shouldRenderStructureInfoCard(summary),
    [structureQuery, summary],
  );

  /** Rüzgar / güneş skoru: yalnız ilgili sekme seçiliyken (veya Rapor açılırken) istek; önbellek rapor PDF ile aynı sunucu anahtarlarını ısıtır. */
  useEffect(() => {
    if (!snapshotId || listingOnly || listingProLocked) return;
    if (activeTab === 'wind_energy') {
      void queryClient.prefetchQuery({
        queryKey: ['portal-wind-energy-score', 'v14', snapshotId],
        queryFn: () => getPortalWindEnergyScore(snapshotId),
        staleTime: 60_000,
      });
    } else if (activeTab === 'solar_energy') {
      void queryClient.prefetchQuery({
        queryKey: ['portal-solar-energy-score', 'v5', snapshotId],
        queryFn: () => getPortalSolarEnergyScore(snapshotId),
        staleTime: 60_000,
      });
    }
  }, [activeTab, snapshotId, listingOnly, listingProLocked, queryClient]);
  const canUseQueryFavorite = !listingOnly && !summary?.listing_id && Number(snapshotId) > 0;
  const isAuthenticated = Boolean(
    typeof window !== 'undefined' && window.PP_USER_AUTHENTICATED,
  );
  const socialMediaTemplateHref = useMemo(() => {
    if (!isAuthenticated) return '';
    const params = new URLSearchParams();
    if (summary?.listing_id) {
      params.set('listing_id', String(summary.listing_id));
      params.set('source', 'detail');
    } else if (Number(snapshotId) > 0) {
      params.set('snapshot_id', String(snapshotId));
      params.set('source', 'detail');
    } else {
      params.set('source', 'menu');
    }
    return `/portal/ilan/sosyal-medya-sablonu/?${params.toString()}`;
  }, [isAuthenticated, summary?.listing_id, snapshotId]);
  const tabPanelRef = useRef(null);
  const ratingsSectionRef = useRef(null);
  const pageBodyRef = useRef(null);
  const topbarRef = useRef(null);
  const skipTabScrollOnMountRef = useRef(true);
  const listingProLockedPrevRef = useRef(undefined);
  const tabChangeAtRef = useRef(0);
  /** URL / kilit mantığı ile sekme değişse bile sayfa ilk açılışta kaymasın; yalnızca kullanıcı tıklayınca true */
  const allowTabPanelAutoScrollRef = useRef(false);
  const tabPanelLastHeightRef = useRef(0);
  const tabSwitchCompensateUntilRef = useRef(0);
  const tabSwitchScrollYRef = useRef(0);
  const roadOverrideSessionKey = useMemo(
    () => `portal-road-override-${String(snapshotId || '')}`,
    [snapshotId],
  );
  const electricOverrideSessionKey = useMemo(
    () => `portal-electric-override-${String(snapshotId || '')}`,
    [snapshotId],
  );

  const parcelRing = useMemo(() => extractParcelRingFromSummary(summary), [summary]);

  useEffect(() => {
    const onDocumentPointerDown = (event) => {
      const root = topbarRef.current;
      if (!root) return;
      const openDropdowns = root.querySelectorAll('details.pp-detail-topbar-dropdown[open]');
      if (!openDropdowns.length) return;
      const target = event.target;
      if (target instanceof Node) {
        for (const detailsEl of openDropdowns) {
          if (detailsEl.contains(target)) return;
        }
      }
      openDropdowns.forEach((detailsEl) => {
        detailsEl.open = false;
      });
    };
    document.addEventListener('mousedown', onDocumentPointerDown);
    return () => document.removeEventListener('mousedown', onDocumentPointerDown);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(roadOverrideSessionKey);
      if (!raw) {
        setRoadOverrideSelections([]);
        return;
      }
      const parsed = JSON.parse(raw);
      const normalized = _normalizeRoadOverrideSelections(parsed);
      setRoadOverrideSelections(normalized);
    } catch {
      setRoadOverrideSelections([]);
    }
  }, [roadOverrideSessionKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(roadOverrideSessionKey, JSON.stringify(roadOverrideSelections));
    } catch {}
  }, [roadOverrideSelections, roadOverrideSessionKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.sessionStorage.getItem(electricOverrideSessionKey);
      if (!raw) {
        setElectricOverrideLines([]);
        return;
      }
      const parsed = JSON.parse(raw);
      setElectricOverrideLines(_normalizeElectricOverrideLines(parsed));
    } catch {
      setElectricOverrideLines([]);
    }
  }, [electricOverrideSessionKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const persist = (electricOverrideLines || []).map((line) => ({
        start: line.start,
        end: line.end,
        voltage_kv: line.voltage_kv,
      }));
      window.sessionStorage.setItem(electricOverrideSessionKey, JSON.stringify(persist));
    } catch {}
  }, [electricOverrideLines, electricOverrideSessionKey]);

  const captureTabPanelHeightBeforeSwitch = useCallback(() => {
    const el = tabPanelRef.current;
    if (el?.offsetHeight > 0) {
      tabPanelLastHeightRef.current = el.offsetHeight;
    }
    tabSwitchScrollYRef.current = typeof window !== 'undefined' ? window.scrollY : 0;
    tabSwitchCompensateUntilRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 4000;
  }, []);

  const compensateTabPanelHeightChange = useCallback(() => {
    if (allowTabPanelAutoScrollRef.current) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now > tabSwitchCompensateUntilRef.current) return;
    if (typeof window !== 'undefined' && Math.abs(window.scrollY - tabSwitchScrollYRef.current) > 48) {
      return;
    }
    const el = tabPanelRef.current;
    if (!el) return;
    const panelRect = el.getBoundingClientRect();
    if (panelRect.bottom < 0 || panelRect.top > window.innerHeight) return;
    // DFA gibi uzun sekmelerde alt kısım görünürken telafi scroll sıçraması yapar.
    if (panelRect.top < -24) return;
    const prevH = tabPanelLastHeightRef.current;
    const nextH = el.offsetHeight;
    if (prevH > 0 && nextH > 0 && prevH !== nextH) {
      window.scrollBy(0, -(nextH - prevH));
    }
    if (nextH > 0) {
      tabPanelLastHeightRef.current = nextH;
    }
  }, []);

  const scrollTabPanelIntoView = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (!allowTabPanelAutoScrollRef.current) return;
    window.requestAnimationFrame(() => {
      if (!allowTabPanelAutoScrollRef.current) return;
      tabPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const scrollRatingsSectionIntoView = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.requestAnimationFrame(() => {
      ratingsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const handleRoadEdgeClick = useCallback((edgeId) => {
    const eid = Number(edgeId);
    if (!Number.isInteger(eid) || eid < 0) return;
    setRoadOverrideSelections((prev) => {
      const current = _normalizeRoadOverrideSelections(prev);
      const existing = current.find((item) => item.edge_id === eid);
      if (existing) return current;
      return _normalizeRoadOverrideSelections([...current, { edge_id: eid, road_type_id: null }]);
    });
    setRoadFocusedEdgeId(eid);
  }, []);

  const handleRoadTypeChange = useCallback((edgeId, roadTypeId) => {
    const eid = Number(edgeId);
    const rid = Number(roadTypeId);
    if (!Number.isInteger(eid) || ![9, 13].includes(rid)) return;
    setRoadOverrideSelections((prev) => _normalizeRoadOverrideSelections(
      prev.map((item) => (item.edge_id === eid ? { ...item, road_type_id: rid } : item)),
    ));
    setRoadFocusedEdgeId(eid);
  }, []);

  const handleRoadSelectionRemove = useCallback((edgeId) => {
    const eid = Number(edgeId);
    if (!Number.isInteger(eid)) return;
    setRoadOverrideSelections((prev) => _normalizeRoadOverrideSelections(
      prev.filter((item) => item.edge_id !== eid),
    ));
    setRoadFocusedEdgeId((prev) => (prev === eid ? null : prev));
  }, []);

  const completedRoadSelections = useMemo(
    () => _normalizeRoadOverrideSelections(roadOverrideSelections).filter((item) => item.road_type_id != null),
    [roadOverrideSelections],
  );

  const canSubmitRoadRerun = useMemo(
    () => completedRoadSelections.length > 0 || electricOverrideLines.length > 0,
    [completedRoadSelections.length, electricOverrideLines.length],
  );

  const handleElectricMapClick = useCallback((coord) => {
    if (!Array.isArray(coord) || coord.length < 2) return;
    const lon = Number(coord[0]);
    const lat = Number(coord[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    if (!electricDraftStart) {
      setElectricDraftStart([lon, lat]);
      setElectricDraftCursor([lon, lat]);
      return;
    }
    setElectricOverrideLines((prev) => _normalizeElectricOverrideLines([
      ...(prev || []),
      {
        start: electricDraftStart,
        end: [lon, lat],
        voltage_kv: electricSelectedVoltageKv,
      },
    ]));
    setElectricDraftStart(null);
    setElectricDraftCursor(null);
  }, [electricDraftStart, electricSelectedVoltageKv]);

  const handleElectricMapMove = useCallback((coord) => {
    if (!electricDraftStart) return;
    if (!Array.isArray(coord) || coord.length < 2) return;
    const lon = Number(coord[0]);
    const lat = Number(coord[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    setElectricDraftCursor([lon, lat]);
  }, [electricDraftStart]);

  const handleElectricRemoveLine = useCallback((lineId) => {
    setElectricOverrideLines((prev) => _normalizeElectricOverrideLines(
      (prev || []).filter((line) => line.id !== lineId),
    ));
  }, []);

  const handleRoadRerunSubmit = useCallback(async (source = 'road') => {
    if (!canSubmitRoadRerun || roadRerunSubmitting) return;
    setRoadRerunSubmitting(true);
    try {
      const sourceKey = String(source || 'road').toLowerCase();
      const electricLinesPayload = (electricOverrideLines || []).map((line) => ({
        start: line.start,
        end: line.end,
        voltage_kv: line.voltage_kv,
      }));
      const roadEdgesPayload = sourceKey === 'electric' ? [] : completedRoadSelections;
      try {
        console.info('[portal-debug] rerun_submit_start', {
          snapshotId,
          source: sourceKey,
          roadEdgeCount: roadEdgesPayload.length,
          electricLineCount: electricLinesPayload.length,
          roadSelections: roadEdgesPayload,
          electricLinesPayload,
        });
      } catch {}
      const rerunResult = await rerunPortalDetailWithRoadOverride(snapshotId, {
        source: sourceKey,
        edges: roadEdgesPayload,
        electric_lines: electricLinesPayload,
      });
      const updatedSummary = rerunResult?.summary;
      if (updatedSummary && typeof updatedSummary === 'object') {
        queryClient.setQueryData(['portal-detail-summary', snapshotId], updatedSummary);
        queryClient.setQueryData(['portal-detail-shell', snapshotId], updatedSummary);
      }
      setReportRefreshKey(Date.now());
      if (electricLinesPayload.length > 0) {
        setElectricOverrideApplied(true);
      }
      setRoadOverrideSelections([]);
      setRoadFocusedEdgeId(null);
      setRoadReportModalOpen(false);
      setElectricOverrideLines([]);
      setElectricDraftStart(null);
      setElectricDraftCursor(null);
      setElectricReportModalOpen(false);
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.removeItem(roadOverrideSessionKey);
          window.sessionStorage.removeItem(electricOverrideSessionKey);
        } catch {}
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['portal-detail-summary', snapshotId] }),
        queryClient.invalidateQueries({ queryKey: ['portal-detail-shell', snapshotId] }),
        queryClient.invalidateQueries({ queryKey: ['portal-detail-enrichment', snapshotId] }),
        queryClient.invalidateQueries({ queryKey: ['portal-detail-section', snapshotId] }),
        queryClient.invalidateQueries({ queryKey: ['portal-detail-report', snapshotId] }),
        queryClient.invalidateQueries({ queryKey: ['portal-investment-score', snapshotId] }),
      ]);
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['portal-detail-shell', snapshotId], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['portal-detail-enrichment', snapshotId], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['portal-detail-summary', snapshotId], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['portal-detail-section', snapshotId], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['portal-detail-report', snapshotId], type: 'active' }),
        queryClient.refetchQueries({ queryKey: ['portal-investment-score', snapshotId], type: 'active' }),
      ]);
      try {
        console.info('[portal-debug] rerun_submit_done', {
          snapshotId,
          source: sourceKey,
          roadSelectionsAfterReset: [],
          electricLinesAfterReset: [],
        });
      } catch {}
      window.alert('Sorgu kullanıcı yol/hat bildirimine göre güncellendi.');
    } catch (err) {
      window.alert(resolvePortalApiErrorMessage(err, 'Sorgu güncellenemedi.'));
    } finally {
      setRoadRerunSubmitting(false);
    }
  }, [
    canSubmitRoadRerun,
    roadRerunSubmitting,
    snapshotId,
    completedRoadSelections,
    electricOverrideLines,
    roadOverrideSessionKey,
    electricOverrideSessionKey,
    queryClient,
  ]);

  useEffect(() => {
    setElectricOverrideApplied(Boolean(summary?.has_user_electric_override));
  }, [snapshotId, summary?.has_user_electric_override]);

  const openMulkScoreTab = useCallback(() => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    captureTabPanelHeightBeforeSwitch();
    allowTabPanelAutoScrollRef.current = true;
    setActiveTab('investment_score');
    scrollTabPanelIntoView();
  }, [listingProLocked, captureTabPanelHeightBeforeSwitch, setActiveTab, scrollTabPanelIntoView]);

  const openSlopeMeshExpand = useCallback(() => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    setTerrainStreetViewOpen(false);
    setTerrainRoadPanelOpen(false);
    setTerrainImagesOpen(false);
    setTerrainMeshExpand('slope');
  }, [listingProLocked]);

  const openMorphologyMeshExpand = useCallback(() => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    setTerrainStreetViewOpen(false);
    setTerrainRoadPanelOpen(false);
    setTerrainImagesOpen(false);
    setTerrainMeshExpand('morphology');
  }, [listingProLocked]);

  const openTerrainMeshExpand = useCallback((mode) => {
    setTerrainStreetViewOpen(false);
    setTerrainRoadPanelOpen(false);
    setTerrainImagesOpen(false);
    setTerrainMeshExpand(mode === 'slope' || mode === 'morphology' ? mode : null);
  }, []);

  const closeTerrainMeshExpand = useCallback(() => {
    setTerrainMeshExpand(null);
  }, []);

  const openRoadPanel = useCallback(() => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    setTerrainStreetViewOpen(false);
    setTerrainMeshExpand(null);
    setTerrainImagesOpen(false);
    setTerrainRoadPanelOpen(true);
  }, [listingProLocked]);

  const closeRoadPanel = useCallback(() => {
    setTerrainRoadPanelOpen(false);
  }, []);

  const closeTerrainImages = useCallback(() => {
    setTerrainImagesOpen(false);
  }, []);

  const toggleTerrainImages = useCallback(() => {
    setTerrainImagesOpen((prev) => {
      const next = !prev;
      if (next) {
        setTerrainStreetViewOpen(false);
        setTerrainMeshExpand(null);
        setTerrainRoadPanelOpen(false);
        setEdgeMeasureVisible(false);
      }
      return next;
    });
  }, []);

  const toggleEdgeMeasureVisible = useCallback(() => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    setEdgeMeasureVisible((prev) => !prev);
  }, [listingProLocked]);

  const edgeSectionQuery = useQuery({
    queryKey: ['portal-detail-section', snapshotId, 'edge'],
    queryFn: () => getPortalDetailSection(snapshotId, 'edge'),
    enabled: Boolean(snapshotId) && edgeMeasureVisible && !listingOnly && !listingProLocked,
    staleTime: 120_000,
  });

  const terrainBandMapOverlays = useMemo(() => {
    const overlays = [...terrainBandOverlays];
    if (!edgeMeasureVisible) return overlays;
    const edgeData = edgeSectionQuery.data?.edge_measure_data || {};
    const parcelGeometry = parcelFeatureCollection?.features?.[0]?.geometry || null;
    const features = createEdgeMeasurementFeatures(parcelGeometry, edgeData);
    if (features.length) {
      overlays.push({
        id: 'edge-measure-canvas',
        data: { type: 'FeatureCollection', features },
        renderAsCanvas: true,
      });
    }
    return overlays;
  }, [
    terrainBandOverlays,
    edgeMeasureVisible,
    edgeSectionQuery.data,
    parcelFeatureCollection,
  ]);

  const terrainBandParcelCenter = useMemo(
    () => parcelCentroidFromFeatureCollection(parcelFeatureCollection),
    [parcelFeatureCollection],
  );

  const toggleTerrainStreetView = useCallback(() => {
    setTerrainStreetViewOpen((prev) => {
      const next = !prev;
      if (next) {
        setEdgeMeasureVisible(false);
        setTerrainMeshExpand(null);
        setTerrainRoadPanelOpen(false);
        setTerrainImagesOpen(false);
      }
      return next;
    });
  }, []);

  const closeTerrainStreetView = useCallback(() => {
    setTerrainStreetViewOpen(false);
  }, []);

  const listingTerrainImageUrls = useMemo(() => {
    const media = summary?.listing_media;
    if (!Array.isArray(media)) return [];
    return media
      .map((m) => {
        if (typeof m === 'string') return m.trim();
        if (m && typeof m.url === 'string') return m.url.trim();
        return '';
      })
      .filter(Boolean);
  }, [summary?.listing_media]);

  const terrainBandMapActions = useMemo(() => ([
    listingTerrainImageUrls.length
      ? {
          key: 'listing-images',
          icon: 'fa-images',
          title: terrainImagesOpen ? 'Resimleri kapat' : 'Resimler',
          label: terrainImagesOpen ? 'Resimleri kapat' : 'Resimler',
          active: terrainImagesOpen,
          onClick: toggleTerrainImages,
        }
      : null,
    {
      key: 'edge-measure',
      icon: 'fa-ruler-combined',
      title: edgeMeasureVisible ? 'Kenar ölçülerini gizle' : 'Kenar ölçülerini göster',
      label: edgeMeasureVisible ? 'Kenar ölçülerini gizle' : 'Kenar ölçülerini göster',
      active: edgeMeasureVisible,
      onClick: () => {
        setTerrainStreetViewOpen(false);
        setTerrainImagesOpen(false);
        toggleEdgeMeasureVisible();
      },
    },
    {
      key: 'street-view',
      icon: 'fa-street-view',
      title: terrainStreetViewOpen ? 'Sokak görüntüsünü kapat' : 'Sokak Görüntüsü',
      label: terrainStreetViewOpen ? 'Sokak görüntüsünü kapat' : 'Sokak Görüntüsü',
      active: terrainStreetViewOpen,
      onClick: toggleTerrainStreetView,
      disabled: !terrainBandParcelCenter,
    },
  ].filter(Boolean)), [
    listingTerrainImageUrls.length,
    terrainImagesOpen,
    toggleTerrainImages,
    edgeMeasureVisible,
    toggleEdgeMeasureVisible,
    terrainStreetViewOpen,
    toggleTerrainStreetView,
    terrainBandParcelCenter,
  ]);

  const openKmTab = useCallback(() => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    captureTabPanelHeightBeforeSwitch();
    allowTabPanelAutoScrollRef.current = true;
    setActiveTab('km');
    scrollTabPanelIntoView();
  }, [listingProLocked, captureTabPanelHeightBeforeSwitch, setActiveTab, scrollTabPanelIntoView]);

  const unlockListingProMutation = useMutation({
    mutationFn: () => unlockListingProSorgu(snapshotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-detail-summary', snapshotId] });
      queryClient.invalidateQueries({ queryKey: ['portal-detail-shell', snapshotId] });
      queryClient.invalidateQueries({ queryKey: ['portal-detail-enrichment', snapshotId] });
      queryClient.invalidateQueries({ queryKey: ['portal-detail-listing-only'] });
      queryClient.invalidateQueries({ queryKey: ['portal-investment-score', snapshotId] });
      setProSorguPurchaseModal(false);
    },
  });

  const electricSectionQuery = useQuery({
    queryKey: ['portal-detail-section', snapshotId, 'electric'],
    queryFn: () => getPortalDetailSection(snapshotId, 'electric'),
    enabled:
      Boolean(snapshotId)
      && !listingOnly
      && (activeTab === 'electric' || heroOverlaysEnabled),
    staleTime: 120_000,
  });

  const electricHeroOverlays = useMemo(() => {
    const sectionPayload = electricSectionQuery.data || {};
    const summaryPayload = {
      electric_line_feature: summary?.electric_line_feature,
      electric_values: summary?.electric_values,
      clipped_electric_multilinestring: summary?.clipped_electric_multilinestring,
    };
    const fromSection = buildElectricMapOverlays(sectionPayload);
    if (fromSection.length) return fromSection;
    return buildElectricMapOverlays(summaryPayload);
  }, [
    electricSectionQuery.data,
    summary?.electric_line_feature,
    summary?.electric_values,
    summary?.clipped_electric_multilinestring,
  ]);

  useEffect(() => {
    if (defaultHeroMapOverlays.length) {
      ensureDefaultLayers(defaultHeroMapOverlays);
    }
  }, [defaultHeroMapOverlays, ensureDefaultLayers]);

  useEffect(() => {
    if (activeTab !== 'electric' || !electricHeroOverlays.length) return;
    const cached = store.getTabConfig('electric');
    if (cached?.overlays?.length) return;
    ensureTabLayers('electric', {
      title: 'Yüksek Gerilim Hattı',
      overlays: electricHeroOverlays,
    });
  }, [activeTab, electricHeroOverlays, ensureTabLayers, store]);

  useEffect(() => {
    const nextCount = Number(summary?.favorite_count_total);
    setFavoriteCountTotal(Number.isFinite(nextCount) && nextCount > 0 ? nextCount : 0);
  }, [summary?.favorite_count_total, summary?.listing_id]);

  useEffect(() => {
    const nextCount = Number(summary?.favorite_count_total);
    setQueryFavoriteCountTotal(Number.isFinite(nextCount) && nextCount > 0 ? nextCount : 0);
  }, [summary?.favorite_count_total, summary?.snapshot_id]);

  useEffect(() => {
    const el = tabPanelRef.current;
    if (!el) return undefined;

    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const panel = tabPanelRef.current;
        if (panel) {
          const h = panel.offsetHeight;
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          if (h > 0 && now > tabSwitchCompensateUntilRef.current) {
            tabPanelLastHeightRef.current = h;
          }
        }
        compensateTabPanelHeightChange();
      });
    });
    ro.observe(el);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [compensateTabPanelHeightChange, snapshotId]);

  /** Programatik sekme açmada tek seferlik kaydırma (özet kartlarından); sekme barı tıklamasında kapalı */
  useLayoutEffect(() => {
    const ts = typeof performance !== 'undefined' ? performance.now() : Date.now();
    tabChangeAtRef.current = ts;
    compensateTabPanelHeightChange();
    if (skipTabScrollOnMountRef.current) {
      skipTabScrollOnMountRef.current = false;
      return;
    }
    if (!allowTabPanelAutoScrollRef.current) return;
    scrollTabPanelIntoView();
  }, [activeTab, compensateTabPanelHeightChange, scrollTabPanelIntoView]);

  useEffect(() => {
    if (!deferNonCriticalReady) return undefined;
    getCreditCosts()
      .then((data) => {
        setCreditCosts(data?.costs || {});
        setCreditCostsLoadFailed(false);
      })
      .catch(() => {
        setCreditCosts({});
        setCreditCostsLoadFailed(true);
      });
    return undefined;
  }, [deferNonCriticalReady]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('comment') || params.get('rating')) {
      scrollRatingsSectionIntoView();
    }
  }, [scrollRatingsSectionIntoView]);

  useEffect(() => {
    if (allTabDefs.some((tab) => tab.id === activeTab)) return;
    const fallback = allTabDefs[0]?.id ?? 'overview';
    if (fallback !== activeTab) {
      setActiveTab(fallback);
    }
  }, [activeTab, setActiveTab, allTabDefs]);

  useEffect(() => {
    const prevLocked = listingProLockedPrevRef.current;
    listingProLockedPrevRef.current = listingProLocked;
    if (!listingProLocked) return;
    if (!LISTING_PRO_SORGU_TAB_IDS.has(activeTab)) return;
    // Yalnızca kilit yeni devreye girdiğinde pro sekmesinden çık (enrichment sonrası tek sefer).
    if (prevLocked === true) return;
    const hasListingDesc = allTabDefs.some((t) => t.id === 'listing_description');
    setActiveTab(hasListingDesc ? 'listing_description' : 'overview');
  }, [listingProLocked, activeTab, setActiveTab, allTabDefs]);

  useEffect(() => {
    if (shouldForceHeroMapMedia(activeTab)) {
      setHeroCardMedia('map');
    }
  }, [activeTab, setHeroCardMedia]);

  const heroMapView = useMemo(
    () => composeHeroMapView(store, { activeTab, electricFallback: electricHeroOverlays }),
    [store, revision, activeTab, electricHeroOverlays],
  );

  useEffect(() => {
    if (activeTab !== 'electric') return;
    logElectricTabClick({
      snapshotId,
      sectionData: electricSectionQuery.data,
      summaryData: {
        electric_line_feature: summary?.electric_line_feature,
        electric_values: summary?.electric_values,
        clipped_electric_multilinestring: summary?.clipped_electric_multilinestring,
      },
      overlayCount: electricHeroOverlays.length,
      heroCardMedia,
      heroOverlayIds: heroMapView.overlays.map((o) => o?.id).filter(Boolean),
      sectionLoading: electricSectionQuery.isLoading,
      sectionError: electricSectionQuery.error?.message || null,
    });
  }, [
    activeTab,
    snapshotId,
    heroCardMedia,
    electricSectionQuery.isLoading,
    electricSectionQuery.error,
    electricSectionQuery.data,
    summary?.electric_line_feature,
    summary?.electric_values,
    electricHeroOverlays,
    heroMapView.overlays,
  ]);

  const openReportViewer = useCallback(() => {
    setTerrainMeshExpand(null);
    setTerrainRoadPanelOpen(false);

    const openModal = () => {
      setShowReportViewer(true);
    };

    /* Mesh'ler modal açılınca kapanır — önce ön yüz ekran görüntüsü al */
    const captureThenOpen = snapshotId
      ? capturePortalPdfMeshes(snapshotId).then(openModal, openModal)
      : Promise.resolve().then(openModal);
    void captureThenOpen;

    if (!snapshotId || listingOnly || listingProLocked) {
      return;
    }

    void queryClient.prefetchQuery({
      queryKey: ['portal-wind-energy-score', 'v14', snapshotId],
      queryFn: () => getPortalWindEnergyScore(snapshotId),
      staleTime: 60_000,
    });
    void queryClient.prefetchQuery({
      queryKey: ['portal-solar-energy-score', 'v5', snapshotId],
      queryFn: () => getPortalSolarEnergyScore(snapshotId),
      staleTime: 60_000,
    });

    void queryClient
      .ensureQueryData({
        queryKey: ['portal-detail-report', snapshotId],
        queryFn: () => getPortalDetailReport(snapshotId),
        staleTime: 60_000,
      })
      .then((data) => {
        if (!data || typeof data !== 'object') return;
        try {
          const payload = { ...data };
          const n = Number(snapshotId);
          if (Number.isFinite(n) && n > 0) {
            if (!payload.snapshot_id) payload.snapshot_id = n;
            if (!payload.dfa_snapshot_id) payload.dfa_snapshot_id = n;
            payload.parameters_data = { ...(payload.parameters_data || {}) };
            if (!payload.parameters_data.dfa_snapshot_id) {
              payload.parameters_data.dfa_snapshot_id = n;
            }
          }
          sessionStorage.setItem('pp_report_data', JSON.stringify(payload));
        } catch {}
      })
      .catch(() => {});
  }, [queryClient, snapshotId, listingOnly, listingProLocked]);

  const closeTopbarDropdownFromTarget = useCallback((target) => {
    const detailsEl = target && target.closest
      ? target.closest('details.pp-detail-topbar-dropdown')
      : null;
    if (detailsEl) {
      detailsEl.removeAttribute('open');
    }
  }, []);

  const handleShareLink = useCallback(async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      closeTopbarDropdownFromTarget(event.currentTarget);
    }
    if (typeof window === 'undefined') return;
    const shareUrl = window.location.href || `${window.location.origin || ''}${currentDetailHref || ''}`;
    if (!shareUrl) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title || 'ProParcel',
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') {
          return;
        }
      }
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        window.alert('Link kopyalandı.');
        return;
      }
    } catch (_) {
      // Clipboard API fallback below.
    }

    try {
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.setAttribute('readonly', 'readonly');
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      window.alert('Link kopyalandı.');
    } catch (_) {
      window.prompt('Linki kopyalayın:', shareUrl);
    }
  }, [closeTopbarDropdownFromTarget, currentDetailHref]);

  const handleSharePdf = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      closeTopbarDropdownFromTarget(event.currentTarget);
    }
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    openReportViewer();
  }, [closeTopbarDropdownFromTarget, listingProLocked, openReportViewer]);

  const closeReportViewer = useCallback(() => {
    setShowReportViewer(false);
  }, []);

  const canOpenProjeOlustur = useMemo(
    () => !listingOnly && canOpenBinaCizFromPortalSummary(summary),
    [listingOnly, summary],
  );

  const openProjeOlustur = useCallback(async () => {
    if (listingProLocked) {
      setProSorguPurchaseModal(true);
      return;
    }
    const result = await openBinaCizFromPortal({
      summary,
      parcelFeatureCollection,
      parcelRing,
    });
    if (!result.ok) {
      window.alert(
        'Proje Oluştur için ada/parsel ve mahalle bilgisi gerekli. Arazi kartındaki alanlar eksik veya TKGM eşlemesi yapılamadı.',
      );
    }
  }, [listingProLocked, summary, parcelFeatureCollection, parcelRing]);

  const ratingScorePct = summary.rating_summary?.rating_score_pct;
  const ratingScoreDisplay = formatTopbarStatNumber(ratingScorePct);
  const ratingScoreLabel =
    ratingScorePct != null && ratingScorePct !== '' && !Number.isNaN(Number(ratingScorePct))
      ? `%${ratingScorePct}`
      : 'puan yok';
  const totalCommentCount = summary.comment_count ?? 0;
  const handleFavoriteCountChange = useCallback(({ wasFavorite, isFavorite }) => {
    if (wasFavorite === isFavorite) return;
    setFavoriteCountTotal((current) => Math.max(0, current + (isFavorite ? 1 : -1)));
  }, []);
  const handleQueryFavoriteCountChange = useCallback(({ wasFavorite, isFavorite }) => {
    if (wasFavorite === isFavorite) return;
    setQueryFavoriteCountTotal((current) => Math.max(0, current + (isFavorite ? 1 : -1)));
  }, []);
  const compareCandidate = useMemo(() => buildPortalCompareItemFromSummary(summary, {
    snapshotId,
    listingId: summary?.listing_id,
    detailHref: currentDetailHref,
  }), [summary, snapshotId, currentDetailHref]);
  const compareSelected = Boolean(compareCandidate && compareSelection.selectedKeySet.has(compareCandidate.key));
  const compareDisabledReason = useMemo(() => {
    if (!compareCandidate || compareSelected) return '';
    const firstGroup = compareSelection.items[0]?.compareGroup || '';
    if (compareSelection.count >= PORTAL_COMPARE_MAX_ITEMS) {
      return `En fazla ${PORTAL_COMPARE_MAX_ITEMS} kayıt seçebilirsiniz.`;
    }
    if (firstGroup && compareCandidate.compareGroup !== firstGroup) {
      return 'Bu kayıt mevcut karşılaştırma grubuyla uyumlu değil.';
    }
    return '';
  }, [compareCandidate, compareSelected, compareSelection.count, compareSelection.items]);
  const comparePageHref = useMemo(() => getPortalComparePageHref(backLinkHref), [backLinkHref]);
  const handleCompareToggle = useCallback(() => {
    if (!compareCandidate) {
      window.alert('Bu kayıt karşılaştırma için uygun değil.');
      return;
    }
    const result = togglePortalCompareItem(compareCandidate);
    if (!result.ok && result.message) {
      window.alert(result.message);
    }
  }, [compareCandidate]);
  /** Kendi ilanı → düzenle; kendi sorgusu (ilan yok) → taslak sihirbazı — İlan Ver ile aynı hedefi tekrarlamamak için href tek kaynak */
  const portalUpdateHref = useMemo(() => {
    if (summary.viewer_is_listing_owner && summary.listing_id) {
      return `/portal/ilan/${encodeURIComponent(String(summary.listing_id))}/duzenle/`;
    }
    const sid = snapshotId != null && snapshotId !== '' ? Number(snapshotId) : NaN;
    if (summary.is_own_query && Number.isFinite(sid) && sid > 0) {
      return `/portal/ilan/olustur/?from_snapshot=${encodeURIComponent(String(sid))}`;
    }
    return null;
  }, [summary.viewer_is_listing_owner, summary.listing_id, summary.is_own_query, snapshotId]);
  const showPortalUpdateButton = Boolean(portalUpdateHref);
  const showOwnerRequestExtras = Boolean(summary.is_own_query) && !listingOnly;
  const showPublicRequestButtons = !listingOnly
    && Number(snapshotId) > 0
    && Boolean(typeof window !== 'undefined' && window.PP_USER_AUTHENTICATED);
  const showConvertToListingCta =
    Boolean(summary.is_own_query) && !listingOnly && !summary.listing_id && !showPortalUpdateButton;
  const ownerRequestButtons = [
    { key: 'spk', label: 'SPK Değerleme', icon: 'chart' },
  ];
  const mapOperationItems = [
    'Yola Terk',
    'Satın Alma Yoldan İhdas',
    'İfraz Ayırma',
    'Tevhit Birleştirme',
    'Aplikasyon Krokisi',
    'Parsel Yer Gösterme',
    'Yapı Aplikasyon Projesi',
    'Cins Değişikliği',
    'Bağımsız Bölüm Yer Gösterme',
    'Hatalı Bağımsız Bölüm Düzeltme',
    'İrtifak Hakkı Terkini',
    'İrtifak Hakkı Tesisi',
    'Zemin Tespit Tutanağı',
  ];

  const buildRequestPayload = useCallback(() => ({
    neighborhoodId: summary.proparcel_value || null,
    parcelRef: {
      city: summary.city_name || '',
      district: summary.town_name || '',
      neighborhood: summary.quarter_name || '',
      city_id: summary.city_id ?? null,
      district_id: summary.town_id ?? null,
      quarter_id: summary.quarter_id ?? null,
      proparcel_value: summary.proparcel_value ?? null,
      tkgm_value: summary.tkgm_value ?? null,
      ada: summary.ada || '',
      parsel: summary.parsel || '',
    },
    sourceReportId: String(summary.snapshot_id || snapshotId),
    idempotencyKey: `web_req_${summary.snapshot_id || snapshotId}_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  }), [summary, snapshotId]);

  const openRequestModal = useCallback((config) => {
    setRequestNote('');
    setRequestFiles([]);
    setRequestModal(config);
  }, []);

  const resolveActionCreditCost = useCallback(async (actionType, cachedCost) => {
    if (cachedCost != null && !Number.isNaN(Number(cachedCost))) {
      return Number(cachedCost);
    }
    try {
      const data = await getCreditCosts();
      if (data?.costs) {
        setCreditCosts(data.costs);
        setCreditCostsLoadFailed(false);
      }
      const liveCost = data?.costs?.[actionType];
      if (liveCost != null && !Number.isNaN(Number(liveCost))) {
        return Number(liveCost);
      }
    } catch {
      setCreditCostsLoadFailed(true);
    }
    return null;
  }, []);

  const handleExpertRequest = useCallback(async () => {
    if (!summary.proparcel_value) {
      window.alert('Mahalle bilgisi bulunamadı.');
      return;
    }
    try {
      const availability = await checkPortalExpertRequestAvailability(summary.proparcel_value, {
        actionType: 'expert_consultancy',
      });
      if (!availability?.available) {
        window.alert(availability?.reason || 'Bu bölgede uygun uzman bulunamadı.');
        return;
      }
      const price = availability.priceCoin ?? creditCosts.expert_consultancy;
      if (price == null || Number.isNaN(Number(price))) {
        window.alert('Uzman görüşü ücreti alınamadı. Lütfen daha sonra tekrar deneyin.');
        return;
      }
      openRequestModal({
        kind: 'expert',
        label: 'Uzman Görüşü',
        price: Number(price),
        eligibleExpertCount: availability.eligibleExpertCount || 0,
      });
    } catch (err) {
      window.alert(resolvePortalApiErrorMessage(err, 'Uzman görüşü talebi başlatılamadı.'));
    }
  }, [creditCosts.expert_consultancy, openRequestModal, summary.proparcel_value]);

  const handleProparcelAskRequest = useCallback(async () => {
    if (!summary.proparcel_value) {
      window.alert('Mahalle bilgisi bulunamadı.');
      return;
    }
    const sourceReportId = summary.snapshot_id || snapshotId;
    try {
      const availability = await checkPortalProparcelAskAvailability({
        neighborhoodId: summary.proparcel_value,
        sourceReportId,
      });
      if (!availability?.available) {
        window.alert(availability?.reason || 'ProParcel görüş talebi şu an oluşturulamıyor.');
        return;
      }
      const price = availability.priceCoin;
      if (price == null || Number.isNaN(Number(price))) {
        window.alert("Proparcel'e Sor ücreti alınamadı. Lütfen daha sonra tekrar deneyin.");
        return;
      }
      openRequestModal({
        kind: 'proparcel',
        label: "Proparcel'e Sor",
        price: Number(price),
        priceTier: availability.priceTier,
        priceSurcharge: availability.priceSurcharge,
        basePriceCoin: availability.basePriceCoin,
      });
    } catch (err) {
      window.alert(resolvePortalApiErrorMessage(err, "Proparcel'e Sor talebi başlatılamadı."));
    }
  }, [openRequestModal, snapshotId, summary]);

  const handleSpkRequest = useCallback(async () => {
    const price = await resolveActionCreditCost('spk_talebi', creditCosts.spk_talebi);
    if (price == null) {
      window.alert('SPK değerleme ücreti alınamadı. Lütfen daha sonra tekrar deneyin.');
      return;
    }
    openRequestModal({
      kind: 'spk',
      label: 'SPK Değerleme',
      price,
    });
  }, [creditCosts.spk_talebi, openRequestModal, resolveActionCreditCost]);

  const handleMapOperationRequest = useCallback(async (label) => {
    const price = await resolveActionCreditCost('harita_talebi', creditCosts.harita_talebi);
    if (price == null) {
      window.alert('Harita işlemi ücreti alınamadı. Lütfen daha sonra tekrar deneyin.');
      return;
    }
    openRequestModal({
      kind: 'map',
      label,
      price,
      requestTypeCode: `map_operation_${String(label || '')
        .toLowerCase()
        .replaceAll('ı', 'i')
        .replaceAll('ğ', 'g')
        .replaceAll('ş', 's')
        .replaceAll('ç', 'c')
        .replaceAll('ö', 'o')
        .replaceAll('ü', 'u')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')}`,
    });
  }, [creditCosts.harita_talebi, openRequestModal, resolveActionCreditCost]);

  const submitRequestModal = useCallback(async () => {
    if (!requestModal) return;
    setRequestSubmitting(true);
    try {
      if (requestModal.kind === 'expert') {
        const result = await createPortalExpertRequest({
          ...buildRequestPayload(),
          note: requestNote.trim(),
          actionType: 'expert_consultancy',
        });
        for (const file of requestFiles) {
          await uploadRequestAttachment('expert_request', result.requestId, file);
        }
        window.alert(result?.status === 'ROUTING_FAILED'
          ? 'Uzman görüşü talebi oluşturuldu ancak şu an uygun yönlendirme bulunamadı. Talep Taleplerim ekranında görünecek.'
          : 'Uzman görüşü talebi oluşturuldu.');
      } else if (requestModal.kind === 'proparcel') {
        const result = await createPortalProparcelAskRequest({
          ...buildRequestPayload(),
          note: requestNote.trim(),
        });
        for (const file of requestFiles) {
          await uploadRequestAttachment('expert_request', result.requestId, file);
        }
        window.alert('ProParcel görüş talebiniz alındı.');
      } else if (requestModal.kind === 'spk') {
        const result = await createPortalSpkValuationRequest({
          ...buildRequestPayload(),
          note: requestNote.trim(),
          requestTypeCode: 'spk_valuation',
        });
        for (const file of requestFiles) {
          await uploadRequestAttachment('spk_valuation_request', result.requestId, file);
        }
        window.alert(
          result?.status === 'ROUTING_FAILED'
            ? 'SPK değerleme talebi oluşturuldu ancak uygun SPK yönlendirmesi bulunamadı. Talep Taleplerim ekranında görünecek.'
            : `SPK değerleme talebi oluşturuldu${result?.recipientCount ? ` ve ${result.recipientCount} uygun uzmana iletildi.` : '.'}`
        );
      } else if (requestModal.kind === 'map') {
        const result = await createPortalMapOperationRequest({
          ...buildRequestPayload(),
          note: requestNote.trim(),
          requestTypeCode: requestModal.requestTypeCode || 'map_operation',
        });
        for (const file of requestFiles) {
          await uploadRequestAttachment('map_operation_request', result.requestId, file);
        }
        window.alert(
          result?.status === 'ROUTING_FAILED'
            ? 'Harita işlemi talebi oluşturuldu ancak ilgili ilçe için aktif LIHKAB eşleşmesi bulunamadı. Talep Taleplerim ekranında görünecek.'
            : 'Harita işlemi talebi oluşturuldu.'
        );
      }
      setRequestModal(null);
      setRequestNote('');
      setRequestFiles([]);
    } catch (err) {
      const code = err?.apiData?.code || err?.code;
      if (err?.status === 402 || code === 'INSUFFICIENT_COIN') {
        window.location.href = '/accounts/pricing/';
        return;
      }
      window.alert(resolvePortalApiErrorMessage(err, 'Talep oluşturulamadı.'));
    } finally {
      setRequestSubmitting(false);
    }
  }, [buildRequestPayload, requestFiles, requestModal, requestNote]);
  const detailModeNoticeTitle = useMemo(
    () => buildDetailModeNoticeTitle(summary),
    [summary],
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let rafId = null;
    let topbarTimer = null;
    const pinnedRef = { current: false };
    setDetailModeNoticeFloatingStyle(null);
    pinnedRef.current = false;

    const update = () => {
      const hostEl = detailModeNoticeHostRef.current;
      if (!hostEl) return;
      const hostRect = hostEl.getBoundingClientRect();
      const topOffset = getPortalTopbarBottomPx();

      const enter = hostRect.top <= topOffset;
      const exit = hostRect.top > topOffset + 14;

      let nextStyle = null;
      if (pinnedRef.current) {
        if (exit) {
          pinnedRef.current = false;
          nextStyle = null;
        } else {
          const hostWidth = hostEl.offsetWidth || hostRect.width;
          nextStyle = {
            left: `${Math.round(hostRect.left)}px`,
            width: `${Math.round(hostWidth)}px`,
            top: `${topOffset}px`,
          };
        }
      } else if (enter) {
        pinnedRef.current = true;
        const hostWidth = hostEl.offsetWidth || hostRect.width;
        nextStyle = {
          left: `${Math.round(hostRect.left)}px`,
          width: `${Math.round(hostWidth)}px`,
          top: `${topOffset}px`,
        };
      }

      setDetailModeNoticeFloatingStyle((prev) => {
        if (!nextStyle && !prev) return prev;
        if (!nextStyle) return null;
        if (prev && prev.left === nextStyle.left && prev.width === nextStyle.width && prev.top === nextStyle.top) {
          return prev;
        }
        return nextStyle;
      });
    };

    const scheduleUpdate = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        update();
      });
    };

    const scheduleTopbarResize = () => {
      if (topbarTimer != null) clearTimeout(topbarTimer);
      topbarTimer = window.setTimeout(() => {
        topbarTimer = null;
        scheduleUpdate();
      }, 120);
    };

    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    document.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
    window.addEventListener('resize', scheduleUpdate);
    const topbarEl =
      document.getElementById('main-topbar') || document.querySelector('header.pp-header');
    const topbarRo =
      topbarEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(scheduleTopbarResize)
        : null;
    topbarRo?.observe(topbarEl);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (topbarTimer != null) clearTimeout(topbarTimer);
      window.removeEventListener('scroll', scheduleUpdate);
      document.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
      topbarRo?.disconnect();
    };
  }, [isListingDetailView]);

  const canRerunProQueryFromPill = !isListingDetailView;

  const refreshPortalDetailAfterRerun = useCallback(async () => {
    setReportRefreshKey(Date.now());
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['portal-detail-summary', snapshotId] }),
      queryClient.invalidateQueries({ queryKey: ['portal-detail-shell', snapshotId] }),
      queryClient.invalidateQueries({ queryKey: ['portal-detail-enrichment', snapshotId] }),
      queryClient.invalidateQueries({ queryKey: ['portal-detail-section', snapshotId] }),
      queryClient.invalidateQueries({ queryKey: ['portal-detail-report', snapshotId] }),
      queryClient.invalidateQueries({ queryKey: ['portal-investment-score', snapshotId] }),
    ]);
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ['portal-detail-shell', snapshotId], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['portal-detail-enrichment', snapshotId], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['portal-detail-summary', snapshotId], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['portal-detail-section', snapshotId], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['portal-detail-report', snapshotId], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['portal-investment-score', snapshotId], type: 'active' }),
    ]);
  }, [queryClient, snapshotId]);

  const handleProSorguPillClick = useCallback(async () => {
    if (proQueryLaunchSubmitting || !canRerunProQueryFromPill || !summary) return;
    setProQueryLaunchSubmitting(true);
    try {
      const context = buildPortalProQueryLauncherContext(summary, snapshotId);
      await launchPortalDetailProQueryFromSummary(context);
    } catch (err) {
      window.alert(resolvePortalApiErrorMessage(err, 'Pro sorgu başlatılamadı.'));
    } finally {
      setProQueryLaunchSubmitting(false);
    }
  }, [
    proQueryLaunchSubmitting,
    canRerunProQueryFromPill,
    summary,
    snapshotId,
  ]);

  const showProQueryCreditNotice =
    !isListingDetailView
    && Boolean(summary?.is_own_query)
    && Boolean(summary?.pro_query_credit_notice?.show);

  useEffect(() => {
    if (!showProQueryCreditNotice || !summary?.pro_query_credit_notice?.message) {
      setProQueryCreditNoticeModal(null);
      return;
    }
    const notice = summary.pro_query_credit_notice;
    const storageKey = `pp-pro-query-credit-notice-modal-${snapshotId}`;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage.getItem(storageKey) === '1') {
        return;
      }
    } catch (_) {
      /* sessionStorage kullanılamıyorsa modal yine gösterilir */
    }
    setProQueryCreditNoticeModal({
      message: notice.message,
      variant: notice.variant || '',
    });
  }, [
    showProQueryCreditNotice,
    snapshotId,
    summary?.pro_query_credit_notice?.message,
    summary?.pro_query_credit_notice?.variant,
  ]);

  const closeProQueryCreditNoticeModal = useCallback(() => {
    const storageKey = `pp-pro-query-credit-notice-modal-${snapshotId}`;
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(storageKey, '1');
      }
    } catch (_) {
      /* ignore */
    }
    setProQueryCreditNoticeModal(null);
  }, [snapshotId]);

  const renderDetailModeNoticeContent = (isFloating = false) => (
    <>
      <a
        href={backLinkHref}
        className="pp-detail-mode-notice__back-link"
        onClick={(event) => handleBackLinkClick(event, backLinkHref)}
        tabIndex={isFloating ? -1 : undefined}
      >
        <i className="fas fa-arrow-left" aria-hidden="true" />
        <span>Listeye geri dön</span>
      </a>
      {detailModeNoticeTitle ? (
        <span className="pp-detail-mode-notice__title">{detailModeNoticeTitle}</span>
      ) : null}
      {isListingDetailView ? (
        <span className="pp-detail-mode-notice__pill">İlan</span>
      ) : (
        <button
          type="button"
          className={`pp-detail-mode-notice__pill${canRerunProQueryFromPill ? ' pp-detail-mode-notice__pill--clickable' : ''}`}
          onClick={canRerunProQueryFromPill && !isFloating ? handleProSorguPillClick : undefined}
          disabled={!canRerunProQueryFromPill || isFloating || proQueryLaunchSubmitting}
          title={canRerunProQueryFromPill ? 'Tip seçerek yeni Pro sorgu başlat (1 Tepe Kredi)' : undefined}
          tabIndex={isFloating ? -1 : undefined}
        >
          ProSorgu
        </button>
      )}
    </>
  );

  const detailInsightCardProps = {
    snapshotId,
    summary: displaySummary,
    listingProLocked,
    riskChips: (insightSupplement.riskChips || []).map((chip) => {
      if (chip.key === 'nufus') {
        return { ...chip, value: populationChipValue || chip.value };
      }
      if (chip.key === 'quarter-sales') {
        return { ...chip, value: quarterSalesChipValue || chip.value };
      }
      if (chip.key === 'valuable-roads') {
        return { ...chip, value: valuableRoadsChipValue || chip.value };
      }
      return chip;
    }),
    parcelRiskChips: insightSupplement.parcelRiskChips || [],
    roadMetrics: insightSupplement.roadMetrics,
    onOpenInvestmentTab: () => setProSorguPurchaseModal(true),
    onOpenMulkScoreTab: openMulkScoreTab,
    onOpenKmTab: openKmTab,
    onOpenRoadTab: openRoadPanel,
    onOpenRatings: scrollRatingsSectionIntoView,
    onQuarterCenterChipClick: handleQuarterCenterChipClick,
    quarterCenterMapActive: quarterCenterMapVisible,
    quarterCenterMapLoading:
      quarterCenterMapLoading
      || (terrainCenterQuery.isFetching && !terrainCenterQuery.data && !quarterCenterLonLat),
    onImarChipClick: handleImarChipClick,
    imarMapActive: imarMapVisible,
    imarMapLoading: terrainGeoQuery.isFetching && !terrainGeoQuery.data,
    onPopulationChipClick: handlePopulationChipClick,
    populationMapActive: populationMapVisible,
    populationMapLoading,
    populationChipLines,
    onRamsarChipClick: handleRamsarChipClick,
    ramsarMapActive: wetlandMapVisible,
    ramsarMapLoading: wetlandMapLoading,
    onHvChipClick: handleHvChipClick,
    hvMapActive: hvMapVisible,
    hvMapLoading,
    onQuarterSalesChipClick: handleQuarterSalesChipClick,
    quarterSalesMapActive: quarterSalesMapVisible,
    quarterSalesMapLoading,
    onValuableRoadsChipClick: handleValuableRoadsChipClick,
    valuableRoadsMapActive: valuableRoadsMapVisible,
    valuableRoadsMapLoading,
  };

  return (
    <div className="pp-detail-layout">
      <div className="pp-detail-topbar-row pp-detail-topbar-row--page-top" ref={topbarRef}>
        <div className="pp-detail-topbar" role="toolbar" aria-label="Sayfa işlemleri">
          <div className="pp-detail-topbar-left">
            <a
              href={backLinkHref}
              className="pp-detail-side-back-link pp-detail-topbar-back"
              onClick={(event) => handleBackLinkClick(event, backLinkHref)}
            >
              <i className="fas fa-arrow-left" aria-hidden="true" />
              <span>Listeye Dön</span>
            </a>
            {(showPortalUpdateButton || showOwnerRequestExtras || showPublicRequestButtons) ? (
              <>
                <span className="pp-detail-topbar-separator" aria-hidden="true" />
                {showPortalUpdateButton ? (
                  <a
                    className="pp-detail-topbar-btn pp-detail-topbar-btn--primary"
                    href={portalUpdateHref}
                    title={
                      summary.viewer_is_listing_owner && summary.listing_id
                        ? 'İlan bilgilerini düzenle'
                        : 'İlan taslak sihirbazını aç (sorgudan gelen veriler)'
                    }
                  >
                    <DetailPanelIcon name="refresh" className="pp-detail-topbar-btn-icon" />
                    <span>Güncelle</span>
                  </a>
                ) : null}
                {showPublicRequestButtons ? (
                  <>
                    {showPortalUpdateButton ? (
                      <span className="pp-detail-topbar-separator" aria-hidden="true" />
                    ) : null}
                    <button
                      type="button"
                      className="pp-detail-topbar-btn pp-detail-topbar-btn--ghost"
                      disabled={
                        creditCostsLoadFailed
                        && creditCosts.expert_consultancy == null
                      }
                      onClick={handleExpertRequest}
                      aria-label="Uzman Görüşü talebi oluştur"
                      title="Uzman Görüşü talebi oluştur"
                    >
                      <DetailPanelIcon name="user" className="pp-detail-topbar-btn-icon" />
                      <span>Uzman Görüşü</span>
                    </button>
                    <span className="pp-detail-topbar-separator" aria-hidden="true" />
                    <button
                      type="button"
                      className="pp-detail-topbar-btn pp-detail-topbar-btn--ghost"
                      disabled={
                        creditCostsLoadFailed
                        && creditCosts.expert_consultancy == null
                      }
                      onClick={handleProparcelAskRequest}
                      aria-label="Proparcel'e Sor talebi oluştur"
                      title="Proparcel'e Sor talebi oluştur"
                    >
                      <DetailPanelIcon name="message" className="pp-detail-topbar-btn-icon" />
                      <span>Proparcel&apos;e Sor</span>
                    </button>
                  </>
                ) : null}
                {showOwnerRequestExtras ? (
                  <>
                    {(showPortalUpdateButton || showPublicRequestButtons) ? (
                      <span className="pp-detail-topbar-separator" aria-hidden="true" />
                    ) : null}
                    {showConvertToListingCta ? (
                      <>
                        <a
                          className="pp-detail-topbar-btn pp-detail-topbar-btn--ghost"
                          href={`/portal/ilan/olustur/?from_snapshot=${encodeURIComponent(String(snapshotId))}`}
                          title="Konum sorgudan gelir; fiyat, detay, galeri ve açıklamayı sihirbazda tamamlayın"
                        >
                          <DetailPanelIcon name="store" className="pp-detail-topbar-btn-icon" />
                          <span>İlan Ver</span>
                        </a>
                        <span className="pp-detail-topbar-separator" aria-hidden="true" />
                      </>
                    ) : null}
                    {ownerRequestButtons.map((button, index) => (
                      <span key={button.key} className="pp-detail-topbar-inline-action">
                        {index > 0 ? (
                          <span className="pp-detail-topbar-separator" aria-hidden="true" />
                        ) : null}
                        <button
                          type="button"
                          className="pp-detail-topbar-btn pp-detail-topbar-btn--ghost"
                          onClick={() => {
                            if (button.key === 'spk') handleSpkRequest();
                          }}
                          aria-label={`${button.label} talebi oluştur`}
                          title={`${button.label} talebi oluştur`}
                        >
                          <DetailPanelIcon name={button.icon} className="pp-detail-topbar-btn-icon" />
                          <span>{button.label}</span>
                        </button>
                      </span>
                    ))}
                    <span className="pp-detail-topbar-separator" aria-hidden="true" />
                    <details className="pp-detail-topbar-dropdown">
                      <summary className="pp-detail-topbar-btn pp-detail-topbar-btn--ghost pp-detail-topbar-btn--dropdown">
                        <DetailPanelIcon name="map" className="pp-detail-topbar-btn-icon" />
                        <span>Harita İşlemleri</span>
                        <DetailPanelIcon name="chevron-down" className="pp-detail-topbar-btn-caret" size={12} />
                      </summary>
                      <div className="pp-detail-topbar-dropdown-menu">
                        {mapOperationItems.map((item) => (
                          <button
                            key={item}
                            type="button"
                            className="pp-detail-topbar-dropdown-item"
                            onClick={() => handleMapOperationRequest(item)}
                            title={`${item} talebi oluştur`}
                          >
                            <i className="fas fa-arrow-right" aria-hidden="true" />
                            <span>{item}</span>
                          </button>
                        ))}
                      </div>
                    </details>
                    {socialMediaTemplateHref ? (
                      <>
                        <span className="pp-detail-topbar-separator" aria-hidden="true" />
                        <details className="pp-detail-topbar-dropdown">
                          <summary className="pp-detail-topbar-btn pp-detail-topbar-btn--ghost pp-detail-topbar-btn--dropdown">
                            <DetailPanelIcon name="share" className="pp-detail-topbar-btn-icon" />
                            <span>Sosyal Medya</span>
                            <DetailPanelIcon name="chevron-down" className="pp-detail-topbar-btn-caret" size={12} />
                          </summary>
                          <div className="pp-detail-topbar-dropdown-menu">
                            <a
                              className="pp-detail-topbar-dropdown-item"
                              href={socialMediaTemplateHref}
                              title="Post şablonu oluştur"
                            >
                              <i className="fas fa-image" aria-hidden="true" />
                              <span>Post Şablonu Oluştur</span>
                            </a>
                          </div>
                        </details>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}
          </div>
          {!listingOnly ? (
            <div className="pp-detail-topbar-group pp-detail-topbar-group--utilities">
              <details className="pp-detail-topbar-dropdown pp-detail-topbar-compare">
                <summary
                  className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item"
                  title="Karşılaştır"
                >
                  <DetailPanelIcon name="compare" className="pp-detail-topbar-btn-icon" size={15} />
                  <span className="pp-detail-topbar-util-value">{formatTopbarStatNumber(compareSelection.count)}</span>
                  <span className="pp-detail-topbar-sr-only">Karşılaştır</span>
                </summary>
                <div className="pp-detail-topbar-dropdown-menu pp-detail-topbar-compare-menu">
                  <button
                    type="button"
                    className="pp-detail-topbar-dropdown-item"
                    onClick={handleCompareToggle}
                    disabled={!compareSelected && Boolean(compareDisabledReason)}
                    title={compareDisabledReason || (compareSelected ? 'Kaydı listeden çıkar' : 'Kaydı listeye ekle')}
                  >
                    <i className="fas fa-check-square" aria-hidden="true" />
                    <span>{compareSelected ? 'Listeden Çıkar' : 'Listeye Ekle'}</span>
                  </button>
                  <a
                    className="pp-detail-topbar-dropdown-item"
                    href={comparePageHref}
                    title="Karşılaştırma listesini aç"
                  >
                    <i className="fas fa-columns" aria-hidden="true" />
                    <span>Listeyi Göster</span>
                  </a>
                </div>
              </details>
              {canUseQueryFavorite ? (
                <QueryFavoriteHeart
                  snapshotId={snapshotId}
                  isAuthenticated={isAuthenticated}
                  loginNextPath={
                    typeof window !== 'undefined' ? window.location.pathname : '/'
                  }
                  className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item pp-detail-favorite-btn"
                  count={queryFavoriteCountTotal}
                  layout="toolbar"
                  onFavoriteChange={handleQueryFavoriteCountChange}
                />
              ) : null}
              {summary.listing_id ? (
                <ListingFavoriteHeart
                  listingId={String(summary.listing_id)}
                  isAuthenticated={Boolean(
                    typeof window !== 'undefined' && window.PP_USER_AUTHENTICATED,
                  )}
                  sourceSurface="listing_detail"
                  loginNextPath={
                    typeof window !== 'undefined' ? window.location.pathname : '/'
                  }
                  className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item pp-detail-favorite-btn"
                  count={favoriteCountTotal}
                  layout="toolbar"
                  tooltip="Tum kullanicilarin toplam favori sayisi"
                  viewerIsOwner={Boolean(summary.viewer_is_listing_owner)}
                  onFavoriteChange={handleFavoriteCountChange}
                />
              ) : null}
              <button
                type="button"
                className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item"
                onClick={() => {
                  scrollRatingsSectionIntoView();
                }}
                aria-label={`Kaydet. Güncel puan ${ratingScoreLabel}`}
                title="Kaydet"
              >
                <DetailPanelIcon name="bookmark" className="pp-detail-topbar-btn-icon" size={15} />
                <span className="pp-detail-topbar-util-value">{ratingScoreDisplay}</span>
              </button>
              <button
                type="button"
                className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item"
                onClick={() => {
                  scrollRatingsSectionIntoView();
                }}
                aria-label={`Yorumlar bölümüne git. Toplam yorum ${totalCommentCount}`}
                title="Yorum"
              >
                <DetailPanelIcon name="message" className="pp-detail-topbar-btn-icon" size={15} />
                <span className="pp-detail-topbar-util-value">{formatTopbarStatNumber(totalCommentCount)}</span>
              </button>
              <details className="pp-detail-topbar-dropdown">
                <summary
                  className={`pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item pp-detail-topbar-btn--util-icon pp-detail-topbar-btn--dropdown${listingProLocked ? ' pp-detail-topbar-btn--locked' : ''}`}
                  aria-label="Raporu paylaş"
                  title="Raporu paylaş"
                >
                  <DetailPanelIcon name="share" className="pp-detail-topbar-btn-icon" size={15} />
                </summary>
                <div className="pp-detail-topbar-dropdown-menu">
                  <button
                    type="button"
                    className="pp-detail-topbar-dropdown-item"
                    onClick={handleShareLink}
                    title="Bu sayfanın bağlantısını paylaş"
                  >
                    <i className="fas fa-link" aria-hidden="true" />
                    <span>Link</span>
                  </button>
                  <button
                    type="button"
                    className="pp-detail-topbar-dropdown-item"
                    onClick={handleSharePdf}
                    title="PDF olarak paylaş"
                  >
                    <i className="fas fa-file-pdf" aria-hidden="true" />
                    <span>PDF</span>
                  </button>
                </div>
              </details>
            </div>
          ) : null}
          {listingOnly && summary.listing_id ? (
            <div className="pp-detail-topbar-group pp-detail-topbar-group--utilities">
              <ListingFavoriteHeart
                listingId={String(summary.listing_id)}
                isAuthenticated={Boolean(
                  typeof window !== 'undefined' && window.PP_USER_AUTHENTICATED,
                )}
                sourceSurface="listing_detail"
                loginNextPath={
                  typeof window !== 'undefined' ? window.location.pathname : '/'
                }
                className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item pp-detail-favorite-btn"
                count={favoriteCountTotal}
                layout="toolbar"
                tooltip="Tum kullanicilarin toplam favori sayisi"
                viewerIsOwner={Boolean(summary.viewer_is_listing_owner)}
                onFavoriteChange={handleFavoriteCountChange}
              />
              <details className="pp-detail-topbar-dropdown">
                <summary
                  className="pp-detail-topbar-btn pp-detail-topbar-btn--util pp-detail-topbar-stat-item pp-detail-topbar-btn--util-icon pp-detail-topbar-btn--dropdown"
                  aria-label="İlanı paylaş"
                  title="İlanı paylaş"
                >
                  <DetailPanelIcon name="share" className="pp-detail-topbar-btn-icon" size={15} />
                </summary>
                <div className="pp-detail-topbar-dropdown-menu">
                  <button
                    type="button"
                    className="pp-detail-topbar-dropdown-item"
                    onClick={handleShareLink}
                    title="Bu ilanın bağlantısını paylaş"
                  >
                    <i className="fas fa-link" aria-hidden="true" />
                    <span>Link</span>
                  </button>
                  <button
                    type="button"
                    className="pp-detail-topbar-dropdown-item"
                    onClick={handleSharePdf}
                    title="PDF olarak paylaş"
                  >
                    <i className="fas fa-file-pdf" aria-hidden="true" />
                    <span>PDF</span>
                  </button>
                </div>
              </details>
            </div>
          ) : null}
        </div>
      </div>
      {!listingOnly && !listingProLocked ? (
        <div className="pp-detail-terrain-band pp-detail-terrain-band--bleed" aria-label="Eğim, morfoloji ve mahalle haritası">
          <div className="pp-detail-terrain-band__body">
            <div className="pp-detail-terrain-band__col pp-detail-terrain-band__col--map">
              <div className={`pp-detail-terrain-band__map-shell${(terrainMeshExpand || terrainRoadPanelOpen || terrainStreetViewOpen || terrainImagesOpen) ? ' pp-detail-terrain-band__map-shell--mesh-expand' : ''}`}>
                <div className="pp-detail-terrain-band__map-split">
                  <div className="pp-detail-terrain-band__map-split-terrain">
                    <MapboxParcelMap
                      eager
                      hideTitle
                      hideMapLabels
                      mapStyle={terrainRightMode === 'satellite' ? MAP_STYLE_SATELLITE : MAP_STYLE_STREETS}
                      satelliteBasemap={false}
                      parcelData={parcelFeatureCollection}
                      overlays={terrainBandMapOverlays}
                      htmlMarkers={terrainBandHtmlMarkers}
                      className="pp-detail-terrain-band__map"
                      heroMapControls="fullscreen"
                      fullscreenTargetSelector=".pp-detail-terrain-band"
                      styleToggle={{
                        label: terrainRightMode === 'satellite' ? 'Harita' : 'Uydu görüntüsü',
                        title: terrainRightMode === 'satellite' ? 'Harita stiline geç' : 'Uydu görüntüsüne geç',
                        icon: terrainRightMode === 'satellite' ? 'fa-map' : 'fa-satellite',
                        onToggle: () => setTerrainRightMode((m) => (m === 'satellite' ? 'streets' : 'satellite')),
                      }}
                      mapActionButtons={terrainBandMapActions}
                      terrain3d
                      mapSky={terrainRightMode !== 'satellite'}
                      mapPitch={0}
                      fitScope="parcel"
                      onMapReady={markTerrainStreetsMapReady}
                      webglSuspended={showReportViewer}
                    />
                    {terrainMapStatusLabel ? (
                      <span className="pp-detail-terrain-band__map-status" role="status" aria-live="polite">
                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                        <span>{terrainMapStatusLabel}</span>
                      </span>
                    ) : null}
                    {!terrainGeoQuery.isFetching && terrainGeoQuery.isError && !terrainGeoQuery.data ? (
                      <span className="pp-detail-terrain-band__map-status pp-detail-terrain-band__map-status--err" role="status">
                        Mahalle sınırı alınamadı
                      </span>
                    ) : null}
                  </div>
                </div>
                <TerrainBandStreetViewSplit
                  open={terrainStreetViewOpen}
                  parcelCenter={terrainBandParcelCenter}
                  parcelFeatureCollection={parcelFeatureCollection}
                  onClose={closeTerrainStreetView}
                />
                <TerrainBandMeshExpandOverlay
                  mode={terrainMeshExpand}
                  snapshotId={snapshotId}
                  onClose={closeTerrainMeshExpand}
                />
                <TerrainBandRoadPanelOverlay
                  open={terrainRoadPanelOpen}
                  snapshotId={snapshotId}
                  onClose={closeRoadPanel}
                />
                <TerrainBandImagesOverlay
                  open={terrainImagesOpen}
                  listingMedia={listingTerrainImageUrls}
                  onClose={closeTerrainImages}
                />
              </div>
            </div>
            <div className="pp-detail-terrain-band__col pp-detail-terrain-band__col--metrics">
              <DetailInsightSlopeMetricCard
                slopePct={insightSupplement.slopePct}
                onOpenSlopeTab={openSlopeMeshExpand}
                snapshotId={snapshotId}
                embedMesh
                meshEnabled={!showReportViewer}
                onExpandMesh={openTerrainMeshExpand}
              />
              <DetailInsightMorphologyMetricCard
                summary={displaySummary}
                onOpenSlopeTab={openMorphologyMeshExpand}
                snapshotId={snapshotId}
                embedMesh
                meshEnabled={!showReportViewer}
                onExpandMesh={openTerrainMeshExpand}
              />
            </div>
          </div>
          <div className="pp-detail-terrain-band__insight">
            <DetailInsightSnapshotCard
              {...detailInsightCardProps}
              variant="horizontal"
            />
            {quarterCenterMapError ? (
              <p className="pp-detail-insight-notice" role="status">
                {quarterCenterMapError}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="pp-detail-shell">
      <div className="pp-detail-notices-stack">
        <div
          className="pp-detail-mode-notice-host pp-detail-mode-notice-host--scroll-anchor"
          ref={detailModeNoticeHostRef}
        >
          <div
            className={`pp-detail-mode-notice pp-detail-mode-notice--${isListingDetailView ? 'listing' : 'query'} pp-detail-mode-notice--static-hidden`}
            aria-hidden="true"
          >
            {renderDetailModeNoticeContent(false)}
          </div>
          {detailModeNoticeFloatingStyle ? (
            <div
              className={`pp-detail-mode-notice pp-detail-mode-notice--${isListingDetailView ? 'listing' : 'query'} pp-detail-mode-notice--floating`}
              style={detailModeNoticeFloatingStyle}
              role="status"
              aria-live="polite"
            >
              {renderDetailModeNoticeContent(true)}
            </div>
          ) : null}
        </div>
        {showProQueryCreditNotice ? (
          <div className="pp-detail-credit-notice pp-detail-credit-notice--no-charge" role="status" aria-live="polite">
            <span className="pp-detail-credit-notice__text">
              {summary.pro_query_credit_notice.message}
            </span>
          </div>
        ) : null}
      </div>
      <div className="pp-detail-page">
        {!showTerrainBand && (showTopInsight || showSideInsight) ? (
          <>
            <DetailInsightSnapshotCard
              {...detailInsightCardProps}
              variant="horizontal"
            />
            {quarterCenterMapError ? (
              <p className="pp-detail-insight-notice" role="status">
                {quarterCenterMapError}
              </p>
            ) : null}
          </>
        ) : null}
        <div
          ref={pageBodyRef}
          className={`pp-detail-page-body${showTerrainBand ? ' pp-detail-page-body--tabs-overview' : ''}`}
        >
          <main className="pp-detail-main">

          {requestModal ? (
            <div className="pp-request-modal-overlay" onClick={() => !requestSubmitting && setRequestModal(null)}>
              <div className="pp-request-modal" onClick={(event) => event.stopPropagation()}>
                <div className="pp-request-modal-head">
                  <h3>{requestModal.label} Talebi</h3>
                  <button type="button" className="pp-request-modal-close" onClick={() => !requestSubmitting && setRequestModal(null)}>×</button>
                </div>
                <div className="pp-request-modal-body">
                  {requestModal.kind === 'expert' ? (
                    <p>Bu bölgede {requestModal.eligibleExpertCount || 0} uygun uzman bulundu.</p>
                  ) : null}
                  {requestModal.kind === 'proparcel' ? (
                    <p>
                      {resolveProparcelAskPriceTierLabel(requestModal.priceTier)}
                      {Number(requestModal.priceSurcharge) > 0
                        ? ` (+${Number(requestModal.priceSurcharge)})`
                        : ''}
                    </p>
                  ) : null}
                  <div className="pp-request-modal-price">Ücret: {Number(requestModal.price || 0)} Tepe Coin</div>
                  <label className="pp-request-modal-label" htmlFor="pp-request-note">Not</label>
                  <textarea
                    id="pp-request-note"
                    className="pp-request-modal-textarea"
                    value={requestNote}
                    onInput={(event) => setRequestNote(event.currentTarget.value)}
                    placeholder="Talep için notunuzu yazın"
                    maxLength={1000}
                  />
                  <label className="pp-request-modal-label" htmlFor="pp-request-files">Ekler</label>
                  <input
                    id="pp-request-files"
                    className="pp-request-modal-file"
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(event) => setRequestFiles(Array.from(event.currentTarget.files || []).slice(0, 4))}
                  />
                  {requestFiles.length ? (
                    <div className="pp-request-modal-file-list">
                      {requestFiles.map((file) => file.name).join(', ')}
                    </div>
                  ) : null}
                  <div className="pp-request-modal-actions">
                    <button type="button" className="pp-request-modal-cancel" onClick={() => !requestSubmitting && setRequestModal(null)}>
                      İptal
                    </button>
                    <button type="button" className="pp-request-modal-submit" onClick={submitRequestModal} disabled={requestSubmitting}>
                      {requestSubmitting ? 'İşleniyor...' : 'Talep Oluştur'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {proSorguPurchaseModal ? (
            <div
              className="pp-request-modal-overlay"
              onClick={() => !unlockListingProMutation.isPending && setProSorguPurchaseModal(false)}
              role="presentation"
            >
              <div className="pp-request-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="pp-prosorgu-modal-title">
                <div className="pp-request-modal-head">
                  <h3 id="pp-prosorgu-modal-title">Pro Sorgu içeriği</h3>
                  <button
                    type="button"
                    className="pp-request-modal-close"
                    onClick={() => !unlockListingProMutation.isPending && setProSorguPurchaseModal(false)}
                    aria-label="Kapat"
                  >
                    ×
                  </button>
                </div>
                <div className="pp-request-modal-body">
                  <p>
                    DFA, Uzman Görüşleri, analiz sekmeleri ve Proparcel değerlemesi dahil tüm Pro Sorgu alanlarını görmek için{' '}
                    <strong>1 Tepe Coin</strong> harcanır. Satın alma sonrası bu sayfadaki içerikler açılır.
                  </p>
                  {unlockListingProMutation.isError ? (
                    <div className="pp-detail-empty" role="alert">{unlockListingProMutation.error?.message}</div>
                  ) : null}
                  <div className="pp-request-modal-actions">
                    <button
                      type="button"
                      className="pp-request-modal-cancel"
                      onClick={() => !unlockListingProMutation.isPending && setProSorguPurchaseModal(false)}
                    >
                      İptal
                    </button>
                    <button
                      type="button"
                      className="pp-request-modal-submit"
                      onClick={() => unlockListingProMutation.mutate()}
                      disabled={unlockListingProMutation.isPending}
                    >
                      {unlockListingProMutation.isPending ? 'İşleniyor...' : '1 Tepe Coin ile aç'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <section
            className={`pp-detail-top-grid pp-detail-top-grid--overview${structureOverviewGrid ? ' pp-detail-top-grid--structure-query' : ''}${showTerrainBand ? ' pp-detail-top-grid--tabs-left' : ''}`}
          >
            {showTerrainBand ? null : (
              <HeroCardMedia
                parcelFeatureCollection={parcelFeatureCollection}
                overlays={heroMapView.overlays}
                mapTitle={heroMapView.title}
                terrain3d={heroMapView.terrain3d}
                mapPitch={heroMapView.mapPitch}
                mapClassName="pp-detail-map-card--hero"
                expertData={expertMapQuery.data}
                expertLoading={expertMapQuery.isLoading}
                listingGalleryUrls={Array.isArray(summary.listing_media) ? summary.listing_media : []}
                listingTitle={
                  summary.listing_id
                    ? String(summary.listing_title || summary.title || '').trim()
                    : ''
                }
              />
            )}
            {structureOverviewGrid && !showTerrainBand ? (
              <DfaSummaryCard
                summary={displaySummary}
                valuationLocked={listingProLocked && Boolean(summary.listing_id)}
                onProSorguInteraction={() => setProSorguPurchaseModal(true)}
                listingOnly={listingOnly}
                isEnriching={isEnriching}
                supplementalCoreRows={insightSupplement.physicalRows}
                structureOverviewGrid
                stackExtra={
                  summary.owner_card ? (
                    <SideNavOwnerCard
                      owner={summary.owner_card}
                      listingId={summary.listing_id}
                      viewerIsOwner={summary.viewer_is_listing_owner}
                      onOpenProfile={setOwnerProfileUserId}
                      variant="sidebar"
                    />
                  ) : null
                }
              />
            ) : (
              <div className="pp-detail-top-stack">
                <DfaSummaryCard
                  summary={displaySummary}
                  valuationLocked={listingProLocked && Boolean(summary.listing_id)}
                  onProSorguInteraction={() => setProSorguPurchaseModal(true)}
                  listingOnly={listingOnly}
                  isEnriching={isEnriching}
                  supplementalCoreRows={insightSupplement.physicalRows}
                />
                {summary.owner_card ? (
                  <SideNavOwnerCard
                    owner={summary.owner_card}
                    listingId={summary.listing_id}
                    viewerIsOwner={summary.viewer_is_listing_owner}
                    onOpenProfile={setOwnerProfileUserId}
                    variant="sidebar"
                  />
                ) : null}
              </div>
            )}
          </section>

          {ownerProfileUserId != null ? (
            <ExpertOwnerProfileModal
              userId={ownerProfileUserId}
              initialOwner={summary.owner_card}
              onClose={() => setOwnerProfileUserId(null)}
            />
          ) : null}

          <div className="pp-detail-main-tab-zone">
            <div className="pp-detail-main-tab-rail">
            <nav className="pp-detail-main-tab-strip" aria-label="İçerik sekmeleri">
              {tabDefs.map((tab) => {
                if (tab.id === IMAR_TAB_DEF.id) {
                  if (!canOpenMahalleBilgileri) {
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        className="pp-detail-main-tab pp-detail-main-tab--locked"
                        disabled
                        title="Bu kayıt için mahalle kimliği yok; İmar Bilgileri açılamıyor."
                      >
                        {formatMainTabLabel(IMAR_TAB_DEF.label)}
                      </button>
                    );
                  }
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className={`pp-detail-main-tab${activeTab === IMAR_TAB_DEF.id ? ' is-active' : ''}${listingProLocked ? ' pp-detail-main-tab--locked' : ''}`}
                      aria-current={activeTab === IMAR_TAB_DEF.id ? 'true' : undefined}
                      aria-disabled={listingProLocked}
                      title={
                        [summary.city_name, summary.town_name, summary.quarter_name].filter(Boolean).join(' / ') ||
                        'İmar bilgileri'
                      }
                      onClick={() => {
                        if (listingProLocked) {
                          setProSorguPurchaseModal(true);
                          return;
                        }
                        captureTabPanelHeightBeforeSwitch();
                        allowTabPanelAutoScrollRef.current = false;
                        setActiveTab(IMAR_TAB_DEF.id);
                      }}
                    >
                      {formatMainTabLabel(IMAR_TAB_DEF.label)}
                    </button>
                  );
                }
                const isProTabLocked = listingProLocked && LISTING_PRO_SORGU_TAB_IDS.has(tab.id);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    className={`pp-detail-main-tab${activeTab === tab.id ? ' is-active' : ''}${isProTabLocked ? ' pp-detail-main-tab--locked' : ''}`}
                    aria-current={activeTab === tab.id ? 'true' : undefined}
                    aria-disabled={isProTabLocked}
                    onClick={() => {
                      if (isProTabLocked) {
                        setProSorguPurchaseModal(true);
                        return;
                      }
                      /* Üst bardan Puan/Yorum vb. ile açılan otomatik kaydırmayı sekme değişiminde tekrarlama */
                      captureTabPanelHeightBeforeSwitch();
                      allowTabPanelAutoScrollRef.current = false;
                      setActiveTab(tab.id);
                    }}
                  >
                    {formatMainTabLabel(tab.label)}
                  </button>
                );
              })}
              {canOpenMahalleBilgileri ? (
                <button
                  type="button"
                  className={`pp-detail-main-tab${activeTab === MAHALLE_TAB_DEF.id ? ' is-active' : ''}${listingProLocked ? ' pp-detail-main-tab--locked' : ''}`}
                  aria-current={activeTab === MAHALLE_TAB_DEF.id ? 'true' : undefined}
                  aria-disabled={listingProLocked}
                  title={
                    [summary.city_name, summary.town_name, summary.quarter_name].filter(Boolean).join(' / ') ||
                    'Mahalle bilgileri'
                  }
                  onClick={() => {
                    if (listingProLocked) {
                      setProSorguPurchaseModal(true);
                      return;
                    }
                    captureTabPanelHeightBeforeSwitch();
                    allowTabPanelAutoScrollRef.current = false;
                    setActiveTab(MAHALLE_TAB_DEF.id);
                  }}
                >
                  {formatMainTabLabel(MAHALLE_TAB_DEF.label)}
                </button>
              ) : (
                <button
                  type="button"
                  className="pp-detail-main-tab pp-detail-main-tab--locked"
                  disabled
                  title="Bu kayıt için mahalle kimliği yok; Mahalle Bilgileri açılamıyor."
                >
                  {formatMainTabLabel(MAHALLE_TAB_DEF.label)}
                </button>
              )}
              {canOpenMahalleBilgileri ? (
                <button
                  type="button"
                  className={`pp-detail-main-tab${activeTab === SOSYAL_ALANLAR_TAB_DEF.id ? ' is-active' : ''}${listingProLocked ? ' pp-detail-main-tab--locked' : ''}`}
                  aria-current={activeTab === SOSYAL_ALANLAR_TAB_DEF.id ? 'true' : undefined}
                  aria-disabled={listingProLocked}
                  title={
                    [summary.city_name, summary.town_name, summary.quarter_name].filter(Boolean).join(' / ') ||
                    'Sosyal alanlar'
                  }
                  onClick={() => {
                    if (listingProLocked) {
                      setProSorguPurchaseModal(true);
                      return;
                    }
                    captureTabPanelHeightBeforeSwitch();
                    allowTabPanelAutoScrollRef.current = false;
                    setActiveTab(SOSYAL_ALANLAR_TAB_DEF.id);
                  }}
                >
                  {formatMainTabLabel(SOSYAL_ALANLAR_TAB_DEF.label)}
                </button>
              ) : (
                <button
                  type="button"
                  className="pp-detail-main-tab pp-detail-main-tab--locked"
                  disabled
                  title="Bu kayıt için mahalle kimliği yok; Sosyal Alanlar açılamıyor."
                >
                  {formatMainTabLabel(SOSYAL_ALANLAR_TAB_DEF.label)}
                </button>
              )}
              {!listingOnly
                ? tailTabDefs.map((tab) => {
                    const isProTabLocked = listingProLocked && LISTING_PRO_SORGU_TAB_IDS.has(tab.id);
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        className={`pp-detail-main-tab${activeTab === tab.id ? ' is-active' : ''}${isProTabLocked ? ' pp-detail-main-tab--locked' : ''}`}
                        aria-current={activeTab === tab.id ? 'true' : undefined}
                        aria-disabled={isProTabLocked}
                        onClick={() => {
                          if (isProTabLocked) {
                            setProSorguPurchaseModal(true);
                            return;
                          }
                          captureTabPanelHeightBeforeSwitch();
                          allowTabPanelAutoScrollRef.current = false;
                          setActiveTab(tab.id);
                        }}
                      >
                        {formatMainTabLabel(tab.label)}
                      </button>
                    );
                  })
                : null}
              {!listingOnly ? (
                <button
                  type="button"
                  className={`pp-detail-main-tab pp-detail-main-tab--proje-olustur${listingProLocked ? ' pp-detail-main-tab--locked' : ''}`}
                  aria-disabled={listingProLocked}
                  title={
                    listingProLocked
                      ? 'Pro sorgu gerekli'
                      : canOpenProjeOlustur
                        ? 'Bina Çiz (yeni sekme)'
                        : 'Mahalle veya parsel bilgisi eksik'
                  }
                  onClick={() => {
                    if (listingProLocked) {
                      setProSorguPurchaseModal(true);
                      return;
                    }
                    openProjeOlustur();
                  }}
                >
                  {formatMainTabLabel('Bina Çiz')}
                </button>
              ) : null}
              {!listingOnly ? (
                <button
                  type="button"
                  className={`pp-detail-main-tab pp-detail-main-tab--report${listingProLocked ? ' pp-detail-main-tab--locked' : ''}`}
                  aria-disabled={listingProLocked}
                  title="PDF rapor (v5)"
                  onClick={() => {
                    if (listingProLocked) {
                      setProSorguPurchaseModal(true);
                      return;
                    }
                    openReportViewer();
                  }}
                >
                  {formatMainTabLabel('Rapor')}
                </button>
              ) : null}
            </nav>
            </div>
            <div ref={tabPanelRef} className="pp-detail-tab-panel">
              <Suspense
                fallback={(
                  <DetailTabPanelShell isLoading>
                    <DetailTabSkeleton variant="chunk" label="Sekme yükleniyor…" />
                  </DetailTabPanelShell>
                )}
              >
                {renderLazyTab(
                  activeTab,
                  snapshotId,
                  displaySummary,
                  listingProLocked,
                  structureQuery,
                  {
                    simulatedFooter: mahalleOrtSimulatedFooter,
                    onSimulatedFooterChange: handleMahalleOrtSimulatedFooterChange,
                    canSeeRoadActions: Boolean(summary?.is_own_query),
                    onOpenRoadModal: () => setRoadReportModalOpen(true),
                    onOpenElectricModal: () => setElectricReportModalOpen(true),
                    canOpenRoadModal: parcelRing.length >= 3,
                    canOpenElectricModal: parcelRing.length >= 3,
                    hasRoadOverride: Boolean(summary?.has_user_road_override) || completedRoadSelections.length > 0,
                    hasElectricOverride: Boolean(summary?.has_user_electric_override) || electricOverrideApplied,
                    isEnriching,
                    enrichmentLoadFailed,
                    onRetryEnrichment,
                  },
                  { onExpertRequest: handleExpertRequest },
                )}
              </Suspense>
            </div>
          </div>

          <div className="pp-detail-main-below">
          <div ref={ratingsSectionRef}>
            <LazyWhenVisible
              eager={belowFoldEager}
              loader={() => import('./DetailRatingsTab.jsx')}
              fallback={(
                <div className="pp-detail-lazy-fallback pp-detail-lazy-fallback--ratings">
                  <PpLoaderAnalysisDots label="Puan bölümü yükleniyor…" />
                </div>
              )}
              placeholder={<div className="pp-detail-skeleton-panel" aria-hidden="true" />}
              snapshotId={snapshotId}
              summary={summary}
            />
          </div>

          <LazyWhenVisible
            eager={belowFoldEager}
            loader={() => import('./SimilarPublishedListingsSection.jsx').then((m) => ({ default: m.SimilarPublishedListingsSection }))}
            fallback={(
              <div className="pp-detail-lazy-fallback pp-detail-lazy-fallback--similar">
                <PpLoaderAnalysisDots label="Benzer ilanlar yükleniyor…" />
              </div>
            )}
            placeholder={<div className="pp-detail-skeleton-panel pp-detail-skeleton-panel--similar" aria-hidden="true" />}
            snapshotId={listingOnly ? null : snapshotId}
            summary={summary}
          />

          <p className="pp-detail-score-disclaimer" role="note">
            Bu skor, çeşitli veri ve algoritmalar kullanılarak oluşturulmuş olup yatırım tavsiyesi niteliği taşımaz.
          </p>
          </div>
        </main>
        </div>
      </div>

      {!listingOnly ? (
        <DetailReportViewerModal
          snapshotId={snapshotId}
          isOpen={showReportViewer}
          viewerUrl={reportViewerUrl}
          onClose={closeReportViewer}
        />
      ) : null}
      <RoadOverrideModal
        open={roadReportModalOpen}
        summary={summary}
        selections={roadOverrideSelections}
        focusedEdgeId={roadFocusedEdgeId}
        onEdgeClick={handleRoadEdgeClick}
        onFocusEdge={setRoadFocusedEdgeId}
        onRoadTypeChange={handleRoadTypeChange}
        onRemoveSelection={handleRoadSelectionRemove}
        onSubmit={() => handleRoadRerunSubmit('road')}
        canSubmit={completedRoadSelections.length > 0}
        submitting={roadRerunSubmitting}
        onClose={() => setRoadReportModalOpen(false)}
      />
      <ElectricOverrideModal
        open={electricReportModalOpen}
        summary={summary}
        lines={electricOverrideLines}
        draftStart={electricDraftStart}
        draftCursor={electricDraftCursor}
        selectedVoltageKv={electricSelectedVoltageKv}
        onMapClick={handleElectricMapClick}
        onMapMove={handleElectricMapMove}
        onVoltageChange={setElectricSelectedVoltageKv}
        onRemoveLine={handleElectricRemoveLine}
        onSubmit={() => handleRoadRerunSubmit('electric')}
        canSubmit={electricOverrideLines.length > 0}
        submitting={roadRerunSubmitting}
        onClose={() => {
          setElectricDraftStart(null);
          setElectricDraftCursor(null);
          setElectricReportModalOpen(false);
        }}
      />
      <ProQueryCreditNoticeModal
        open={Boolean(proQueryCreditNoticeModal)}
        message={proQueryCreditNoticeModal?.message ?? ''}
        variant={proQueryCreditNoticeModal?.variant ?? ''}
        onClose={closeProQueryCreditNoticeModal}
      />
    </div>
    </div>
  );
}

export function PortalRecentQueryDetailApp({
  snapshotId,
  listingId = null,
  handoffJobId = null,
  initialShell = null,
}) {
  const queryClient = useQueryClient();
  const initialHandoffJobId = String(handoffJobId || '').trim() || null;
  const [pendingHandoffJobId, setPendingHandoffJobId] = useState(initialHandoffJobId);
  const [handoffResolvedSnapshotId, setHandoffResolvedSnapshotId] = useState(null);

  const handoffQuery = useQuery({
    queryKey: ['pro-query-handoff', pendingHandoffJobId],
    enabled: Boolean(pendingHandoffJobId),
    refetchInterval: (query) => {
      const body = query.state.data?.body;
      if (body?.analysis_failed || body?.status === 'failed') return false;
      const sid = body?.dfa_snapshot_id;
      if (Number.isFinite(sid) && sid > 0) return false;
      return body?.analysis_ready ? 500 : 2000;
    },
    queryFn: async () => {
      const res = await getProQueryHandoff(pendingHandoffJobId);
      if (res.status === 401) {
        throw new Error('Oturumunuz sona ermiş. Giriş yapıp tekrar deneyin.');
      }
      if (res.status === 403 || res.status === 404) {
        throw new Error(res.body?.error || 'Sorgu bulunamadı');
      }
      return res;
    },
    retry: 2,
  });

  useEffect(() => {
    const sid = handoffQuery.data?.body?.dfa_snapshot_id;
    if (!pendingHandoffJobId || !Number.isFinite(sid) || sid <= 0) return;
    const url = `/portal/recent-queries/${sid}/`;
    try {
      window.history.replaceState(null, '', url);
    } catch (_) {}
    prefetchPortalDetailShell(sid);
    setHandoffResolvedSnapshotId(sid);
    setPendingHandoffJobId(null);
  }, [handoffQuery.data, pendingHandoffJobId]);

  if (pendingHandoffJobId) {
    const handoffBody = handoffQuery.data?.body;
    const handoffAnalysisFailed = Boolean(
      handoffBody?.analysis_failed || handoffBody?.status === 'failed',
    );
    if (handoffQuery.isError) {
      return (
        <div className="pp-detail-empty">
          <p>{handoffQuery.error?.message || 'Kayıt hazırlanamadı.'}</p>
          <a href="/portal/recent-queries/">Son 90 Gün Sorgular</a>
        </div>
      );
    }
    if (handoffAnalysisFailed) {
      return (
        <div className="pp-detail-empty">
          <p>{handoffBody?.error || 'Pro sorgu tamamlanamadı.'}</p>
          <a href="/portal/recent-queries/">Son 90 Gün Sorgular</a>
        </div>
      );
    }
    return (
      <DetailPageShell
        snapshotId={0}
        slow
      />
    );
  }

  const resolvedSnapshotId = handoffResolvedSnapshotId || Number(snapshotId) || 0;
  const listingOnlyMode = Boolean(listingId) && (!resolvedSnapshotId || resolvedSnapshotId === 0);
  const prefetchSnapshotId = listingOnlyMode ? 0 : resolvedSnapshotId;
  const [slowLoading, setSlowLoading] = useState(false);
  const shellReadyMarkedRef = useRef(false);
  const lastShellSummaryRef = useRef(initialShell || null);

  const listingQuery = useQuery({
    queryKey: ['portal-detail-listing-only', listingId],
    queryFn: listingOnlyMode
      ? async () => {
          const data = await fetchPublicListingDetailData(listingId);
          const psRaw = data?.portal_snapshot_id;
          const psid =
            psRaw != null && String(psRaw).trim() !== ''
              ? Number.parseInt(String(psRaw).trim(), 10)
              : NaN;
          if (Number.isFinite(psid) && psid > 0) {
            try {
              return await withDetailTimeout(getPortalDetailShell(psid));
            } catch (e) {
              try {
                console.warn(
                  '[portal-detail] linked snapshot shell unavailable, listing-only fallback',
                  e?.message || e,
                );
              } catch (_) {}
              return buildListingOnlyPortalSummary(data);
            }
          }
          return buildListingOnlyPortalSummary(data);
        }
      : async () => null,
    retry: false,
    enabled: listingOnlyMode && Boolean(listingId),
  });

  const shellQuery = useQuery({
    queryKey: ['portal-detail-shell', resolvedSnapshotId],
    queryFn: () => withDetailTimeout(getPortalDetailShell(resolvedSnapshotId)),
    retry: false,
    enabled: !listingOnlyMode && Boolean(resolvedSnapshotId),
    initialData: initialShell || undefined,
    initialDataUpdatedAt: initialShell ? Date.now() : undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const enrichmentSnapshotId = listingOnlyMode
    ? Number(listingQuery.data?.snapshot_id) || 0
    : resolvedSnapshotId;

  const enrichmentQuery = useQuery({
    queryKey: ['portal-detail-enrichment', enrichmentSnapshotId],
    queryFn: () => withDetailTimeout(getPortalDetailEnrichment(enrichmentSnapshotId)),
    enabled:
      enrichmentSnapshotId > 0
      && Boolean(listingOnlyMode ? listingQuery.data : shellQuery.data)
      && !(listingOnlyMode && listingQuery.data?.listing_only),
    staleTime: 60_000,
    retry: 2,
    retryDelay: 1500,
  });

  const summary = useMemo(() => {
    if (listingOnlyMode) return listingQuery.data || null;
    const shell = shellQuery.data ?? lastShellSummaryRef.current;
    if (shellQuery.data) {
      lastShellSummaryRef.current = shellQuery.data;
    }
    if (!shell) return null;
    const enrichment = enrichmentQuery.data;
    const merged = enrichment && typeof enrichment === 'object'
      ? { ...shell, ...enrichment }
      : { ...shell };
    /* Enrichment null/eksik coords ile shell geometrisini silmesin — harita DEFAULT'a kaçmasın */
    const mergedCoords = merged.parcel_coords_lonlat;
    const shellCoords = shell.parcel_coords_lonlat;
    if (
      (!Array.isArray(mergedCoords) || mergedCoords.length < 3)
      && Array.isArray(shellCoords)
      && shellCoords.length >= 3
    ) {
      merged.parcel_coords_lonlat = shellCoords;
    }
    const pinnedTotal = resolvePortalTotalPrice(merged);
    if (pinnedTotal != null) {
      merged.total_price = pinnedTotal;
    }
    return merged;
  }, [listingOnlyMode, listingQuery.data, shellQuery.data, enrichmentQuery.data]);

  const isHydrating = listingOnlyMode
    ? listingQuery.isLoading && !summary
    : shellQuery.isLoading && !summary;
  const isEnriching = !listingOnlyMode && Boolean(summary) && enrichmentQuery.isLoading && !enrichmentQuery.data;
  const enrichmentLoadFailed = !listingOnlyMode
    && Boolean(summary)
    && enrichmentQuery.isError
    && !enrichmentQuery.data;
  const onRetryEnrichment = useCallback(() => {
    void enrichmentQuery.refetch();
  }, [enrichmentQuery]);
  const loadError = listingOnlyMode ? listingQuery.error : shellQuery.error;

  const shellReady = listingOnlyMode
    ? Boolean(listingQuery.data)
    : Boolean(shellQuery.data ?? initialShell);
  const enrichmentDone = listingOnlyMode
    || Boolean(enrichmentQuery.data)
    || enrichmentQuery.isError
    || !enrichmentSnapshotId;

  useEffect(() => {
    if (!prefetchSnapshotId || !shellReady || !enrichmentDone || listingOnlyMode) return undefined;
    void queryClient.prefetchQuery({
      queryKey: ['portal-investment-score', prefetchSnapshotId],
      queryFn: () => getPortalInvestmentScore(prefetchSnapshotId),
      staleTime: 60_000,
    });
    return undefined;
  }, [prefetchSnapshotId, queryClient, shellReady, enrichmentDone, listingOnlyMode]);

  useEffect(() => {
    if (!isHydrating) {
      setSlowLoading(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setSlowLoading(true), 12_000);
    return () => window.clearTimeout(timer);
  }, [isHydrating]);

  useEffect(() => {
    if (!summary || shellReadyMarkedRef.current) return;
    shellReadyMarkedRef.current = true;
    markDetailShellReady(initialShell ? 'ssr' : listingOnlyMode ? 'listing' : 'api');
  }, [initialShell, listingOnlyMode, summary]);

  if (loadError && !summary) {
    return <div className="pp-detail-empty">{loadError.message}</div>;
  }

  if (!summary) {
    return (
      <DetailPageShell
        snapshotId={listingOnlyMode ? 0 : resolvedSnapshotId}
        slow={slowLoading}
        onRetry={() => (listingOnlyMode ? listingQuery.refetch() : shellQuery.refetch())}
      />
    );
  }

  const effectiveSnapshotId = listingOnlyMode
    ? Number(summary?.snapshot_id) || 0
    : resolvedSnapshotId;

  return (
    <DetailPageStoreProvider summary={summary}>
      <PortalDetailMapLayerProvider>
        <PortalRecentQueryDetailAppInner
          snapshotId={effectiveSnapshotId}
          summary={summary}
          requestedListingId={listingId}
          isEnriching={isEnriching}
          enrichmentLoadFailed={enrichmentLoadFailed}
          onRetryEnrichment={onRetryEnrichment}
        />
      </PortalDetailMapLayerProvider>
    </DetailPageStoreProvider>
  );
}
